/* ============================================================
   CONFIGURATOR RACK LIB
   assets/configurator-rack-lib.js

   Firewood-rack runtime. Mirrors configurator-pool-lib.js pattern:
     - Lightweight controllers stay in this IIFE.
     - Big objects (RackRenderer, RackEngine) are exposed directly
       via global.X = {...} so the section's inline boot() can call
       them after exposing window.STATE/THREE/etc.

   Public API exposed on window:
     window.RackLib            controller (boot, state, handlers)
     window.RackRenderer       Three.js rack mesh builder
     window.RackEngine         scene / camera / renderer setup
     window.RackHandles        drag-handle DOM positioning
     window.RackBoQ            BoQ formulas (kvh, tst, ror)

   Dependencies (must be on window before any method call):
     window.THREE, window.OrbitControls   (from section module script)
     window.RK_BOOT                        (CFG_BOOT payload)
     window.Configurator                   (core: bus, units, log)
   ============================================================ */

(function (global) {
  'use strict';

  var VERSION = 'rack-v10-docs';

  /* ----------------------------------------------------------
     STATE  (metric internally; UI formats to imperial if needed)
     ---------------------------------------------------------- */
  var STATE = {
    L: 1.83,         /* length (m), along Z -- Matt Thompson's 72 in (rack-v10-docs) */
    W: 0.51,         /* width  (m), along X -- 20 in */
    H: 1.78,         /* height (m), along Y -- back posts 70 in */
    view: '3d',      /* '3d' | '2d' — cycled by cf-btn-view */
    vizStage: 1,     /* Visualisation stage within current view, cycled by cf-btn-viz.
                        3D stages (3 total):
                          • 1 = classic full build + asset picking (default)
                          • 2 = inspection (structure 30 %, fasteners 100 %)
                          • 3 = profiles (color-coded lumber categories)
                        2D stages (3 total):
                          • 1 = top-down plan
                          • 2 = side elevation (bokorys)
                          • 3 = front elevation (nárys)
                        Switching view always resets vizStage to 1. */
    isMetric: true,
    assemblyStep: 0, /* 0=composed, 1=exploded, 2=empty, 3=frame, 4=+decking, 5=+posts, 6=+rails, 7=+rafters, 8=+battens, 9=composed */
    bodyVisible: false, /* scale-reference person visible? (off by default — user toggles on) */
    bodyX: null,        /* world X (null → default halfW + 0.60) */
    bodyZ: null,        /* world Z (null → default 0) */
    opacity2D: 0.45     /* fill opacity in 2D views — adjustable via slider, range 0..0.80 */
  };
  /* Expose STATE on window for shared modules that might read it.
     The PDF exporter's generic flow (triggered by passing
     {title, rows}) doesn't actually need STATE — but other shared
     modules might. Cheap to expose. */
  global.STATE = STATE;
  var DEFAULTS = { L: 1.83, W: 0.51, H: 1.78 };
  var RANGES = {
    L: { min: 0.6, max: 4.0 },
    W: { min: 0.5, max: 2.0 },
    H: { min: 1.4, max: 2.5 }
  };

  /* Fixed profile cross-sections (metric, EU true-inch / rough-sawn convention):
       2×4: true 2" × 4" = 50 × 100 mm  (KVH10060)
       1×6: true 1" × 6" = 25 × 152 mm  (TST00001 — decking boards)
       1×2: true 1" × 2" = 25 × 50 mm   (top rim, TODO) */
  var DIMS = {
    twoByFour:  { w: 0.050, h: 0.100 },
    oneByTwo:   { w: 0.025, h: 0.050 },
    decking:    { w: 0.025, h: 0.152 },
    roof:       { thickness: 0.02 },
    deckSpacing: 0.157,
    deckHeight:  0.30,
    deckGap:     0.005,
    rafterExt:   0.15,
    rafterChamfer: 0.06,
    rafterGap:   0.38,
    battenGap:   0.50,
    battenInset: 0.050,
    roofOverX:   0.05,
    roofOverZ:   0.14,
    braceOver:   0.02,
    snap:        0.01
  };

  /* THE RACK IN INCHES IS BUILT FROM US LUMBER (rack-v10-docs, Marek: the
     buyer of Matt's plans found our digital ones unusable -- "cuts like 5.91
     ft aren't useful ... Matt's plans have an even 72 in"). In imperial the
     model is rebuilt with the real US profiles (2x4 = 1 1/2 x 3 1/2 in, 1x2 =
     3/4 x 1 1/2, 1x6 = 3/4 x 5 1/2), the sizes snap to 1/2 in, and the
     default is Matt Thompson's original: 72 x 20 in, posts 70 / 73 1/2 in,
     deck at 12 in, rafters 30 in. Metric keeps its profiles, snaps to 10 mm
     and starts from the same design rounded. DIMS is mutated in place (the
     snippet reads RackLib.DIMS). */
  var IN_M = 0.0254;
  var UNIT_DIMS = {
    metric: {
      twoByFour: { w: 0.050, h: 0.100 }, oneByTwo: { w: 0.025, h: 0.050 }, decking: { w: 0.025, h: 0.152 },
      deckSpacing: 0.157, deckHeight: 0.30, deckGap: 0.005, rafterExt: 0.15, rafterChamfer: 0.06,
      rafterGap: 0.38, battenGap: 0.50, battenInset: 0.050, roofOverX: 0.05, roofOverZ: 0.14, braceOver: 0.02, snap: 0.01
    },
    imperial: {
      twoByFour: { w: 1.5 * IN_M, h: 3.5 * IN_M }, oneByTwo: { w: 0.75 * IN_M, h: 1.5 * IN_M }, decking: { w: 0.75 * IN_M, h: 5.5 * IN_M },
      deckSpacing: 5.6875 * IN_M, deckHeight: 12 * IN_M, deckGap: 0.1875 * IN_M, rafterExt: 7 * IN_M, rafterChamfer: 2 * IN_M,
      rafterGap: 18 * IN_M, battenGap: 12 * IN_M, battenInset: 2 * IN_M, roofOverX: 2 * IN_M, roofOverZ: 5.5 * IN_M, braceOver: 0.75 * IN_M, snap: 0.5 * IN_M
    }
  };
  var DEFAULT_DIMS = {
    metric:   { L: 1.83, W: 0.51, H: 1.78 },
    imperial: { L: 72 * IN_M, W: 20 * IN_M, H: 70 * IN_M }
  };
  function _applyUnitDims() {
    var U = UNIT_DIMS[STATE.isMetric ? 'metric' : 'imperial'], k;
    for (k in U) {
      if (!U.hasOwnProperty(k)) continue;
      if (U[k] && typeof U[k] === 'object') { DIMS[k].w = U[k].w; DIMS[k].h = U[k].h; }
      else DIMS[k] = U[k];
    }
    var D = DEFAULT_DIMS[STATE.isMetric ? 'metric' : 'imperial'];
    DEFAULTS.L = D.L; DEFAULTS.W = D.W; DEFAULTS.H = D.H;
  }
  /* sizes on the unit's grid (1/2 in, 10 mm) and inside the ranges */
  function _snapDims() {
    var st = DIMS.snap || 0.01;
    ['L', 'W', 'H'].forEach(function (k) {
      var r = RANGES[k], v = Math.round(STATE[k] / st) * st;
      if (v < r.min) v = Math.ceil(r.min / st) * st;
      if (v > r.max) v = Math.floor(r.max / st) * st;
      STATE[k] = Math.round(v * 1e6) / 1e6;
    });
  }
  /* decking boards: as many as fit with the nominal gap, never overlapping */
  function _deckCount(L) {
    var dWide = DIMS.decking.h, dGap = DIMS.deckGap;
    var n = Math.max(2, Math.round((L + dGap) / (dWide + dGap)));
    while (n > 2 && n * dWide > L + 1e-6) n--;
    return n;
  }

  /* Above this rack length (m) we add a third pair of posts at z=0
     (mid-span) on both the high (+X) and low (-X) sides to reduce
     deflection of the long top rails and bottom frame length rails.
     Result: 4 posts up to and including 3.5 m, 6 posts above. */
  var MID_POST_THRESHOLD = 3.5;

  /* ----------------------------------------------------------
     MATERIALS — hardcoded material catalog (matches the BoQ database).
     Each entry is the source of truth for BoQ output, display, and
     (optionally) Three.js material color/finish.
     ---------------------------------------------------------- */
  var MATERIALS = {
    /* ── LUMBER PROFILES ────────────────────────────────────── */
    KVH10060: {
      id: 'KVH10060',
      name: '2×4 KVH lumber',
      category: 'lumber',
      profile: { w: 0.050, h: 0.100 },
      unit: 'm',
      unitPrice: 8.50,
      selectable: true,
      color: 0xb98c5e,
      roughness: 0.85
    },
    KVL20040: {
      id: 'KVL20040',
      name: '1×2 timber batten',
      category: 'lumber',
      /* Physical 25×50mm (planed 1×2) — geometry unchanged across imperial/metric */
      profile: { w: 0.025, h: 0.050 },
      unit: 'm',
      unitPrice: 2.20,
      selectable: true,
      color: 0xd9b07e,
      roughness: 0.80
    },
    /* ── DECKING ────────────────────────────────────────────── */
    TST00001: {
      id: 'TST00001',
      name: '1×6 decking',
      category: 'decking',
      profile: { w: 0.025, h: 0.152 },
      unit: 'm',
      unitPrice: 5.40,
      selectable: true,
      color: 0x8c5d3a,
      roughness: 0.95
    },
    /* ── ROOFING ────────────────────────────────────────────── */
    ROR12004: {
      id: 'ROR12004',
      name: 'Corrugated roofing',
      category: 'roofing',
      unit: 'm²',
      unitPrice: 18.00,
      selectable: true,
      color: 0xc4c8cc,
      roughness: 0.45,
      metalness: 0.85
    },
    /* ── FASTENERS — calculated, NOT individually selectable ─ */
    FAS80100: { id: 'FAS80100', name: '8×100mm wood screw', category: 'fastener', unit: 'pcs', unitPrice: 0.18, selectable: false },
    FAS60160: { id: 'FAS60160', name: '6×160mm wood screw', category: 'fastener', unit: 'pcs', unitPrice: 0.22, selectable: false },
    FAS60080: { id: 'FAS60080', name: '6×80mm wood screw',  category: 'fastener', unit: 'pcs', unitPrice: 0.12, selectable: false },
    FAS50060: { id: 'FAS50060', name: '5×60mm wood screw',  category: 'fastener', unit: 'pcs', unitPrice: 0.08, selectable: false },
    FAS00225: { id: 'FAS00225', name: 'EPDM sealing screw 25mm', category: 'fastener', unit: 'pcs', unitPrice: 0.35, selectable: false }
  };

  /* Map screw-tag → material ID (which physical fastener is used at each joint). */
  var SCREW_MATERIAL = {
    'screw-frame':       'FAS80100',
    'screw-decking':     'FAS60080',
    'screw-post-bottom': 'FAS80100',
    'screw-rail-side':   'FAS80100',
    'screw-rail-long':   'FAS60080',
    'screw-rafter':      'FAS60160',
    'screw-batten':      'FAS50060',
    'screw-roof':        'FAS00225',
    'screw-brace':       'FAS80100'
  };

  /* Map lumber-part-tag → material ID. */
  var PART_MATERIAL = {
    'frame':     'KVH10060',
    'post':      'KVH10060',
    'rail-long': 'KVH10060',
    'rail-side': 'KVH10060',
    'rafter':    'KVH10060',
    'brace':     'KVH10060',
    'batten':    'KVL20040',
    'decking':   'TST00001',
    'roof':      'ROR12004'
  };

  /* ----------------------------------------------------------
     RACK INSTANCES — single source of truth for clickable parts.

     Each mesh that the user can pick in 3D stage 1 (default full
     build view) gets
     registered here during RackRenderer.build(). The registry is
     queried by:
       • RackRaycaster — picks instance from raycast hit mesh
       • ConfiguratorAssetDrawer — paints BoQ + per-instance data
       • RackHighlight — applies hover/select rendering

     Instance shape (MINIMAL — bridge provides rich data on demand):
       {
         id          — stable string ('rack-post-high-back-l', …)
         partType    — internal rack tag ('post', 'decking', …) used by
                       the renderer for opacity / highlight rules
         boqCode     — material ID ('KVH10060', 'TST00001', …) — links
                       to central BoQ Google Sheet (name, category,
                       unit, translations) and to user_prices
                       (per-customer affiliate product assignment)
         length      — this piece's contribution (m for lumber, m² for roof)
         position    — { x, y, z } world coords (snapshot at build time)
         mesh        — Three.js mesh reference
       }

     NAME, CATEGORY, UNIT, etc. are NOT stored here. They are looked
     up at drawer-open time via ConfiguratorBudgetBridge.getBoqItem,
     which fetches from the central BoQ Google Sheet — single source
     of truth, localized across 20 languages.

     Rebuilt fresh on every RackRenderer.build() — STATE changes
     reset the registry, so instance IDs aren't stable across L/W/H
     edits. That's deliberate: the drawer closes on any rebuild, and
     IDs only need to last for the lifetime of a single configuration.
     ---------------------------------------------------------- */
  global.RackInstances = {
    _list:    {},     /* id → instance */
    _meshes:  [],     /* parallel array of meshes for raycaster */
    _counter: {},     /* partType → next index */

    clear: function () {
      this._list = {};
      this._meshes = [];
      this._counter = {};
    },

    /* Add one instance. Returns the assigned ID (caller may stash). */
    add: function (instance) {
      if (!instance || !instance.id) return null;
      this._list[instance.id] = instance;
      if (instance.mesh) this._meshes.push(instance.mesh);
      return instance.id;
    },

    /* Generate an auto-id from partType + counter. Use idHint to inject
       a meaningful suffix ('post-high-back-l', 'decking-3') when the
       caller can be specific; otherwise falls back to numeric index. */
    nextId: function (partType, idHint) {
      if (this._counter[partType] === undefined) this._counter[partType] = 0;
      var idx = this._counter[partType]++;
      var suffix = idHint || String(idx).padStart(2, '0');
      return 'rack-' + partType + '-' + suffix;
    },

    get: function (id) {
      return this._list[id] || null;
    },

    all: function () {
      var out = [];
      for (var k in this._list) {
        if (this._list.hasOwnProperty(k)) out.push(this._list[k]);
      }
      return out;
    },

    meshes: function () {
      return this._meshes.slice();
    },

    /* Reverse lookup: find instance by mesh reference (used by raycaster
       to translate a hit Object3D back to an instance). */
    findByMesh: function (mesh) {
      if (!mesh) return null;
      for (var k in this._list) {
        if (this._list.hasOwnProperty(k) && this._list[k].mesh === mesh) {
          return this._list[k];
        }
      }
      return null;
    },

    /* Helper used by drawer to enrich an instance with project-level
       quantities, BoQ master data, and any per-customer affiliate
       product assignment.

       Single source of truth (in priority order):
         1. ConfiguratorBudgetBridge — Central BoQ Google Sheet (name,
            category, unit, dimensions, translations) +
            per-customer user_prices (assigned product, price, URL,
            image, vendor). Both shared with budget-prd-cat-selector.
         2. MATERIALS (this file) — geometric profile data only
            (width/height of timber profile, color, roughness). These
            are intrinsic configurator-renderer concerns, NOT BoQ data.
         3. RackBoQ.compute() — project quantity for this code (the
            configurator OWNS amount calculations per Marek's design).

       The drawer doesn't care about MATERIALS at all; it sees the
       enriched shape below. Bridge calls are null-safe — if the bridge
       hasn't loaded yet (slow Sheets fetch), the drawer falls back to
       the partType / category we tagged at build() time, so the user
       sees something meaningful immediately and the rich BoQ name
       fills in once the sheet finishes loading. */
    enrichForDrawer: function (instance) {
      if (!instance) return null;
      var bridge = global.ConfiguratorBudgetBridge || null;

      /* Bridge: BoQ master data (localized name/category/unit from
         the central Google Sheet's per-locale columns). If the bridge
         hasn't loaded yet (slow Sheets fetch on first visit), we fall
         back to the BoQ code itself so the user sees something stable
         while the rich data fills in once the sheet finishes loading. */
      var boqItem = (bridge && bridge.getBoqItem) ? bridge.getBoqItem(instance.boqCode) : null;
      var name     = (boqItem && boqItem.name)     || instance.boqCode;
      var category = (boqItem && boqItem.category) || null;

      /* Pick metric/imperial unit based on STATE.
         Resolution order:
           1. Localized unit from the central Google Sheet (best —
              respects locale, e.g. CS "ks" vs EN "pcs").
           2. Local MATERIALS catalog unit (always available, never
              localized — used while the sheet is still loading or
              when an item isn't in the sheet yet).
           3. null — drawer renders "—" rather than guess.
         The fallback to MATERIALS guarantees the user never sees a
         bare number without a unit, even on a cold first visit
         when the sheet fetch is still in flight. */
      var unit = boqItem
        ? (STATE.isMetric ? boqItem.unitMetric : boqItem.unitImperial)
        : null;
      if (!unit) {
        var localMat = MATERIALS[instance.boqCode];
        if (localMat && localMat.unit) {
          /* Local catalog stores metric units. For imperial mode we
             map area/linear unit names so the user sees a consistent
             system (no "ft × 18 m²" mixups). */
          if (STATE.isMetric) {
            unit = localMat.unit;
          } else {
            var lm = String(localMat.unit).toLowerCase();
            if (lm === 'm' || lm === 'meter')          unit = 'ft';
            else if (lm === 'm²' || lm === 'm2')       unit = 'ft²';
            else if (lm === 'm³' || lm === 'm3')       unit = 'ft³';
            else                                        unit = localMat.unit; /* pcs / kg — unchanged */
          }
        }
      }

      /* `type` shown in the drawer's Instance section. We use the BoQ
         name (already localized via the sheet) — no hardcoded English
         text. The technical identifier (idHint) is already visible as
         the Instance ID, so the type field stays purely descriptive
         of WHAT the part is, not WHERE it is. */
      var type = name;

      /* Bridge: user_prices assignment (if any).
         userPrice.price is ALREADY converted to current unit by the
         bridge (e.g. $7/FT in imperial mode), and userPrice.priceMetric
         keeps the raw storage value ($22.97/m) for any consumer that
         needs it. */
      var userPrice = (bridge && bridge.getUserPrice) ? bridge.getUserPrice(instance.boqCode) : null;
      var product = userPrice ? {
        title:    userPrice.title,
        url:      userPrice.url,
        image:    userPrice.image,
        source:   userPrice.source,
        price:    userPrice.price,
        currency: userPrice.currency,
        unit:     unit
      } : null;

      /* Project quantity. RackBoQ.byMaterial holds METRIC values
         (e.g. 46.32 m for KVH lumber). For the drawer's "Project
         quantity" and "Project total" lines we want CURRENT-unit
         numbers, so the math user sees matches the unit label next
         to it (151.93 FT × $7/FT = $1063 in imperial). */
      var boq = global.RackBoQ ? global.RackBoQ.compute() : null;
      var metricQty = (boq && boq.byMaterial) ? boq.byMaterial[instance.boqCode] : null;

      /* Convert qty to current unit via the same coefficient logic
         the bridge uses for prices. Keeps math symmetric: if qty is
         multiplied by 3.281 going metric→imperial, price is divided
         by 3.281 the other way → total stays invariant. */
      var qty = metricQty;
      if (metricQty != null && !STATE.isMetric && boqItem) {
        var label = String(boqItem.unitMetric || '').trim().toUpperCase();
        if (label === 'M')        qty = metricQty * 3.281;
        else if (label === 'M2')  qty = metricQty * 10.764;
        else if (label === 'M3')  qty = metricQty * (String(boqItem.unitImperial || '').trim().toUpperCase() === 'YD3' ? 1.30795 : 35.315);
        /* PCS/KG/unknown — keep metric value (already correct). */
      }

      /* Project totals — uses per-customer price when available.
         Both unitPrice and qty are now in the same unit system, so
         the multiplication is meaningful. */
      var unitPrice = (userPrice && typeof userPrice.price === 'number') ? userPrice.price : null;
      var currency  = (userPrice && userPrice.currency) || null;
      var totalPrice = (qty != null && unitPrice != null) ? (qty * unitPrice) : null;

      return {
        id:                 instance.id,
        name:               name,
        type:               type,
        boqCode:            instance.boqCode,
        category:           category,
        unit:               unit,
        position:           instance.position,
        length:             instance.length,
        /* 2D extent for plate-like pieces (roof, decking). If set,
           the asset drawer shows "L × W m" and hides the linear
           length cell. Lumber leaves this null. */
        dimensions:         instance.dimensions || null,
        projectQuantity:    qty,
        projectUnitPrice:   unitPrice,
        projectTotalPrice:  totalPrice,
        currency:           currency,
        product:            product
      };
    }
  };

  /* ----------------------------------------------------------
     RACK BOQ
     ---------------------------------------------------------- */
  global.RackBoQ = {
    compute: function (s) {
      s = s || STATE;
      var tw = DIMS.twoByFour.w, th = DIMS.twoByFour.h;
      var dWide = DIMS.decking.h, dGap = DIMS.deckGap;
      var battenW = DIMS.oneByTwo.h;

      var Hfront = s.H + th, Hback = s.H;
      var halfL = s.L / 2, halfW = s.W / 2;

      /* ── KVH (2×4) — count every piece precisely ────────────────────── */
      var rafterExtension = DIMS.rafterExt;
      var rafterLenX = s.W + 2 * tw + rafterExtension;
      var rafterTypicalGap = DIMS.rafterGap;
      var rafterCount = Math.max(2, Math.round((s.L - tw) / rafterTypicalGap) + 1);

      /* Post count: 4 for short racks, 6 once L exceeds MID_POST_THRESHOLD
         (extra pair on +X tall side and -X short side at mid-span). */
      var hasMidPosts  = s.L > MID_POST_THRESHOLD;
      var nPostsHigh   = hasMidPosts ? 3 : 2;
      var nPostsLow    = hasMidPosts ? 3 : 2;
      var nPostsTotal  = nPostsHigh + nPostsLow;

      /* Brace length — approximate centerline (iterate-free estimate) */
      var braceTopY = Hback - 2 * th;
      var braceBotY = DIMS.deckHeight;
      var braceTopZ = -halfL - 0.05;
      var braceBotZ =  halfL + 0.05;
      var braceLen = Math.sqrt(Math.pow(braceTopY - braceBotY, 2) +
                               Math.pow(braceTopZ - braceBotZ, 2));

      var kvhMeters =
        nPostsHigh * Hfront + nPostsLow * Hback +  /* 4 or 6 posts */
        2 * s.L +                        /* 2 bottom-frame length rails */
        2 * (s.W - 4 * tw) +             /* 2 bottom-frame width rails */
        2 * s.L +                        /* 2 long top rails (front + back) */
        2 * (s.W - 2 * tw) +             /* 2 side top rails */
        rafterCount * rafterLenX +       /* rafters */
        braceLen;                        /* diagonal brace */

      /* ── KVL20040 (battens) ─────────────────────────────────────────── */
      var battenInset = DIMS.battenInset;
      var rafterBaseLen = s.W + 2 * tw;
      var battenFirstX = -rafterBaseLen / 2 + battenInset + battenW / 2;
      var battenLastX  =  rafterBaseLen / 2 + rafterExtension - battenInset - battenW / 2;
      var battenUsable = battenLastX - battenFirstX;
      var battenTypicalGap = DIMS.battenGap;
      var battenCount = Math.max(2, Math.round(battenUsable / battenTypicalGap) + 1);
      var battenMeters = battenCount * s.L;

      /* ── TST00001 (decking) ─────────────────────────────────────────── */
      var Nboards = _deckCount(s.L);
      var boardLen = s.W - 2 * tw;
      var deckingMeters = Nboards * boardLen;

      /* ── ROR12004 (roofing area) ────────────────────────────────────── */
      var overhangX = DIMS.roofOverX, overhangZ = DIMS.roofOverZ;
      var roofXSpan = s.W + 2 * tw + rafterExtension + 2 * overhangX;
      var roofL = s.L + 2 * overhangZ;
      var roofArea = roofXSpan * roofL;

      /* ── FASTENER COUNTS ────────────────────────────────────────────── */
      var nFrameScrew      = 2 * 4;                  /* 4 corners × 2 side-driven screws */
      var nDeckingScrew    = 4 * Nboards;            /* 4 per board (2 per joint × 2 joints) */
      var nPostBottomScrew = nPostsTotal * 4;        /* every post × 4 screws into frame */
      var nRailSideScrew   = 2 * 2 * 2;              /* 2 rails × 2 ends × 2 screws stacked */
      var nRailLongScrew   = nPostsTotal * 4;        /* every post × 4 top-joint screws */
      var nRafterScrew     = 2 * rafterCount;
      var nBattenScrew     = battenCount * rafterCount;
      /* Roof screws — one per valley per batten, within the inset zone.
         Matches the valley-enumeration placement in the renderer. */
      var corrugPeriodLocal = 0.076;
      var roofInsetLocal    = 0.050;
      var ribsLocal = Math.max(8, Math.round(roofL / corrugPeriodLocal));
      var zMinLocal = -s.L / 2 + roofInsetLocal;
      var zMaxLocal =  s.L / 2 - roofInsetLocal;
      var nRoofScrewPerBatten = 0;
      var validIdxC = 0;
      for (var rk = 0; rk <= ribsLocal; rk++) {
        var vzLocal = roofL * ((rk - 0.25) / ribsLocal - 0.5);
        if (vzLocal >= zMinLocal && vzLocal <= zMaxLocal) {
          if (validIdxC % 3 === 0) nRoofScrewPerBatten++;
          validIdxC++;
        }
      }
      var nRoofScrew       = battenCount * nRoofScrewPerBatten;
      var nBraceScrew      = 2 * 2;                  /* 2 ends × 2 screws on brace centreline */

      /* Aggregate per material ID (some IDs are shared across joint types) */
      var FAS80100 = nFrameScrew + nPostBottomScrew + nRailSideScrew + nBraceScrew;
      var FAS60160 = nRafterScrew;
      var FAS60080 = nDeckingScrew + nRailLongScrew;
      var FAS50060 = nBattenScrew;
      var FAS00225 = nRoofScrew;

      return {
        byMaterial: {
          KVH10060: kvhMeters,
          KVL20040: battenMeters,
          TST00001: deckingMeters,
          ROR12004: roofArea,
          FAS80100: FAS80100,
          FAS60160: FAS60160,
          FAS60080: FAS60080,
          FAS50060: FAS50060,
          FAS00225: FAS00225
        },
        /* Backward-compat (used by existing refreshUI bindings) */
        kvh: kvhMeters,
        tst: deckingMeters,
        ror: roofArea,
        decking_count: Nboards,
        rafter_count: rafterCount,
        batten_count: battenCount
      };
    }
  };

  /* ----------------------------------------------------------
     PERSON SILHOUETTE — async GLB loader (cached).
     Uses the IDENTICAL loader pattern as pool snippet's inline
     _getGLTFLoader() (around line 2948 of configurator-pool-snippet.liquid).
     Pool's pattern works in production; this is a byte-for-byte copy.
     ---------------------------------------------------------- */
  var PERSON_GLB_URL = 'https://cdn.shopify.com/3d/models/a185bdffeca46290/human.glb';
  var _personScene = null;
  var _personLoading = null;
  var _personAttachLogCount = 0;  /* counter to suppress repeated attach logs */

  function _dbg(msg) {
    if (global.Configurator && global.Configurator.log && global.Configurator.log.log) {
      global.Configurator.log.log('[rack-person] ' + msg);
    }
  }

  function _getGLTFLoader() {
    return new Promise(function (resolve, reject) {
      var T = global.THREE;
      if (T && T.GLTFLoader) { _dbg('THREE.GLTFLoader already on window'); resolve(T.GLTFLoader); return; }
      if (global.GLTFLoader) { _dbg('window.GLTFLoader already loaded');   resolve(global.GLTFLoader); return; }
      if (!T) { reject(new Error('THREE not on window')); return; }

      var rev = T.REVISION || '128';
      _dbg('THREE revision = ' + rev + ', loading GLTFLoader...');

      /* Step 1: classic <script> from jsdelivr (works for r147-, 404s for r148+). */
      var script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/three@0.' + rev + '.0/examples/js/loaders/GLTFLoader.js';
      _dbg('trying classic: ' + script.src);
      script.onload = function () {
        if (T.GLTFLoader)        { _dbg('classic GLTFLoader loaded → THREE'); resolve(T.GLTFLoader); return; }
        if (global.GLTFLoader)   { _dbg('classic GLTFLoader loaded → window'); resolve(global.GLTFLoader); return; }
        _dbg('classic script loaded but no GLTFLoader exposed, trying ESM');
        tryESM();
      };
      script.onerror = function () {
        _dbg('classic script 404 (expected for r148+), trying ESM');
        tryESM();
      };
      document.head.appendChild(script);

      function tryESM() {
        var url = 'https://esm.sh/three@0.' + rev + '/examples/jsm/loaders/GLTFLoader.js';
        _dbg('importing ESM: ' + url);
        try {
          import(url).then(function (mod) {
            if (mod && mod.GLTFLoader) { _dbg('ESM GLTFLoader resolved'); resolve(mod.GLTFLoader); }
            else { _dbg('ESM module has no GLTFLoader'); reject(new Error('no GLTFLoader in esm.sh module')); }
          }).catch(function (e) {
            _dbg('ESM import rejected: ' + (e && e.message ? e.message : e));
            reject(e);
          });
        } catch (e) {
          _dbg('ESM import threw: ' + (e && e.message ? e.message : e));
          reject(e);
        }
      }
    });
  }

  function _loadPersonScene() {
    if (_personScene) return Promise.resolve(_personScene);
    if (_personLoading) return _personLoading;

    _personLoading = _getGLTFLoader().then(function (Loader) {
      return new Promise(function (resolve, reject) {
        var T = global.THREE;
        _dbg('GLTFLoader ready, loading GLB from ' + PERSON_GLB_URL);
        var loader = new Loader();
        loader.load(PERSON_GLB_URL, function (gltf) {
          _dbg('GLB loaded successfully');
          var scene = gltf.scene;
          /* Scale to 175 cm tall. */
          var bbox = new T.Box3().setFromObject(scene);
          var size = bbox.getSize(new T.Vector3());
          _dbg('original bbox y=' + size.y.toFixed(3));
          if (size.y > 0) scene.scale.setScalar(1.75 / size.y);
          /* Re-measure AFTER scaling, then translate so:
                feet at Y=0, X-center at 0, Z-center at 0. */
          var bbox2 = new T.Box3().setFromObject(scene);
          var center = bbox2.getCenter(new T.Vector3());
          scene.position.set(-center.x, -bbox2.min.y, -center.z);
          _dbg('after-fit: bbox.min.y=' + bbox2.min.y.toFixed(2) +
               ' center.x=' + center.x.toFixed(2) + ' center.z=' + center.z.toFixed(2));
          /* Override all materials → faded near-white. */
          var mat = new T.MeshStandardMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.18,
            depthWrite: false,
            roughness: 1.0,
            emissive: 0xffffff,
            emissiveIntensity: 0.15
          });
          scene.traverse(function (child) {
            if (child.isMesh) {
              child.material = mat;
              child.castShadow = false;
              child.receiveShadow = false;
            }
          });
          _personScene = scene;
          resolve(scene);
        }, function (xhr) {
          /* progress */
        }, function (err) {
          _dbg('GLB load failed: ' + (err && err.message ? err.message : err));
          reject(err);
        });
      });
    }).catch(function (err) {
      _dbg('person load chain failed: ' + (err && err.message ? err.message : err));
      _personLoading = null;  /* allow retry on next build */
      throw err;
    });

    return _personLoading;
  }

  /* ----------------------------------------------------------
     ENGINE  (scene / camera / renderer / controls)
     ---------------------------------------------------------- */
  global.RackEngine = {
    scene: null,
    camera: null,           /* perspective — used in 3D */
    orthoCamera: null,      /* orthographic — used in 2D top-down (no perspective distortion) */
    renderer: null,
    controls: null,         /* OrbitControls bound to perspective camera */
    orthoControls: null,    /* OrbitControls bound to ortho camera (rotate disabled) */
    rackGroup: null,
    _orthoHalfW: 2.5,       /* ortho frustum half-width in world units (≈ 5m wide view by default) */
    _syncingPan: false,     /* re-entrancy guard for the pan-sync handlers */

    init: function () {
      var T = global.THREE;
      var root = document.getElementById('cf-root');
      var canvas = document.getElementById('cf-canvas');
      if (!T || !root || !canvas) return false;

      this.scene = new T.Scene();
      /* No background colour: canvas is transparent so the page background
         (or map-manager mapbox layer) shows through. Page bg is #f5f5f5. */

      var W = root.clientWidth, H = root.clientHeight;
      var ratio = W / H;

      /* ── Perspective camera (3D mode) ───────────────────────────────── */
      this.camera = new T.PerspectiveCamera(40, ratio, 0.1, 100);
      this.camera.position.set(3.5, 2.2, 3.5);
      this.camera.lookAt(0, 0.9, 0);

      /* ── Orthographic camera (2D top-down mode) ──────────────────────
         Position high above the origin looking straight down (-Y). The
         actual frustum (left/right/top/bottom) is set by _applyOrthoZoom
         so it adapts to the canvas aspect on resize. */
      var hw = this._orthoHalfW;
      this.orthoCamera = new T.OrthographicCamera(-hw, hw, hw / ratio, -hw / ratio, 0.01, 200);
      this.orthoCamera.position.set(0, 50, 0);
      this.orthoCamera.lookAt(0, 0, 0);

      /* ── Elevation cameras (2D stage 2 — nárys + bokorys CAD drawings) ──
         Two more ortho cameras used ONLY when STATE.vizStage === 2 in 2D.
         The render loop draws each into one half of the canvas via
         setViewport. Position them far enough from the rack to clear any
         model size; the frustum is recomputed per-frame to fit STATE.L/W/H
         with a small margin so the drawings look like proper elevation
         views (filling each half).
           • orthoCameraFront — looks DOWN +Z (south → north), shows L × H
           • orthoCameraSide  — looks DOWN +X (east → west), shows W × H
         Both are children of `this` rather than the scene, so no orbit
         controls are attached — the views are static, fitted per frame. */
      this.orthoCameraFront = new T.OrthographicCamera(-1, 1, 1, -1, 0.01, 200);
      this.orthoCameraFront.position.set(0, 0, 50);
      this.orthoCameraFront.up.set(0, 1, 0);
      this.orthoCameraFront.lookAt(0, 0, 0);

      this.orthoCameraSide = new T.OrthographicCamera(-1, 1, 1, -1, 0.01, 200);
      this.orthoCameraSide.position.set(50, 0, 0);
      this.orthoCameraSide.up.set(0, 1, 0);
      this.orthoCameraSide.lookAt(0, 0, 0);

      /* preserveDrawingBuffer: true is REQUIRED so we can call
         canvas.toDataURL() for PDF export (and other snapshot use
         cases). Without it Three.js clears the WebGL backbuffer
         right after present, and toDataURL returns a transparent
         image — that's the bug Marek hit in the first PDF where
         the 3D / 2D pages came out empty.
         Small perf cost (browser can't recycle the backbuffer) but
         the rack scene is interaction-driven, not 60fps animated,
         so it's invisible in practice. */
      this.renderer = new T.WebGLRenderer({
        canvas: canvas,
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true
      });
      this.renderer.setClearColor(0x000000, 0);  /* fully transparent */
      this.renderer.setSize(W, H, false);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      this.renderer.toneMapping = T.ACESFilmicToneMapping;

      /* ── Perspective controls (3D) ─────────────────────────────────── */
      this.controls = new global.OrbitControls(this.camera, canvas);
      this.controls.target.set(0, 0.9, 0);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.08;
      this.controls.minDistance = 1.5;
      this.controls.maxDistance = 15;
      this.controls.maxPolarAngle = Math.PI * 0.49;
      this.controls.update();

      /* ── Ortho controls (2D) ───────────────────────────────────────────
         Mirrors the pool configurator's pattern: rotation disabled (we
         must stay top-down), LEFT mouse button DISABLED so it doesn't
         steal events from drag handles, MIDDLE = dolly (zoom),
         RIGHT = pan. Single touch is freed for handles too; pinch
         = dolly + pan. */
      this.orthoControls = new global.OrbitControls(this.orthoCamera, canvas);
      this.orthoControls.enableRotate = false;
      this.orthoControls.enableDamping = false;
      this.orthoControls.mouseButtons = { LEFT: null, MIDDLE: T.MOUSE.DOLLY, RIGHT: T.MOUSE.PAN };
      this.orthoControls.touches = { ONE: null, TWO: T.TOUCH.DOLLY_PAN };
      this.orthoControls.minZoom = 0.3;
      this.orthoControls.maxZoom = 6;
      this.orthoControls.enabled = false;

      /* ── Pan synchronisation ───────────────────────────────────────────
         When the user pans in one view, mirror the pan in the other so
         that switching 2D ↔ 3D keeps the same world point under the
         camera. Only the X/Z components are synchronised (Y differs —
         perspective camera looks at rack mid-height, ortho looks at
         ground). The _syncingPan guard prevents infinite ping-pong. */
      var self = this;
      this._lastOrthoT = { x: 0, z: 0 };
      this._lastPerspT = { x: this.controls.target.x, z: this.controls.target.z };

      function syncFromOrtho() {
        var tx = self.orthoControls.target.x;
        var tz = self.orthoControls.target.z;
        if (!self._syncingPan) {
          var dx = tx - self._lastOrthoT.x;
          var dz = tz - self._lastOrthoT.z;
          if (Math.abs(dx) > 1e-5 || Math.abs(dz) > 1e-5) {
            self._syncingPan = true;
            self.controls.target.x += dx;
            self.controls.target.z += dz;
            self.camera.position.x += dx;
            self.camera.position.z += dz;
            self._lastPerspT.x = self.controls.target.x;
            self._lastPerspT.z = self.controls.target.z;
            self._syncingPan = false;
          }
        }
        self._lastOrthoT.x = tx; self._lastOrthoT.z = tz;
      }
      function syncFromPersp() {
        var tx = self.controls.target.x;
        var tz = self.controls.target.z;
        if (!self._syncingPan) {
          var dx = tx - self._lastPerspT.x;
          var dz = tz - self._lastPerspT.z;
          if (Math.abs(dx) > 1e-5 || Math.abs(dz) > 1e-5) {
            self._syncingPan = true;
            self.orthoControls.target.x += dx;
            self.orthoControls.target.z += dz;
            self.orthoCamera.position.x += dx;
            self.orthoCamera.position.z += dz;
            self._lastOrthoT.x = self.orthoControls.target.x;
            self._lastOrthoT.z = self.orthoControls.target.z;
            self._syncingPan = false;
          }
        }
        self._lastPerspT.x = tx; self._lastPerspT.z = tz;
      }
      this.orthoControls.addEventListener('change', syncFromOrtho);
      this.controls.addEventListener('change', syncFromPersp);

      /* ── Lights ─────────────────────────────────────────────────────── */
      this.scene.add(new T.AmbientLight(0xffffff, 0.65));
      var sun = new T.DirectionalLight(0xffffff, 0.85);
      sun.position.set(3, 6, 4);
      this.scene.add(sun);
      var fill = new T.DirectionalLight(0xffffff, 0.25);
      fill.position.set(-3, 4, -2);
      this.scene.add(fill);

      this.rackGroup = new T.Group();
      this.scene.add(this.rackGroup);

      /* ── Animation loop ────────────────────────────────────────────────
         Renders with whichever camera is "active" per STATE.view +
         STATE.vizStage:
           • 3D any stage:  perspective camera, full canvas
           • 2D stage 1:    top-down ortho, full canvas (plan)
           • 2D stage 2:    split screen — front elevation (L × H) on the
                             left half, side elevation (W × H) on the right
                             half. Both cameras' frusta are re-fitted per
                             frame to STATE.L/W/H with a small margin so
                             the drawings always fill their half. */
      this.renderer.setAnimationLoop(function () {
        if (self._renderPathLogCount === undefined) self._renderPathLogCount = 0;
        if (self._renderPathLogCount < 5) {
          self._renderPathLogCount++;
          var L_ = global.Configurator && global.Configurator.log;
          if (L_) L_.log('[render-path] frame=' + self._renderPathLogCount +
                         ' view=' + STATE.view + ' stage=' + STATE.vizStage +
                         ' branch=' + (STATE.view === '2d'
                            ? (STATE.vizStage === 2 ? 'side-elevation' : 'plan')
                            : '3d'));
        }

        if (STATE.view === '2d') {
          /* Both stages use orthoCamera + orthoControls. Difference is
             camera position (set in applyVizStage): stage 1 top-down,
             stage 2 side view. Render path identical. */
          self.orthoControls.update();
          if (global.RackHandles && global.RackHandles.reposition) global.RackHandles.reposition();
          self.renderer.setClearColor(0x000000, 0);
          self.renderer.render(self.scene, self.orthoCamera);
        } else {
          self.controls.update();
          if (global.RackHandles && global.RackHandles.reposition) global.RackHandles.reposition();
          self.renderer.setClearColor(0x000000, 0);
          self.renderer.render(self.scene, self.camera);
        }
      });

      function onResize() {
        var w = root.clientWidth, h = root.clientHeight;
        self.renderer.setSize(w, h, false);
        self.camera.aspect = w / h;
        self.camera.updateProjectionMatrix();
        self._applyOrthoZoom();
      }
      window.addEventListener('resize', onResize);
      if (window.ResizeObserver) new ResizeObserver(onResize).observe(root);

      /* ── Stage 2 elevation zoom — wheel + pinch ─────────────────────
         OrbitControls only works on top-down orthoCamera (which is the
         renderer's plan camera, not elevation cameras). For stage 2 we
         add a custom zoom handler that adjusts `_elevZoomFactor`; the
         next _fitElevationCameras call picks up the new factor.
         Bounds: 0.5× (zoomed out) .. 3.0× (zoomed in). */
      self._elevZoomFactor = 1.0;
      function adjustElevZoom(delta) {
        if (STATE.view !== '2d' || STATE.vizStage !== 2) return;
        var z = self._elevZoomFactor * Math.exp(delta);
        if (z < 0.4) z = 0.4;
        if (z > 3.5) z = 3.5;
        self._elevZoomFactor = z;
      }
      canvas.addEventListener('wheel', function (e) {
        if (STATE.view !== '2d' || STATE.vizStage !== 2) return;
        e.preventDefault();
        adjustElevZoom(-e.deltaY * 0.001);
      }, { passive: false });

      var _pinchPrev = 0;
      canvas.addEventListener('touchstart', function (e) {
        if (e.touches.length === 2) {
          var dx = e.touches[0].clientX - e.touches[1].clientX;
          var dy = e.touches[0].clientY - e.touches[1].clientY;
          _pinchPrev = Math.sqrt(dx*dx + dy*dy);
        }
      }, { passive: true });
      canvas.addEventListener('touchmove', function (e) {
        if (STATE.view !== '2d' || STATE.vizStage !== 2) return;
        if (e.touches.length !== 2) return;
        e.preventDefault();
        var dx = e.touches[0].clientX - e.touches[1].clientX;
        var dy = e.touches[0].clientY - e.touches[1].clientY;
        var dist = Math.sqrt(dx*dx + dy*dy);
        if (_pinchPrev > 0) {
          var ratio = dist / _pinchPrev;
          adjustElevZoom(Math.log(ratio));
        }
        _pinchPrev = dist;
      }, { passive: false });

      return true;
    },

    /* Recompute ortho camera frustum from current canvas aspect.
       Called on resize and from init. _orthoHalfW controls the "zoom"
       level (smaller value = more zoomed in). */
    _applyOrthoZoom: function () {
      if (!this.orthoCamera) return;
      var root = document.getElementById('cf-root');
      if (!root) return;
      var W = root.clientWidth, H = root.clientHeight;
      var ratio = W / H;
      var hw = this._orthoHalfW || 2.5;
      this.orthoCamera.left = -hw;
      this.orthoCamera.right = hw;
      this.orthoCamera.top = hw / ratio;
      this.orthoCamera.bottom = -hw / ratio;
      this.orthoCamera.updateProjectionMatrix();
    },

    /* Stage 2 — side elevation (bokorys) camera fit.
       Clone of _applyOrthoZoom but for orthoCameraSide which looks at the
       rack from +X axis (so we see W horizontally, H vertically).
       Uses same _orthoHalfW value as plan camera so visual scale matches.
       Camera centred at rack mid-height for nicer framing. */
    _fitSideElevationCamera: function () {
      if (!this.orthoCameraSide) return;
      var root = document.getElementById('cf-root');
      if (!root) return;
      var W = root.clientWidth, H = root.clientHeight;
      var ratio = W / H;
      var hw = this._orthoHalfW || 2.5;
      var Hg = STATE.H;
      var cS = this.orthoCameraSide;
      cS.left   = -hw;
      cS.right  =  hw;
      cS.top    =  hw / ratio;
      cS.bottom = -hw / ratio;
      cS.position.set(50, Hg / 2, 0);
      cS.up.set(0, 1, 0);
      cS.lookAt(0, Hg / 2, 0);
      cS.updateMatrixWorld(true);
      cS.updateProjectionMatrix();
    },

    /* ─── _renderElevations ─────────────────────────────────────────────
       Renders 2D stage 2 (nárys + bokorys as a CAD-style drawing):
       split-screen with the front elevation on the LEFT half and the
       side elevation on the RIGHT half.

       Each elevation:
         • Frustum is fitted PER FRAME to STATE.L/W/H × MARGIN_FACTOR so
           the drawing always fills its half regardless of how the user
           resized the rack with drag handles.
         • Camera looks ORTHOGONALLY at the rack — front along +Z axis
           (so we see L horizontally, H vertically), side along +X axis
           (so we see W horizontally, H vertically).
         • Renderer's clear color is set to WHITE for elevations only;
           reset to transparent for stages that need the page bg.
         • Edge color (set on materials in applyVizStage) makes the
           drawing read like a CAD print: dark thin contours on white.

       Why per-frame fit instead of once on stage entry: the user can
       drag dimension handles WHILE in stage 2 (we keep handles visible
       per the design); on each frame we re-measure STATE and re-derive
       the frustum so the drawing scales smoothly to fit. */
    /* Configure orthoCameraFront and orthoCameraSide frusta + positions so
       both elevations fit their viewport half with UNIFORM SCALE.

       Fitting strategy: take the SMALLER scale (= rack fills the more-
       constrained axis exactly, may overshoot the other). This is the
       opposite of "object-fit: contain" — it's "object-fit: cover" without
       cropping. In practice for an upright rack (taller than wide) inside
       a tall viewport half, this means the rack fills the vertical extent
       with horizontal padding; for a wide rack inside the same viewport,
       it fills horizontally with vertical padding.

       Why this works better than "contain" (max scale): with contain, an
       upright rack inside a tall viewport ended up tiny because the
       frustum had to grow vertically to match the viewport aspect, leaving
       2/3 of the canvas empty around the rack. With cover-style fit the
       rack visually dominates the canvas (with light padding from MARGIN).

         scaleX = rack_W / halfW    meters/pixel needed for full width
         scaleY = rack_H / canvasH  meters/pixel needed for full height
         pickedScale = MIN(scaleX, scaleY)  ← the LOOSER axis wins
         frustumW = halfW × pickedScale     ← may be > rack width (padding)
         frustumH = canvasH × pickedScale   ← may be > rack height (padding)

       Called from animation loop BEFORE handle reposition (so projections
       use up-to-date frustum) and from _renderElevations. Idempotent. */
    _fitElevationCameras: function () {
      var T = global.THREE;
      if (!T) return;
      var canvas = this.renderer.domElement;
      var canvasW = canvas.width;
      var canvasH = canvas.height;
      var halfW = canvasW / 2;
      var L = STATE.L, Wd = STATE.W, Hg = STATE.H;

      /* Use a FIXED pixels-per-meter scale, modulated by user zoom.
         At base (zoom=1.0): 1 meter = ~200px in device space, so a 1.5m
         rack renders ~300px wide. With pixelRatio 1.75, that's ~170px
         CSS width. Reasonable rack size, with room for dim chips around.

         The user zoom factor lets them pinch/wheel to make rack bigger
         or smaller without changing world coordinates. Each elevation
         camera gets a frustum sized exactly to its viewport half so
         pixels-per-meter is uniform across both views. */
      var BASE_PX_PER_M = 200;
      var zoom = this._elevZoomFactor || 1.0;
      var pxPerM = BASE_PX_PER_M * zoom;

      /* Frustum width/height in METERS = pixels / pxPerM.
         Same for both elevations — uniform scale guarantees rack-front
         and rack-side appear at the same scale relative to each other. */
      var frustumW = halfW   / pxPerM;
      var frustumH = canvasH / pxPerM;

      var cF = this.orthoCameraFront;
      cF.left   = -frustumW / 2;
      cF.right  =  frustumW / 2;
      cF.top    =  frustumH / 2;
      cF.bottom = -frustumH / 2;
      cF.position.set(0, Hg / 2, 50);
      cF.up.set(0, 1, 0);
      cF.lookAt(0, Hg / 2, 0);
      cF.updateMatrixWorld(true);
      cF.updateProjectionMatrix();

      var cS = this.orthoCameraSide;
      cS.left   = -frustumW / 2;
      cS.right  =  frustumW / 2;
      cS.top    =  frustumH / 2;
      cS.bottom = -frustumH / 2;
      cS.position.set(50, Hg / 2, 0);
      cS.up.set(0, 1, 0);
      cS.lookAt(0, Hg / 2, 0);
      cS.updateMatrixWorld(true);
      cS.updateProjectionMatrix();

      this._lastFrontFW = frustumW;
      this._lastFrontFH = frustumH;
      this._lastSideSW  = frustumW;
      this._lastSideSH  = frustumH;
    },

    _renderElevations: function () {
      var T = global.THREE;
      if (!T) return;
      var canvas = this.renderer.domElement;
      var W = canvas.width;
      var H = canvas.height;
      var halfW = W / 2;

      /* Configure both elevation cameras for current canvas + STATE.
         (Idempotent — also called from animation loop before reposition.) */
      this._fitElevationCameras();
      var cF = this.orthoCameraFront;
      var cS = this.orthoCameraSide;

      /* One-shot diagnostic dump. */
      if (!this._elevDiagLogged) {
        this._elevDiagLogged = true;
        var L_ = global.Configurator && global.Configurator.log;
        if (L_) {
          var rect = canvas.getBoundingClientRect();
          L_.log('[elev-diag] canvas: device=' + W + 'x' + H +
                 ' css=' + Math.round(rect.width) + 'x' + Math.round(rect.height) +
                 ' pixelRatio=' + window.devicePixelRatio);
          L_.log('[elev-diag] STATE: L=' + STATE.L.toFixed(3) +
                 ' W=' + STATE.W.toFixed(3) +
                 ' H=' + STATE.H.toFixed(3) +
                 ' view=' + STATE.view + ' stage=' + STATE.vizStage);
          L_.log('[elev-diag] halfW(device)=' + halfW + ' aspect=' + (halfW / H).toFixed(3));
          L_.log('[elev-diag] FRONT frustum: L/R=±' + (this._lastFrontFW / 2).toFixed(2) +
                 ' T/B=±' + (this._lastFrontFH / 2).toFixed(2) +
                 ' pos=(0,' + (STATE.H / 2).toFixed(2) + ',50)');
          L_.log('[elev-diag] SIDE  frustum: L/R=±' + (this._lastSideSW / 2).toFixed(2) +
                 ' T/B=±' + (this._lastSideSH / 2).toFixed(2) +
                 ' pos=(50,' + (STATE.H / 2).toFixed(2) + ',0)');
        }
      }

      /* Two-pass render with scissor + viewport. Clear once white at the
         start, then render each elevation into its half. */
      this.renderer.setScissorTest(true);
      this.renderer.setClearColor(0xffffff, 1);

      /* Left half — front elevation */
      this.renderer.setViewport(0, 0, halfW, H);
      this.renderer.setScissor(0, 0, halfW, H);
      this.renderer.render(this.scene, cF);

      /* Right half — side elevation */
      this.renderer.setViewport(halfW, 0, halfW, H);
      this.renderer.setScissor(halfW, 0, halfW, H);
      this.renderer.render(this.scene, cS);

      /* Per-frame diagnostic (first 3 frames) — log viewport state
         right after each render to verify split-screen is happening. */
      if (!this._splitRenderLogCount) this._splitRenderLogCount = 0;
      if (this._splitRenderLogCount < 3) {
        this._splitRenderLogCount++;
        var DL = global.Configurator && global.Configurator.log;
        if (DL) {
          DL.log('[split-render] frame=' + this._splitRenderLogCount +
                 ' halfW=' + halfW + ' canvasH=' + H +
                 ' cF.pos=(' + cF.position.x.toFixed(0) + ',' + cF.position.y.toFixed(2) + ',' + cF.position.z.toFixed(0) +
                 ') cF.frust=(' + cF.left.toFixed(2) + ',' + cF.right.toFixed(2) + ',' + cF.top.toFixed(2) + ',' + cF.bottom.toFixed(2) +
                 ') cS.pos=(' + cS.position.x.toFixed(0) + ',' + cS.position.y.toFixed(2) + ',' + cS.position.z.toFixed(0) + ')');
        }
      }

      /* CRITICAL: restore the full-canvas viewport AND scissor so that
         when STATE.vizStage changes back to 1 (or view switches to 3D),
         the next render() call uses the entire canvas. */
      this.renderer.setScissorTest(false);
      this.renderer.setViewport(0, 0, W, H);
      this.renderer.setScissor(0, 0, W, H);
      this.renderer.setClearColor(0x000000, 0);
    },

    /* ─── VIEW + VIZ STAGE ──────────────────────────────────────────────
       Architecture:
         view      — '3d' or '2d'. Set via toggleView() or applyView().
         vizStage  — 1, 2 or 3 (3D only). Set via cycleVizStage() or applyVizStage().
         Switching view always resets vizStage to 1 in the new view.

       applyView()       — enables/disables the correct camera + controls
                           and updates the 2D/3D icon. Does NOT reset
                           camera position so user-zoom/pan persists
                           across toggles.
       applyVizStage()   — applies visual changes for current view+stage:
                           material opacities, background, mapbox visibility,
                           handle visibility, dimension overlay.
       toggleView()      — flips STATE.view, resets stage to 1, applies both.
       cycleVizStage()   — cycles vizStage within the current view.
    */
    applyView: function () {
      if (!this.camera || !this.controls || !this.orthoCamera || !this.orthoControls) return;
      /* Switch enabled state — animation loop reads STATE.view to pick
         which camera to render with, so flipping enabled here is enough.
         No camera position resets: persisted pan/zoom survive toggling. */
      if (STATE.view === '2d') {
        this.controls.enabled = false;
        this.orthoControls.enabled = true;
        this.orthoControls.update();
      } else {
        this.controls.enabled = true;
        this.orthoControls.enabled = false;
        this.controls.update();
      }

      /* Swap the 2D/3D toggle icon to reflect the TARGET state (clicking
         shows the view it would switch to). Uses the same Shopify-hosted
         PNGs as the pool configurator so visual style matches across
         configurators. */
      var iconEl = document.getElementById('cf-view-icon');
      if (iconEl) {
        if (STATE.view === '2d') {
          iconEl.src = 'https://cdn.shopify.com/s/files/1/0803/9815/6119/files/3d.png?v=1772625064';
          iconEl.alt = '3D';
        } else {
          iconEl.src = 'https://cdn.shopify.com/s/files/1/0803/9815/6119/files/2d.png?v=1772625064';
          iconEl.alt = '2D';
        }
      }
    },

    toggleView: function () {
      this._logCameraState('BEFORE toggleView (current view=' + STATE.view + ')');
      STATE.view = (STATE.view === '3d') ? '2d' : '3d';
      STATE.vizStage = 1;                 /* reset stage on every view switch */
      /* Reset elevation diagnostic flags so re-entering stage 2 re-logs. */
      this._elevDiagLogged = false;
      this._elevFrustumLogged = false;
      this._renderPathLogCount = 0;
      this._splitRenderLogCount = 0;
      if (global.RackHandles) {
        global.RackHandles._diagLogged = false;
        global.RackHandles._elevHandlesLogged = false;
      }
      if (global.RackDimensions) global.RackDimensions._dimDiagLogged = false;
      this.applyView();
      this.applyVizStage();
      this._logCameraState('AFTER  toggleView (new view=' + STATE.view + ')');
      if (global.RackLib && global.RackLib.savePref)   global.RackLib.savePref();
      if (global.RackLib && global.RackLib.refreshUI)  global.RackLib.refreshUI();
    },

    cycleVizStage: function () {
      /* 3D has 3 stages — stage 1 doubles as the asset-select mode
         (clickable, hover + outline), so users get the full-build view
         AND can inspect any piece at the same time. No separate
         "Select" stage; the raycaster is active whenever stage 1 is
         visible in 3D.
           • stage 1 = full build + asset picking (default)
           • stage 2 = inspection (structure 30 %, fasteners 100 %)
           • stage 3 = profiles (color-coded lumber categories)
         2D has 3 stages:
           • stage 1 = top-down plan (půdorys) with L/W handles + grid
           • stage 2 = side elevation (bokorys) with W/H handles
           • stage 3 = front elevation (nárys) with L/H handles
         Cycle wraps within current view. */
      this._logCameraState('BEFORE cycleVizStage (current=' + STATE.view + ':' + STATE.vizStage + ')');
      var maxStage = 3;
      STATE.vizStage = (STATE.vizStage >= maxStage) ? 1 : (STATE.vizStage + 1);
      /* Reset elev diag flags whenever we enter stage 2 from somewhere. */
      this._elevDiagLogged = false;
      this._elevFrustumLogged = false;
      this._renderPathLogCount = 0;
      this._splitRenderLogCount = 0;
      if (global.RackHandles) {
        global.RackHandles._diagLogged = false;
        global.RackHandles._elevHandlesLogged = false;
      }
      if (global.RackDimensions) global.RackDimensions._dimDiagLogged = false;
      this.applyVizStage();
      this._logCameraState('AFTER  cycleVizStage (new=' + STATE.view + ':' + STATE.vizStage + ')');
      if (global.RackLib && global.RackLib.savePref)   global.RackLib.savePref();
      if (global.RackLib && global.RackLib.refreshUI)  global.RackLib.refreshUI();
    },

    /* Diagnostic helper: dumps key camera state so we can spot when
       something gets mutated across view/stage transitions. Logs:
         • perspective camera position + controls.target + distance + fov
         • top-down ortho camera position + zoom + frustum
       The previously seen "rack enlarged and shifted after returning from
       2D stage 2" symptom would show up here as a delta in controls.target
       or orthoCamera.position between BEFORE/AFTER snapshots. */
    _logCameraState: function (label) {
      var L = global.Configurator && global.Configurator.log;
      if (!L) return;
      var c  = this.camera, t = this.controls && this.controls.target;
      var oc = this.orthoCamera;
      if (c && t) {
        L.log('[cam-state] ' + label + ' persp pos=(' +
              c.position.x.toFixed(2) + ',' + c.position.y.toFixed(2) + ',' + c.position.z.toFixed(2) +
              ') target=(' + t.x.toFixed(2) + ',' + t.y.toFixed(2) + ',' + t.z.toFixed(2) +
              ') dist=' + this.controls.getDistance().toFixed(2) + ' fov=' + c.fov);
      }
      if (oc) {
        L.log('[cam-state] ' + label + ' ortho pos=(' +
              oc.position.x.toFixed(2) + ',' + oc.position.y.toFixed(2) + ',' + oc.position.z.toFixed(2) +
              ') frustum L/R=' + oc.left.toFixed(2) + '/' + oc.right.toFixed(2) +
              ' T/B=' + oc.top.toFixed(2) + '/' + oc.bottom.toFixed(2) +
              ' zoom=' + oc.zoom.toFixed(2));
      }
      /* Rack group state: position, scale, child count, mesh material status. */
      var rg = this.rackGroup;
      if (rg) {
        var meshCount = 0, edgeCount = 0;
        var minOp = 1, maxOp = 0, sampleColors = [];
        rg.traverse(function (n) {
          if (n.isMesh && n.material) {
            meshCount++;
            if (n.material.opacity < minOp) minOp = n.material.opacity;
            if (n.material.opacity > maxOp) maxOp = n.material.opacity;
            if (sampleColors.length < 3 && n.material.color) {
              sampleColors.push('#' + n.material.color.getHexString());
            }
          } else if (n.userData && n.userData.isEdgeLine) {
            edgeCount++;
          }
        });
        L.log('[rack-state] ' + label + ' group pos=(' +
              rg.position.x.toFixed(3) + ',' + rg.position.y.toFixed(3) + ',' + rg.position.z.toFixed(3) +
              ') scale=(' + rg.scale.x.toFixed(3) + ',' + rg.scale.y.toFixed(3) + ',' + rg.scale.z.toFixed(3) +
              ') children=' + rg.children.length);
        L.log('[rack-state] ' + label + ' meshes=' + meshCount + ' edges=' + edgeCount +
              ' opacity=[' + minOp.toFixed(2) + '..' + maxOp.toFixed(2) + '] colors=' + sampleColors.join(','));
      }
      /* Renderer state: viewport, scissor, clear color. */
      if (this.renderer) {
        var vp = new (global.THREE.Vector4)();
        this.renderer.getViewport(vp);
        var sc = new (global.THREE.Vector4)();
        this.renderer.getScissor(sc);
        var cc = this.renderer.getClearColor(new (global.THREE.Color)());
        L.log('[renderer-state] ' + label +
              ' viewport=(' + Math.round(vp.x) + ',' + Math.round(vp.y) + ',' + Math.round(vp.z) + ',' + Math.round(vp.w) +
              ') scissor=(' + Math.round(sc.x) + ',' + Math.round(sc.y) + ',' + Math.round(sc.z) + ',' + Math.round(sc.w) +
              ') scissorTest=' + this.renderer.getScissorTest() +
              ' clear=#' + cc.getHexString());
      }
    },

    /* applyVizStage —— pure visual application of STATE.view + STATE.vizStage.
       Walks the rackGroup once and sets per-part opacity based on a rule
       function, plus handles edge-line styling, renderer background,
       mapbox visibility, drag handles, and dimension overlay.

       Opacity rules:
         3D stage 1: everything 1.0, edges 0.55 (classic look)
         3D stage 2: structural 0.30, fasteners 1.0, edges 0.55 (inspection)
         2D stage 1: everything 1.0, edges 0.55 (top view, drag enabled)
         2D stage 2: everything 0.20, edges 1.0 black (drawing, no drag)

       Person is hidden in 2D always (per user request — person has its own
       toggle that we only honour in 3D). */
    applyVizStage: function () {
      var rg = this.rackGroup;
      if (!rg) return;

      var view = STATE.view, stage = STATE.vizStage;

      /* View class on cf-root for CSS targeting (e.g. smaller drag
         handles in 2D). */
      var root = document.getElementById('cf-root');
      if (root) {
        root.classList.toggle('cf-view-2d', view === '2d');
        root.classList.toggle('cf-view-3d', view === '3d');
      }

      /* Control state per stage:
           • 2D any stage → orthoControls enabled (pan/zoom)
           • 3D any stage → perspective controls (handled in applyView) */
      if (this.orthoControls) {
        this.orthoControls.enabled = (view === '2d');
      }

      /* Camera position per 2D stage. Same orthoCamera object, just
         repositioned — animation loop renders with it identically.
         CONVENTION (matches STATE: L along Z, W along X):
           stage 1 (plan/půdorys)   → (0, 50, 0)   lookAt (0, 0, 0)
                                       top-down, sees X×Z = W×L
           stage 2 (side/bokorys)   → (0, H/2, 50) lookAt (0, H/2, 0)
                                       camera at +Z looks -Z, sees X×Y = W×H
           stage 3 (front/nárys)    → (50, H/2, 0) lookAt (0, H/2, 0)
                                       camera at +X looks -X, sees Z×Y = L×H
         updateProjectionMatrix not needed (no frustum change), but
         orthoControls.target must move to lookAt point or pan/zoom
         feels weird. */
      if (view === '2d' && this.orthoCamera) {
        var Hg = STATE.H;
        if (stage === 2) {
          /* Bokorys — camera at (50, H/2, 0) looks -X. Sees Z (length L)
             horizontally, Y (height H) vertically. Chip shows L + H. */
          this.orthoCamera.position.set(50, Hg / 2, 0);
          this.orthoCamera.up.set(0, 1, 0);
          this.orthoCamera.lookAt(0, Hg / 2, 0);
          if (this.orthoControls) this.orthoControls.target.set(0, Hg / 2, 0);
        } else if (stage === 3) {
          /* Nárys — camera at (0, H/2, 50) looks -Z. Sees X (width W)
             horizontally, Y (height H) vertically. Chip shows W + H. */
          this.orthoCamera.position.set(0, Hg / 2, 50);
          this.orthoCamera.up.set(0, 1, 0);
          this.orthoCamera.lookAt(0, Hg / 2, 0);
          if (this.orthoControls) this.orthoControls.target.set(0, Hg / 2, 0);
        } else {
          this.orthoCamera.position.set(0, 50, 0);
          this.orthoCamera.up.set(0, 0, -1);  /* +X right, -Z up in screen */
          this.orthoCamera.lookAt(0, 0, 0);
          if (this.orthoControls) this.orthoControls.target.set(0, 0, 0);
        }
        this.orthoCamera.updateMatrixWorld(true);
        if (this.orthoControls) this.orthoControls.update();
      }

      var STRUCTURAL = { 'frame':1, 'decking':1, 'post':1, 'rail-side':1,
                         'rail-long':1, 'rafter':1, 'batten':1, 'roof':1,
                         'brace':1, 'logs':1 };
      function isScrew(pt) { return pt && pt.indexOf('screw-') === 0; }

      /* Stage 3 "profile view" colors — muted wood-tone palette with a
         distinct color cast per profile category. Each is a wood-brown
         base hue with a sage/rust/slate tint, low saturation so the
         parts still read as timber rather than plastic toys. */
      var STAGE3_LUMBER  = 0x7a9968;  /* 2×4 KVH (frame, posts, rails, rafters, brace) → sage-tinted oak */
      var STAGE3_DECKING = 0xa66042;  /* 1×6 TST (decking boards) → terracotta cedar */
      var STAGE3_BATTEN  = 0x6f8195;  /* 1×2 KVL (battens) → weathered slate-blue timber */
      /* Default material colors — mirror values in _ensureMaterials.
         Reset to these whenever NOT in stage 3 so material colors are
         restored after leaving the profile view. */
      var DEFAULT_LUMBER  = 0xb98c5e;
      var DEFAULT_DECKING = 0x8c5d3a;
      var DEFAULT_BATTEN  = 0xd9b07e;
      var DEFAULT_ROOF    = 0xc4c8cc;

      var partOpacityFor, edgeOpacity, edgeColor, bgColor, bgAlpha;
      var hidePart;   /* function(pt) → bool : part hidden by stage rule (rendered with opacity 0 so assembly visibility stays intact) */

      if (view === '3d' && stage === 1) {
        partOpacityFor = function () { return 1.0; };
        edgeOpacity = 0.55; edgeColor = 0x4a3320;
        bgColor = 0x000000; bgAlpha = 0;
      } else if (view === '3d' && stage === 2) {
        partOpacityFor = function (pt) {
          if (STRUCTURAL[pt]) return 0.30;
          if (isScrew(pt))    return 1.0;
          return 1.0;
        };
        edgeOpacity = 0.55; edgeColor = 0x4a3320;
        bgColor = 0x000000; bgAlpha = 0;
      } else if (view === '3d' && stage === 3) {
        /* Profile view — only main lumber categories shown, color-coded.
           Roof (corrugated metal — not a lumber profile), logs (firewood,
           not structure), and all fasteners are hidden via opacity 0. */
        partOpacityFor = function () { return 1.0; };
        edgeOpacity = 0.45; edgeColor = 0x222222;
        bgColor = 0x000000; bgAlpha = 0;
        hidePart = function (pt) {
          return (pt === 'roof' || pt === 'logs' || isScrew(pt));
        };
      } else if (view === '2d' && stage === 2) {
        /* 2D stage 2 — SIDE ELEVATION (bokorys). Dimmed body fills +
           stronger edges to emphasize the outline. Body opacity comes
           from STATE.opacity2D (slider-controlled, 0..0.80). */
        var op2 = STATE.opacity2D;
        partOpacityFor = function (pt) {
          if (pt === 'roof') return op2 * 0.45;
          return op2;
        };
        edgeOpacity = 0.85; edgeColor = 0x2a1f12;
        bgColor = 0x000000; bgAlpha = 0;
      } else if (view === '2d' && stage === 3) {
        /* 2D stage 3 — FRONT ELEVATION (nárys). Same dimmed look as
           stage 2. Body opacity slider-controlled. */
        var op3 = STATE.opacity2D;
        partOpacityFor = function (pt) {
          if (pt === 'roof') return op3 * 0.45;
          return op3;
        };
        edgeOpacity = 0.85; edgeColor = 0x2a1f12;
        bgColor = 0x000000; bgAlpha = 0;
      } else if (view === '2d') {
        /* 2D stage 1 — orthographic top-down plan with L/W drag
           handles. Body opacity slider-controlled. */
        var op1 = STATE.opacity2D;
        partOpacityFor = function (pt) {
          if (pt === 'roof') return op1 * 0.45;
          return op1;
        };
        edgeOpacity = 0.85; edgeColor = 0x2a1f12;
        bgColor = 0x000000; bgAlpha = 0;
      } else {
        /* Defensive fallback — unknown view/stage combo → 3D stage 1 look. */
        partOpacityFor = function () { return 1.0; };
        edgeOpacity = 0.55; edgeColor = 0x4a3320;
        bgColor = 0x000000; bgAlpha = 0;
      }

      /* Apply colors at the shared material level. Materials are shared
         across parts (e.g. mats.lumber is used by frame, post, rail-*,
         rafter, brace), so setting the color on the material updates all
         those parts at once. Always reset to defaults FIRST so leaving
         stage 3 properly restores wood-tone palette. */
      var mats = global.RackRenderer && global.RackRenderer.materials;
      if (mats) {
        var inStage3 = (view === '3d' && stage === 3);
        if (mats.lumber)  { mats.lumber.color.setHex(inStage3 ? STAGE3_LUMBER  : DEFAULT_LUMBER);  mats.lumber.needsUpdate = true; }
        if (mats.decking) { mats.decking.color.setHex(inStage3 ? STAGE3_DECKING : DEFAULT_DECKING); mats.decking.needsUpdate = true; }
        if (mats.batten)  { mats.batten.color.setHex(inStage3 ? STAGE3_BATTEN  : DEFAULT_BATTEN);  mats.batten.needsUpdate = true; }
        if (mats.roof)    { mats.roof.color.setHex(DEFAULT_ROOF); mats.roof.needsUpdate = true; }
      }

      /* Walk rackGroup; per child, set material opacity for meshes and
         edges. Hidden parts (stage 3 fasteners/roof/logs) get opacity 0
         on BOTH meshes and edge lines — preserves assembly-step .visible
         attribute so toggling stages doesn't fight the assembly sequence. */
      rg.children.forEach(function (c) {
        var pt = c.userData && c.userData.partType;
        if (!pt || pt === 'person') return;

        var partHidden = hidePart && hidePart(pt);
        var op = partHidden ? 0 : partOpacityFor(pt);
        var edgeOp = partHidden ? 0 : edgeOpacity;

        c.traverse(function (node) {
          if (node.isMesh && node.material) {
            node.material.transparent = op < 1;
            node.material.opacity = op;
            node.material.depthWrite = (op > 0);
            node.material.needsUpdate = true;
          } else if (node.userData && node.userData.isEdgeLine && node.material) {
            node.material.opacity = edgeOp;
            node.material.color.setHex(edgeColor);
            node.material.transparent = edgeOp < 1;
            node.material.needsUpdate = true;
          }
        });
      });

      /* Person — hidden in 2D, honours STATE.bodyVisible in 3D. */
      rg.children.forEach(function (c) {
        if (c.userData && c.userData.partType === 'person') {
          c.visible = (view === '3d' && STATE.bodyVisible !== false);
        }
      });

      /* Body drag handle — hide whenever person is hidden. */
      var bh = document.getElementById('cf-body-handle');
      if (bh) {
        var personShown = (view === '3d' && STATE.bodyVisible !== false);
        bh.style.display = personShown ? '' : 'none';
      }

      /* Renderer background */
      if (this.renderer) this.renderer.setClearColor(bgColor, bgAlpha);

      /* Grid overlay — shown in BOTH 2D stages (stage 1 = plan,
         stage 2 = side elevation). Same "graph paper" look provides
         visual scale reference. */
      var grid = document.getElementById('cf-grid-overlay');
      if (grid) {
        grid.classList.toggle('cf-grid-active', view === '2d');
      }

      /* Mapbox layer — visible in all stages (same behaviour as stage 1). */
      var mapbox = document.getElementById('cf-mapbox');
      if (mapbox) {
        mapbox.style.display = '';
      }

      /* 2D fill-opacity slider — shown in all 2D stages, hidden in 3D. */
      var op2dWrap = document.getElementById('cf-2d-opacity-slider-wrap');
      if (op2dWrap) {
        op2dWrap.classList.toggle('cf-2d-visible', view === '2d');
        /* One-shot diagnostic on first applyVizStage in 2D. */
        if (view === '2d' && !this._op2dWrapLogged) {
          this._op2dWrapLogged = true;
          var DLop = global.Configurator && global.Configurator.log;
          if (DLop) {
            var cs = window.getComputedStyle(op2dWrap);
            DLop.log('[opacity-slider] wrap found, view=' + view +
                     ' classList=' + op2dWrap.className +
                     ' display=' + cs.display +
                     ' right=' + cs.right + ' top=' + cs.top);
          }
        }
      } else {
        var DLop2 = global.Configurator && global.Configurator.log;
        if (DLop2 && !this._op2dWrapMissingLogged) {
          this._op2dWrapMissingLogged = true;
          DLop2.log('[opacity-slider] ELEMENT NOT FOUND — snippet markup not deployed?');
        }
      }

      /* Asset raycaster — active ONLY in 3D stage 1 (full build view).
         Toggling here removes hover/select highlight and closes the
         drawer when the user moves to inspection or profile stages.
         Drag handles coexist on stage 1; they catch their own pointer
         events with stopPropagation, so the raycaster never sees them. */
      if (global.RackRaycaster && global.RackRaycaster.setActive) {
        global.RackRaycaster.setActive(view === '3d' && stage === 1);
      }

      /* Drag handles — visibility depends on view+stage+assembly. */
      if (typeof _refreshHandlesVisibility === 'function') _refreshHandlesVisibility();

      /* Dimension overlay (SVG) */
      if (global.RackDimensions && global.RackDimensions.refresh) {
        global.RackDimensions.refresh();
      }
    }
  };

  /* ----------------------------------------------------------
     RENDERER  (rebuilds rack mesh from STATE)
     ---------------------------------------------------------- */
  global.RackRenderer = {
    materials: null,

    _ensureMaterials: function () {
      if (this.materials) return;
      var T = global.THREE;
      this.materials = {
        /* 2×4 lumber (KVH10060) — posts, bottom frame, top rails, rafters: warm tan */
        lumber:  new T.MeshStandardMaterial({ color: 0xb98c5e, roughness: 0.85 }),
        /* 1×6 decking (TST00001) — floor boards: darker reddish-brown */
        decking: new T.MeshStandardMaterial({ color: 0x8c5d3a, roughness: 0.95 }),
        /* 1×2 battens — cross battens under roof: lighter golden */
        batten:  new T.MeshStandardMaterial({ color: 0xd9b07e, roughness: 0.80 }),
        roof:    new T.MeshStandardMaterial({ color: 0xc4c8cc, roughness: 0.45, metalness: 0.85 }),
        /* Screw fasteners */
        screwHead:    new T.MeshStandardMaterial({ color: 0x333333, roughness: 0.4, metalness: 0.85 }),
        screwCap:     new T.MeshStandardMaterial({ color: 0x222222, roughness: 0.7, metalness: 0.05 }),
        /* Edge line material — slightly darker than wood */
        edge:    new T.LineBasicMaterial({ color: 0x4a3320, linewidth: 1, transparent: true, opacity: 0.55 })
      };
    },

    /* Attach edge-highlight lines as a child of a mesh (auto-inherits its transform). */
    _addEdges: function (mesh, thresholdDeg) {
      var T = global.THREE;
      try {
        var edgesGeom = new T.EdgesGeometry(mesh.geometry, thresholdDeg || 25);
        var edges = new T.LineSegments(edgesGeom, this.materials.edge);
        edges.userData.isEdgeLine = true;
        mesh.add(edges);
      } catch (e) { /* ignore */ }
      return mesh;
    },

    /* Wood screw 6 × 60 mm — pan head + tapered shaft.
       Built as a Group, axis along Y by default (head at +Y, tip at -Y).
       Use opts.rotZ for rotation around Z (e.g., screw entering along X).
       (x,y,z) = position of the HEAD center. */
    _mkScrew: function (x, y, z, opts) {
      var T = global.THREE;
      opts = opts || {};
      var headR = opts.headRadius || 0.0050;     /* 10mm dia */
      var headH = opts.headHeight || 0.0025;     /* 2.5mm tall */
      var shaftR = opts.shaftRadius || 0.0026;   /* 5mm dia */
      var shaftL = opts.shaftLen   || 0.040;     /* 40mm visible shaft */
      var tipR   = shaftR * 0.15;

      var g = new T.Group();

      /* HEAD — pan head: short cylinder, slight taper for visual */
      var headGeo = new T.CylinderGeometry(headR, headR * 0.95, headH, 16);
      var head = new T.Mesh(headGeo, this.materials.screwHead);
      head.position.y = 0;
      g.add(head);

      /* SHAFT — long cylinder tapering to tip */
      var shaftGeo = new T.CylinderGeometry(shaftR, tipR, shaftL, 10);
      var shaft = new T.Mesh(shaftGeo, this.materials.screwHead);
      shaft.position.y = -(headH/2 + shaftL/2);
      g.add(shaft);

      g.position.set(x, y, z);
      if (opts.rotX) g.rotation.x = opts.rotX;
      if (opts.rotZ) g.rotation.z = opts.rotZ;
      if (opts.rotY) g.rotation.y = opts.rotY;
      return g;
    },

    /* Corrugated-roof screw — heavy-duty with EPDM cap, hex head, pointed shaft.
       Axis along Y (cap up, shaft down). Use tilt = rotation.z to match roof slope.
       (x,y,z) = position of the CAP TOP center. */
    _mkRoofScrew: function (x, y, z, tilt) {
      var T = global.THREE;
      var capR  = 0.009;     /* 18mm dia rubber EPDM cap */
      var capH  = 0.004;     /* 4mm tall */
      var hexR  = 0.005;     /* 10mm hex head */
      var hexH  = 0.0045;    /* 4.5mm tall */
      var shaftR = 0.0030;   /* 6mm dia */
      var shaftL = 0.025;    /* 25mm visible (rest is in the wood) */
      var tipR   = shaftR * 0.2;

      var g = new T.Group();

      /* EPDM cap: domed top (low metalness, black rubber look) */
      var capGeo = new T.CylinderGeometry(capR * 0.85, capR, capH, 14);
      var cap = new T.Mesh(capGeo, this.materials.screwCap);
      cap.position.y = 0;
      g.add(cap);

      /* HEX head: 6-sided cylinder approximation */
      var hexGeo = new T.CylinderGeometry(hexR, hexR, hexH, 6);
      var hex = new T.Mesh(hexGeo, this.materials.screwHead);
      hex.position.y = -(capH/2 + hexH/2);
      g.add(hex);

      /* Shaft with pointed tip */
      var shaftGeo = new T.CylinderGeometry(shaftR, tipR, shaftL, 10);
      var shaft = new T.Mesh(shaftGeo, this.materials.screwHead);
      shaft.position.y = -(capH/2 + hexH + shaftL/2);
      g.add(shaft);

      g.position.set(x, y, z);
      if (tilt) g.rotation.z = tilt;
      return g;
    },

    _mkBox: function (w, h, d, mat, x, y, z) {
      var T = global.THREE;
      var m = new T.Mesh(new T.BoxGeometry(w, h, d), mat);
      m.position.set(x, y, z);
      return m;
    },

    /* Tag a mesh with a part type for assembly-step visibility & explode logic. */
    _tag: function (mesh, partType) {
      mesh.userData.partType = partType;
      /* Save initial position for explode/restore */
      mesh.userData.basePos = mesh.position.clone();
      return mesh;
    },

    build: function () {
      var eng = global.RackEngine;
      if (!eng || !eng.rackGroup) return;
      this._ensureMaterials();

      /* Clear previous geometry AND instance registry — every rebuild
         starts from scratch so IDs match the meshes we just added. */
      while (eng.rackGroup.children.length) {
        var c = eng.rackGroup.children.pop();
        if (c.geometry) c.geometry.dispose();
      }
      if (global.RackInstances && global.RackInstances.clear) {
        global.RackInstances.clear();
      }
      /* If a selection was active, drop it — the mesh is gone now. */
      if (global.RackHighlight && global.RackHighlight._reset) {
        global.RackHighlight._reset();
      }
      if (global.ConfiguratorAssetDrawer && global.ConfiguratorAssetDrawer.isOpen &&
          global.ConfiguratorAssetDrawer.isOpen()) {
        global.ConfiguratorAssetDrawer.close();
      }

      var L = STATE.L, W = STATE.W, H = STATE.H;
      var tw = DIMS.twoByFour.w, th = DIMS.twoByFour.h;
      var halfW = W / 2, halfL = L / 2;
      var ML = this.materials.lumber, MD = this.materials.decking,
          MBT = this.materials.batten,  MR = this.materials.roof;
      var mk = this._mkBox.bind(this);
      var G = eng.rackGroup;
      var self = this;
      /* Tagged-add helper: tags the mesh, attaches edge highlights for lumber, adds to G. */
      function addT(mesh, partType) {
        self._tag(mesh, partType);
        /* Add edge highlights for wooden parts (skip screws, roof has its own look) */
        if (partType === 'frame' || partType === 'decking' || partType === 'post' ||
            partType === 'rail-long' || partType === 'rail-side' || partType === 'brace' ||
            partType === 'rafter' || partType === 'batten') {
          self._addEdges(mesh);
        }
        G.add(mesh);
        return mesh;
      }

      /* Instance tracker — registers a mesh with RackInstances if its
         partType maps to a selectable material. Fastener meshes (screws)
         are intentionally excluded: they're calculated from joint counts,
         not individually pickable.

         The stored instance is INTENTIONALLY minimal — name, category
         and unit are NOT stored here. They are looked up at drawer-open
         time by ConfiguratorBudgetBridge.getBoqItem(boqCode), which
         pulls the localized values from the central BoQ Google Sheet.
         This avoids two problems:
           1. Hardcoded English text that needs translating per config
           2. Stale data if central BoQ updates after build

         What we DO store:
           id        — stable string for the lifetime of this build
                       (rebuilt on every STATE change)
           partType  — internal tag ('post', 'decking', ...) used by
                       the renderer for opacity/highlight rules
           boqCode   — links to central BoQ (and user_prices)
           length    — this PIECE's contribution to the project total
                       (e.g. one decking board = 0.85 m); useful for
                       per-instance dimension display in the drawer
           position  — world-space snapshot for the drawer
           mesh      — for raycaster reverse-lookup

         Returns the mesh unchanged so it can be inlined:
           `trackI(addT(mesh, 'post'), 'post', { idHint: 'high-front', length: H })` */
      function trackI(mesh, partType, opts) {
        if (!mesh || !global.RackInstances) return mesh;
        opts = opts || {};
        var boqCode = PART_MATERIAL[partType];
        if (!boqCode) return mesh;
        var mat = MATERIALS[boqCode];
        if (!mat || mat.selectable === false) return mesh;
        var id = global.RackInstances.nextId(partType, opts.idHint);
        global.RackInstances.add({
          id:       id,
          partType: partType,
          boqCode:  boqCode,
          length:   (typeof opts.length === 'number') ? opts.length : null,
          /* dimensions: optional { length, width } pair in metres for
             pieces whose physical extent is a 2D shape rather than a
             linear cut. Asset drawer renders these as "L × W m" in
             place of the single length cell. Used for roof, decking
             boards, anything plate-like. Linear lumber pieces (posts,
             rafters, rails) leave this null and only fill `length`. */
          dimensions: opts.dimensions || null,
          /* end-cut angle in degrees (off-square). Linear pieces with a
             mitered/chamfered end (rafters) set this so the cut list can
             draw the slanted end + label. Square cuts leave it 0. */
          angle:    (typeof opts.angle === 'number') ? opts.angle : 0,
          position: { x: mesh.position.x, y: mesh.position.y, z: mesh.position.z },
          mesh:     mesh
        });
        return mesh;
      }

      /* Roof slope along the SHORT side (W = X axis).
         FRONT = +X = HIGH side (raised front length rail).
         BACK  = -X = LOW side (flush back length rail).
         Rafters (TODO) will span across X from front raised rail down to back rail. */
      var roofTiltDeg = 12;
      var roofTiltRad = roofTiltDeg * Math.PI / 180;

      /* All 4 corner posts (2×4 = 50×100). Wide face (100mm) along Z direction.
         Posts share OUTER X edge with frame (flush along X).
         Posts STICK OUT in Z beyond the frame — their outer Z face = ±halfL.
         HIGH SIDE = +X side → posts are TALLER (Hfront = H + th).
         LOW SIDE  = -X side → posts are at H (Hback). */
      var Hfront = H + th;
      var Hback  = H;
      [-halfL + th/2, halfL - th/2].forEach(function (z) {
        var zSide = (z < 0) ? 'back' : 'front';
        var pHigh = addT(mk(tw, Hfront, th, ML,  halfW - tw/2, Hfront/2, z), 'post');
        pHigh.userData.explodeOffset = [ 0.35, 0, 0];   /* +X side moves out +X */
        trackI(pHigh, 'post', { idHint: 'high-' + zSide, length: Hfront });
        var pLow  = addT(mk(tw, Hback,  th, ML, -halfW + tw/2, Hback/2,  z), 'post');
        pLow.userData.explodeOffset  = [-0.35, 0, 0];   /* -X side moves out -X */
        trackI(pLow, 'post', { idHint: 'low-' + zSide, length: Hback });
      });

      /* MIDDLE POSTS — added once rack length exceeds MID_POST_THRESHOLD.
         A third pair of posts sits at z=0 on both the high (+X) and low (-X)
         sides. Same geometry, materials and explode behaviour as the corner
         posts. They sit outside the decking (same X position) and pass under
         the long top rails which extend the full L. */
      if (L > MID_POST_THRESHOLD) {
        var pHighMid = addT(mk(tw, Hfront, th, ML,  halfW - tw/2, Hfront/2, 0), 'post');
        pHighMid.userData.explodeOffset = [ 0.35, 0, 0];
        trackI(pHighMid, 'post', { idHint: 'high-mid', length: Hfront });
        var pLowMid  = addT(mk(tw, Hback,  th, ML, -halfW + tw/2, Hback/2,  0), 'post');
        pLowMid.userData.explodeOffset  = [-0.35, 0, 0];
        trackI(pLowMid, 'post', { idHint: 'low-mid', length: Hback });
      }

      /* BOTTOM FRAME — closed rectangle, all 2×4 on edge (upright).
         Cross-section: tw=50mm thickness × th=100mm vertical height.
         Top edge at deckHeight. Center Y = deckHeight - th/2.

         Posts are at corners with outer X = ±halfW, outer Z = ±halfL.
         Frame fits BETWEEN posts in X (inset by tw on each side) and EXTENDS to outer post Z edges. */

      /* Length rails (along Z) — inside the post X-footprint, fitting between front and back posts.
         X center: just inside post inner X face by tw/2 → ±(halfW - tw - tw/2) = ±(halfW - 1.5*tw)
         Z length: extends to outer post Z faces = full L. */
      addT(mk(tw, th, L, ML,  halfW - 1.5*tw, DIMS.deckHeight - th/2, 0), 'frame');
      addT(mk(tw, th, L, ML, -halfW + 1.5*tw, DIMS.deckHeight - th/2, 0), 'frame');
      /* Instance tracking for the two length rails (post-creation lookup). */
      (function () {
        var children = eng.rackGroup.children;
        var lenRail2 = children[children.length - 1];
        var lenRail1 = children[children.length - 2];
        trackI(lenRail1, 'frame', { idHint: 'length-pos-x', length: L });
        trackI(lenRail2, 'frame', { idHint: 'length-neg-x', length: L });
      })();

      /* Width rails (along X) — at ends, outer Z face flush with ±halfL.
         Z center: ±(halfL - tw/2).
         X length: between length-rail inner X faces = W - 4*tw. */
      [-(halfL - tw/2), halfL - tw/2].forEach(function (z) {
        var widthRail = addT(mk(W - 4*tw, th, tw, ML, 0, DIMS.deckHeight - th/2, z), 'frame');
        var zSide = (z < 0) ? 'back' : 'front';
        trackI(widthRail, 'frame', { idHint: 'width-' + zSide, length: W - 4*tw });
      });

      /* TOP RAILS — attached to OUTER X faces of posts. Upright (2×4 on edge).
         Cross-section: tw depth (X, sticking outward) × th height (Y).
         Length L along Z (full, from outer Z-edge of one post to outer Z-edge of other).
         BUT POST OUTER X = ±halfW (flush with frame outer X), and rail X center = ±halfW + tw/2.
         So rail OUTER X face = ±halfW + tw → rail sticks OUTWARD by tw beyond rack footprint.
         If we want rail outer X flush with rack outer X (=±halfW), rail center should be at
         ±(halfW - tw/2), which is INSIDE the post footprint — overlapping with post.
         User said posts and frame share OUTER X edge, and rails go on OUTER side of posts.
         So accept that rails extend slightly beyond W. */
      addT(mk(tw, th, L, ML,  halfW + tw/2, Hfront - th/2, 0), 'rail-long');
      addT(mk(tw, th, L, ML, -halfW - tw/2, Hback  - th/2, 0), 'rail-long');
      (function () {
        var children = eng.rackGroup.children;
        var rL2 = children[children.length - 1];
        var rL1 = children[children.length - 2];
        trackI(rL1, 'rail-long', { idHint: 'front-pos-x', length: L });
        trackI(rL2, 'rail-long', { idHint: 'back-neg-x',  length: L });
      })();

      /* SIDE TOP RAILS — at +Z / -Z ends, between posts, along X axis.
         Same height as BACK top rail (top edge at Hback = H).
         Length X = W - 2*tw (between post inner X faces).
         Cross-section: th=100mm height (Y), tw=50mm depth (Z), oriented upright.
         Z position: outer Z face flush with rack outer Z edge (= ±halfL).
         Rail Z center = ±(halfL - tw/2). */
      [-(halfL - tw/2), halfL - tw/2].forEach(function (z) {
        var sideRail = addT(mk(W - 2*tw, th, tw, ML, 0, Hback - th/2, z), 'rail-side');
        var zSide = (z < 0) ? 'back' : 'front';
        trackI(sideRail, 'rail-side', { idHint: zSide, length: W - 2*tw });
      });

      /* DIAGONAL BRACE (vzpěra) — single 2×4 on the back (-X) side, diagonally bracing the structure.
         Sits OUTSIDE (-X) of the back posts: brace X range = [-halfW - tw, -halfW].
         Outer corners (the corners closest to ±halfL) overhang the post outer Z faces by 2cm.
         Top: Y = Hback - 2*th (below back rail with clearance).
         Bot: Y = deckHeight. */
      (function () {
        var T = global.THREE;
        var topY = Hback - 2*th;
        var botY = DIMS.deckHeight;
        var overhang = DIMS.braceOver;   /* 2cm (3/4 in) past +-halfL */
        /* Iteratively find centerline endpoint Z so that outer corner reaches ±halfL ± overhang */
        var topZ = -halfL - 0.05, botZ = halfL + 0.05;
        var halfTh = th / 2;
        for (var iter = 0; iter < 5; iter++) {
          var dy = topY - botY;
          var dz = topZ - botZ;
          var len = Math.sqrt(dy*dy + dz*dz);
          var uy = dy / len, uz = dz / len;
          var py = -uz, pz = uy;
          /* Outer corner at top end: topZ - pz*halfTh (more negative, toward -halfL).
             Want outer corner Z = -halfL - overhang */
          var outerTopZ = topZ - pz * halfTh;
          var err = outerTopZ - (-halfL - overhang);
          topZ -= err;
          botZ = -topZ;  /* symmetric */
        }
        var dy = topY - botY;
        var dz = topZ - botZ;
        var len = Math.sqrt(dy*dy + dz*dz);
        var uy = dy / len, uz = dz / len;
        var py = -uz, pz = uy;
        var c1 = [topY + py * halfTh, topZ + pz * halfTh];
        var c2 = [topY - py * halfTh, topZ - pz * halfTh];
        var c3 = [botY - py * halfTh, botZ - pz * halfTh];
        var c4 = [botY + py * halfTh, botZ + pz * halfTh];
        var shape = new T.Shape();
        shape.moveTo(c1[1], c1[0]);
        shape.lineTo(c2[1], c2[0]);
        shape.lineTo(c3[1], c3[0]);
        shape.lineTo(c4[1], c4[0]);
        shape.closePath();
        var braceGeom = new T.ExtrudeGeometry(shape, { depth: tw, bevelEnabled: false });
        braceGeom.rotateY(-Math.PI / 2);
        var brace = new T.Mesh(braceGeom, ML);
        brace.position.x = -halfW;   /* brace X range matches post X range = [-halfW, -halfW + tw] */
        var b = addT(brace, 'brace');
        b.userData.explodeOffset = [-0.40, 0, 0];
        /* Brace centerline length — final values of dy/dz after the iterative
           solve above are in scope here. */
        var braceLen = Math.sqrt(dy*dy + dz*dz);
        trackI(b, 'brace', { idHint: 'diag-back', length: braceLen });
      })();

      /* Decking boards (TST00001) — boards span the FRAME WIDTH (along X, between length rails),
         spaced along Z (length) over the FULL L including behind posts.
         Layout rules:
           - Outer edges of FIRST and LAST board sit at ±halfL (rack outer Z edges).
           - Board length = W - 2*tw (frame outer X span, between length rails outer X faces).
           - L is quantized to fit N whole boards + (N-1) gaps exactly. */
      var dThick = DIMS.decking.w;        /* 25mm thick */
      var dWide  = DIMS.decking.h;        /* 152mm wide */
      var dGap   = DIMS.deckGap;          /* 5mm (3/16 in) nominal gap */
      var deckLen = W - 2*tw;             /* frame width */
      var N = _deckCount(L);
      var stride = (N > 1) ? (L - dWide) / (N - 1) : 0;
      var firstZ = -halfL + dWide / 2;
      for (var i = 0; i < N; i++) {
        var z = firstZ + i * stride;
        var board = addT(mk(deckLen, dThick, dWide, MD,
          0, DIMS.deckHeight + dThick/2 + 0.0005, z), 'decking');
        trackI(board, 'decking', {
          idHint: String(i + 1).padStart(2, '0'),
          length: deckLen
        });
      }

      /* RAFTERS (krokve) — 2×4 ON EDGE (upright), sit ON TOP of front and back top rails.
         Cross-section: th=100mm height (Y), tw=50mm depth (Z).
         Length along X: spans both top rails + extra extension forward (+X) with chamfered front end.
         Tilted to match roof slope.
         Layout: edge rafters flush with rack outer Z (±halfL), others evenly spaced.
         Count scales with L using typical gap ≈ 0.38m → 5 rafters at default L=1.565m. */
      var rafterTypicalGap = DIMS.rafterGap;
      var rafterCount = Math.max(2, Math.round((L - tw) / rafterTypicalGap) + 1);
      var rafterTopY = (Hfront + Hback) / 2 + th / 2;
      var rafterBaseLen = W + 2*tw;         /* span both top rails */
      var rafterExtension = DIMS.rafterExt;      /* 15cm (7 in) protrusion past front rail */
      var rafterChamfer = DIMS.rafterChamfer;    /* chamfer length along extension */
      var rafterTilt = Math.atan((Hfront - Hback) / (W + 2*tw));
      /* Chamfered front end: the cut runs from the bottom edge (chamfer
         back) up to the front-top corner over the full section height th.
         Angle off the square end face = atan(run / rise). Reported on the
         cut list as "N° cut" and drawn as a slanted bar end. */
      var rafterCutAngle = Math.round(Math.atan(rafterChamfer / th) * 180 / Math.PI);
      var rafterFirstZ = -halfL + tw/2;
      var rafterSpan   = L - tw;

      /* Build a SHARED chamfered-rafter shape (2D outline in XY), extruded along Z by tw */
      var T = global.THREE;
      var shape = new T.Shape();
      var totalLen = rafterBaseLen + rafterExtension;
      /* Vertices counter-clockwise in XY (start at back-bottom): */
      shape.moveTo(-rafterBaseLen/2, 0);                              /* back-bottom */
      shape.lineTo( rafterBaseLen/2 + rafterExtension - rafterChamfer, 0);  /* front-bottom (chamfer start) */
      shape.lineTo( rafterBaseLen/2 + rafterExtension, th);           /* front-top (chamfer end, higher) */
      shape.lineTo(-rafterBaseLen/2, th);                             /* back-top */
      shape.closePath();
      var rafterGeom = new T.ExtrudeGeometry(shape, { depth: tw, bevelEnabled: false });
      rafterGeom.translate(0, 0, -tw/2);   /* center along Z */

      for (var r = 0; r < rafterCount; r++) {
        var rz = (rafterCount === 1)
          ? 0
          : rafterFirstZ + rafterSpan * r / (rafterCount - 1);
        var rafter = new T.Mesh(rafterGeom, ML);
        rafter.position.set(0, rafterTopY - th/2, rz);   /* shape Y goes 0..th, so center is th/2 above 0 */
        rafter.rotation.z = rafterTilt;
        addT(rafter, 'rafter');
        trackI(rafter, 'rafter', {
          idHint: String(r + 1).padStart(2, '0'),
          length: rafterBaseLen + rafterExtension,
          angle:  rafterCutAngle
        });
      }

      /* BATTENS (latě) — 1×2 inch profile (25 × 50 mm), laid flat on top of rafters,
         perpendicular to rafters (running along Z), supporting the roof.
         Cross-section: 50mm wide (X), 25mm thin (Y, lying flat).
         Length along Z = L (full rack length).
         Tilted same as rafters.
         Count scales with W: 3 at default W=0.9, more for wider racks. */
      var battenW = DIMS.oneByTwo.h;       /* 50mm horizontal width (along X) */
      var battenH = DIMS.oneByTwo.w;       /* 25mm vertical (Y) — laid flat */
      var battenInset = DIMS.battenInset;   /* 50 mm (2 in) inset from rafter outer X edges */
      var battenFirstX = -rafterBaseLen/2 + battenInset + battenW/2;
      var battenLastX  =  rafterBaseLen/2 + rafterExtension - battenInset - battenW/2;
      var battenUsable = battenLastX - battenFirstX;
      var battenTypicalGap = DIMS.battenGap;
      var battenCount = Math.max(2, Math.round(battenUsable / battenTypicalGap) + 1);
      for (var b = 0; b < battenCount; b++) {
        var bx = (battenCount === 1)
          ? rafterExtension/2
          : battenFirstX + battenUsable * b / (battenCount - 1);
        var bY = rafterTopY + th/2 + battenH/2 + 0.003 + bx * Math.sin(rafterTilt);
        var batten = mk(battenW, battenH, L, MBT, bx, bY, 0);
        batten.rotation.z = rafterTilt;
        addT(batten, 'batten');
        trackI(batten, 'batten', {
          idHint: String(b + 1).padStart(2, '0'),
          length: L
        });
      }

      /* Corrugated roofing panel (ROR12004) — sits ON TOP of rafters.
         Y center raised by th (rafter height) above the previous "on rails" position.
         Roof extends further on +X side to cover the protruding rafter ends. */
      var overhangX = DIMS.roofOverX;
      var overhangZ = DIMS.roofOverZ;
      var roofXSpan = W + 2*tw + rafterExtension + 2*overhangX;
      var roofL = L + 2*overhangZ;
      /* Roof centered on the extended rafter X-range: midpoint shifted by +rafterExtension/2 */
      /* Roof midline Y so its lowest corrugation point (valley) is
         tangent to batten top surface. Roof rotates around its own
         centre (rafterExtension/2) but batten rotates around bx, so
         we add (rafterExtension/2)*sin(tilt) to compensate the pivot
         offset. corrugAmp=0.015, battenH=0.025, +1mm gap. */
      var roofY = rafterTopY + th/2 + battenH + 0.003 + 0.015
                + (rafterExtension / 2) * Math.sin(rafterTilt) + 0.001;
      var roof = this._buildCorrugatedRoof(roofXSpan, roofL, MR);
      roof.position.set(rafterExtension/2, roofY, 0);
      roof.rotation.z = rafterTilt;
      addT(roof, 'roof');
      /* the drawings (RackDoc) draw the sheet from its numbers, not from
         its thousands of corrugation triangles */
      roof.userData.roofSpec = { w: roofXSpan, len: roofL, amp: 0.015, period: 0.076 };
      /* Roof tracked as a single instance — per Marek's decision the
         roof is one rectangular surface, not panel-segmented. The
         BoQ stores it as m² (area = roofXSpan × roofZSpan) so the
         project quantity / unit price math stays right. The asset
         drawer reads `dimensions` to display "L × W m" alongside
         the area, so the user sees both:
            PROJECT QUANTITY: 2.89 m²   (for BoQ)
            DIMENSIONS:       1.84 × 1.57 m  (for ordering / cutting) */
      trackI(roof, 'roof', {
        idHint:     'panel',
        length:     roofXSpan * roofL,
        dimensions: { length: roofL, width: roofXSpan }
      });

      /* ─── FASTENERS / SCREWS ───────────────────────────────────────────────
         Per-joint fasteners tagged by sub-type so the assembly animation can
         add them in stages:
           screw-frame, screw-decking, screw-post-bottom, screw-rail-side,
           screw-rail-long, screw-rafter, screw-batten, screw-roof
       */

      /* ── FRAME CORNER SCREWS — 2 horizontal screws per corner, driven
         from the SIDE through the length rail into the end-grain of the
         width rail. Heavy-duty 8×100mm wood screw (FAS80100).
         Geometry:
           • Length rails sit at X = ±(halfW - 1.5*tw), tw thick. Their
             OUTER face is at ±(halfW - tw).
           • Width rails butt in between them at Z = ±(halfL - tw/2).
           • Screw enters through length-rail outer face, passes 60mm
             length-rail, ~40mm into width-rail end.
         Screw head should licovat (be flush) with outer face of length
         rail, slightly proud (2mm protrusion).
         Native screw axis = Y (head up, shaft down). To get head on +X
         side with shaft going -X, we rotate by ROT_Z = -π/2 (NOT +π/2 —
         that flipped it the wrong way). Mirrored for -X side. */
      var th       = 0.100;                          /* frame vertical height */
      var frameTop = DIMS.deckHeight;                /* y = 0.30 */
      var frameBot = DIMS.deckHeight - th;           /* y = 0.20 */
      /* Two screws stacked vertically, 1/3 + 2/3 of height. */
      var screwYHi  = frameBot + th * 2 / 3;
      var screwYLo  = frameBot + th * 1 / 3;
      var headProud = 0.002;                          /* head sticks out 2mm */
      var xOuterPos = halfW - tw + headProud;         /* head sits just outside +X length rail */
      var xOuterNeg = -(halfW - tw + headProud);      /* head sits just outside -X length rail */
      var FRAME_S = [
        /* [xHead, zCorner, rotZ] — head at xHead; rotZ orients shaft inward */
        [ xOuterPos,  halfL - tw / 2, -Math.PI / 2 ],
        [ xOuterNeg,  halfL - tw / 2,  Math.PI / 2 ],
        [ xOuterPos, -halfL + tw / 2, -Math.PI / 2 ],
        [ xOuterNeg, -halfL + tw / 2,  Math.PI / 2 ]
      ];
      FRAME_S.forEach(function (c) {
        var x = c[0], z = c[1], rz = c[2];
        [screwYHi, screwYLo].forEach(function (y) {
          var s = addT(self._mkScrew(x, y, z, {
            rotZ: rz,
            headRadius: 0.0065,    /* 13mm head — heavier 8mm screw */
            shaftRadius: 0.0040,   /* 8mm shaft */
            shaftLen:    0.080     /* 100mm screw, ~80mm visible after head */
          }), 'screw-frame');
          s.userData.explodeOffset = [(x > 0 ? 0.35 : -0.35), 0, 0];
        });
      });

      /* ── DECKING SCREWS — 4 per board: 2 at each rail. The 2 screws
         on each rail are spread along the BOARD WIDTH (Z axis) at the
         1/4 and 3/4 points of the board, so each screw is 1/4 from the
         board edge and they're separated by 1/2 board width.
         Each screw FAS60080 (6×80mm wood screw): 25mm decking + 55mm
         into 60mm length rail. Heads visible above deck surface. */
      (function () {
        var nailRail = halfW - 1.5 * tw;             /* length-rail X centers */
        var dThick = DIMS.decking.w;                 /* 0.025 — board thickness */
        var dWide  = DIMS.decking.h;                 /* 0.152 — board width along Z */
        var dGap   = DIMS.deckGap;
        var Nboards = _deckCount(L);
        var firstZ = -halfL + dWide / 2;
        var stride = (Nboards > 1) ? (L - dWide) / (Nboards - 1) : 0;

        /* Z offsets from board CENTER: ±dWide/4 → 1/4 and 3/4 of width. */
        var zOff = dWide / 4;
        /* Head sits 5mm proud of deck surface so it's clearly visible. */
        var headY = DIMS.deckHeight + dThick + 0.005;

        for (var i = 0; i < Nboards; i++) {
          var zCenter = firstZ + i * stride;
          [ nailRail, -nailRail ].forEach(function (railX) {
            [ -zOff, +zOff ].forEach(function (off) {
              var sz = zCenter + off;
              var n = addT(self._mkScrew(railX, headY, sz, {
                headRadius:  0.0050,    /* 10mm head */
                headHeight:  0.0022,
                shaftRadius: 0.0030,    /* 6mm shaft */
                shaftLen:    0.070      /* 80mm screw, ~70mm shaft */
              }), 'screw-decking');
              n.userData.explodeOffset = [0, 0.35, 0];
            });
          });
        }
      })();

      /* ── SIDE RAIL SCREWS — 2 per joint, driven from OUTSIDE post X face
         horizontally through the post into the END-GRAIN of the side rail.
         Same pattern as frame corners. 2 rails × 2 ends × 2 screws = 8.
         Material FAS80100 (8×100mm). Heads slightly proud of post outer
         face. Two screws stacked vertically at rail mid-height.
         Side rail dims:
           • runs along X, length = W - 2*tw
           • Z centre = ±(halfL - tw/2)
           • Y centre = Hback - th/2 (top area of rack)
           • End-grain at X = ±(halfW - tw)
         Screw enters from X = ±halfW + headProud (post outer face). */
      (function () {
        var sideRailY = Hback - th / 2;             /* rail vertical centre */
        var stackY    = th * 0.25;                  /* vertical stack offset for the 2 screws */
        var sideRailYHi = sideRailY + stackY;
        var sideRailYLo = sideRailY - stackY;
        var headProudS  = 0.006;
        var xHeadPos    = halfW + headProudS;
        var xHeadNeg    = -(halfW + headProudS);
        /* Post Z centre = ±(halfL - th/2). Screw Z must be aligned with
           the POST centre (not the rail centre) so the shaft passes
           through solid post material straight into rail end-grain. */
        var postZpos = halfL - th / 2;
        var postZneg = -(halfL - th / 2);
        [ postZneg, postZpos ].forEach(function (rz) {
          /* +X end joint */
          [sideRailYHi, sideRailYLo].forEach(function (y) {
            var s = addT(self._mkScrew(xHeadPos, y, rz, {
              rotZ: -Math.PI / 2,
              headRadius:  0.0065,
              headHeight:  0.0025,
              shaftRadius: 0.0040,
              shaftLen:    0.095    /* 100mm screw — full visible shaft */
            }), 'screw-rail-side');
            s.userData.explodeOffset = [0.35, 0, 0];
          });
          /* -X end joint */
          [sideRailYHi, sideRailYLo].forEach(function (y) {
            var s = addT(self._mkScrew(xHeadNeg, y, rz, {
              rotZ: Math.PI / 2,
              headRadius:  0.0065,
              headHeight:  0.0025,
              shaftRadius: 0.0040,
              shaftLen:    0.095
            }), 'screw-rail-side');
            s.userData.explodeOffset = [-0.35, 0, 0];
          });
        });
      })();

      /* ── RAFTER SCREWS — 2 per rafter, sitting on the X axis (centre)
         of the front and back long rails, and on the Z axis (centre)
         of each rafter. Long rail centres are at X = ±(halfW + tw/2).
         Material: FAS60160 (6×160mm). Visible shaft drawn full length
         so the screw doesn't look stubby; head proud above rafter top. */
      (function () {
        var n = rafterCount;
        var firstZ = rafterFirstZ;
        var span = rafterSpan;
        var railFrontX = halfW + tw / 2;        /* front long rail centre X */
        var railBackX  = -(halfW + tw / 2);     /* back long rail centre X */
        for (var r = 0; r < n; r++) {
          var rz = (n === 1) ? 0 : firstZ + span * r / (n - 1);
          [ railBackX, railFrontX ].forEach(function (rx) {
            /* Y on tilted rafter top surface at this X. */
            var ry = rafterTopY + th / 2 + 0.008
                   + rx * Math.sin(rafterTilt);
            var s = addT(self._mkScrew(rx, ry, rz, {
              rotZ:        rafterTilt,
              headRadius:  0.0050,    /* 10mm head */
              headHeight:  0.0022,
              shaftRadius: 0.0030,    /* 6mm shaft */
              shaftLen:    0.150      /* 160mm screw — full visible shaft */
            }), 'screw-rafter');
            s.userData.explodeOffset = [0, 0.40, 0];
          });
        }
      })();

      /* ── BATTEN NAILS — small nails at each batten × rafter intersection.
         Batten X positions: battenFirstX..battenLastX (3+ battens).
         Rafter Z positions: rafterFirstZ + i*span/N. */
      (function () {
        var nRaft = rafterCount;
        var firstZ = rafterFirstZ;
        var span = rafterSpan;
        for (var b3 = 0; b3 < battenCount; b3++) {
          var bx = (battenCount === 1)
            ? rafterExtension/2
            : battenFirstX + battenUsable * b3 / (battenCount - 1);
          for (var r = 0; r < nRaft; r++) {
            var rz = (nRaft === 1) ? 0 : firstZ + span * r / (nRaft - 1);
            var by = rafterTopY + th/2 + 0.003 + battenH + 0.004 + bx * Math.sin(rafterTilt);
            var n = addT(self._mkScrew(bx, by, rz, {
              rotZ: rafterTilt,
              headRadius:  0.0045,    /* ~9mm head — 5mm shaft */
              headHeight:  0.0020,
              shaftRadius: 0.0025,    /* 5mm shaft */
              shaftLen:    0.055      /* 60mm screw — full visible */
            }), 'screw-batten');
            n.userData.explodeOffset = [0, 0.55, 0];
          }
        }
      })();

      /* ── BRACE SCREWS — 2 per end of the diagonal brace (4 total).
         FAS80100 (8×100mm). Two screws on the brace centreline (centred
         across the 100mm brace width), spaced ~35mm apart along the
         brace's long axis. Head proud on the OUTER -X face of the brace.
         Brace is a 2×4 (tw=50 depth × th=100 width) sitting outside the
         posts at X = [-halfW, -halfW + tw]. Outer face is at X=-halfW. */
      (function () {
        var topY = Hback - 2*th;
        var botY = DIMS.deckHeight;
        var overhang = DIMS.braceOver;
        var topZ = -halfL - 0.05, botZ = halfL + 0.05;
        var halfTh = th / 2;
        for (var iter = 0; iter < 5; iter++) {
          var dy = topY - botY;
          var dz = topZ - botZ;
          var len = Math.sqrt(dy*dy + dz*dz);
          var uy = dy / len, uz = dz / len;
          var py = -uz, pz = uy;
          var outerTopZ = topZ - pz * halfTh;
          var err = outerTopZ - (-halfL - overhang);
          topZ -= err;
          botZ = -topZ;
        }
        var dy = topY - botY;
        var dz = topZ - botZ;
        var len = Math.sqrt(dy*dy + dz*dz);
        var uy = dy / len, uz = dz / len;     /* unit vector ALONG brace (bot→top) */

        /* Brace OUTER -X face = -halfW - tw (brace extends from -halfW
           outward by tw). Head sits 5mm proud BEYOND that outer face. */
        var faceX = -halfW - tw - 0.005;
        var rotZ  = Math.PI / 2;              /* head -X, shaft +X (into wood) */

        /* Two screws per joint: along brace direction at ±17.5mm from
           the joint centre, both on brace CENTRELINE (no perpendicular
           offset). Joint centre placed 60mm inward from the very end so
           the pair sits comfortably inside the joint material, not on
           the chamfered tip. */
        var jointCentreInset = 0.060;
        var pairHalfSpacing  = 0.0175;        /* 17.5mm → 35mm apart */
        function placeJointPair(endY, endZ, signAlong) {
          /* signAlong = +1 for bottom end (inward direction is +uy/+uz),
                       = -1 for top    end (inward direction is -uy/-uz). */
          var cy = endY + signAlong * jointCentreInset * uy;
          var cz = endZ + signAlong * jointCentreInset * uz;
          [-pairHalfSpacing, +pairHalfSpacing].forEach(function (off) {
            var y = cy + off * uy;
            var z = cz + off * uz;
            var s = addT(self._mkScrew(faceX, y, z, {
              rotZ:        rotZ,
              headRadius:  0.0065,    /* 13mm head — heavy 8mm screw */
              headHeight:  0.0025,
              shaftRadius: 0.0040,    /* 8mm shaft */
              shaftLen:    0.095      /* 100mm screw — full visible shaft */
            }), 'screw-brace');
            s.userData.explodeOffset = [-0.65, 0, 0];
          });
        }
        placeJointPair(topY, topZ, -1);       /* top end → inward = -uy,-uz */
        placeJointPair(botY, botZ, +1);       /* bot end → inward = +uy,+uz */
      })();

      function addPostScrews(postX, postZ, postH, isFrontSide) {
        /* Wide face of post is at outer X (=±halfW). Screws sit on the OUTER X face.
           TOP joint screws → 'screw-rail-long' (they attach the long rail to the post).
           BOTTOM joint screws → 'screw-post-bottom' (they attach the post to the bottom frame).
           Bottom joint uses heavy FAS80100 (8×100mm) — same massive screw
           as frame corners — driven through the post into the frame. */
        var faceX = (postX > 0 ? postX + tw/2 + 0.002 : postX - tw/2 - 0.002);
        var rotZ = (postX > 0) ? -Math.PI / 2 : Math.PI / 2;
        var insetZ = 0.022;
        var insetY = 0.04;
        var sxOff = (postX > 0 ? 0.60 : -0.60);
        var topJointY = postH - th/2;
        /* TOP joint: 4 screws — tagged as screw-rail-long.
           rail-long sits OUTSIDE the post on outer X. Screw enters from
           rail-long's OUTER face (where head sits proud), passes 50mm
           through rail, 30mm into post. Head is on OUTER side, visible.
           Joint = rail (50mm) + post (50mm) = 100mm; FAS60080 (6×80mm)
           reaches 80mm — fully embedded. Visible shaft short (only the
           bit poking proud beyond the rail outer face). */
        /* rail-long sits OUTSIDE the post. Its OUTER face is at
           ±(halfW + tw) — i.e. post outer face (±halfW) + rail thickness
           (tw=50mm). Head proud 6mm beyond that face. */
        var railOuterX = (postX > 0
          ? halfW + tw + 0.006
          : -halfW - tw - 0.006);
        var topInsetY  = 0.020;
        [[topJointY - topInsetY, +insetZ], [topJointY - topInsetY, -insetZ],
         [topJointY + topInsetY, +insetZ], [topJointY + topInsetY, -insetZ]].forEach(function (yz) {
          var y = yz[0], dz = yz[1];
          if (y < 0 || y > postH) return;
          var s = addT(self._mkScrew(railOuterX, y, postZ + dz, {
            rotZ: rotZ,
            headRadius:  0.0050,
            headHeight:  0.0022,
            shaftRadius: 0.0030,
            shaftLen:    0.075      /* 80mm screw — full visible shaft */
          }), "screw-rail-long");
          s.userData.explodeOffset = [sxOff, 0, 0];
        });
        /* BOTTOM joint: 4 screws — tagged as screw-post-bottom.
           Frame extends y = 0.20..0.30 (centre 0.25). Screws sit closer
           to the centre — insetY 0.02 → y = 0.23 and 0.27, well inside
           the frame, not on the edges. Heavy 8mm screw, head proud. */
        var bottomY = DIMS.deckHeight - th/2;
        var insetYBot = 0.020;
        [[-insetYBot, +insetZ], [-insetYBot, -insetZ],
         [+insetYBot, +insetZ], [+insetYBot, -insetZ]].forEach(function (yz) {
          var y = bottomY + yz[0], dz = yz[1];
          if (y < 0 || y > postH) return;
          var s = addT(self._mkScrew(faceX, y, postZ + dz, {
            rotZ: rotZ,
            headRadius:  0.0065,   /* 13mm head — heavy 8mm screw */
            shaftRadius: 0.0040,   /* 8mm shaft */
            shaftLen:    0.080     /* 100mm screw, ~80mm visible */
          }), "screw-post-bottom");
          s.userData.explodeOffset = [sxOff, 0, 0];
        });
      }
      /* Apply to all corner posts, plus the middle pair when present.
         Both top-joint (screw-rail-long) and bottom-joint (screw-post-bottom)
         fasteners are added for every post, so screw counts in the BoQ
         stay in sync with the geometry. */
      var postZPositions = [-halfL + th/2, halfL - th/2];
      if (L > MID_POST_THRESHOLD) postZPositions.push(0);
      postZPositions.forEach(function (pz) {
        addPostScrews( halfW - tw/2, pz, Hfront, true);    /* +X tall posts */
        addPostScrews(-halfW + tw/2, pz, Hback,  false);   /* -X short posts */
      });

      /* ROOF SCREWS — at intersections of CORRUGATION VALLEYS with battens.
         Corrugation runs along X (slope direction), so the wave oscillates
         along Z with period ≈ 76mm and amplitude 15mm. Screws must sit
         in the VALLEY (lowest point), not on the ridge — that's where
         the EPDM gasket seals against the rib trough.
         Snap each screw Z to the nearest valley centre, then lower Y by
         the amplitude so the head sits IN the valley, not floating above
         the ridge. */
      var corrugPeriod  = 0.076;
      var corrugAmp     = 0.015;
      var roofScrewInset = 0.050;          /* keep screws ≥50mm inside batten ends */

      /* Match the EXACT phase formula used in _buildCorrugatedRoof:
           phase = (z/roofL + 0.5) * 2π * ribs   where ribs = round(roofL/period)
         Valley centres are sin(phase) = -1 → phase = -π/2 + 2πk
           z = roofL * ((k - 1/4)/ribs - 1/2)
         Enumerate ALL valleys, keep those inside the batten safe zone. */
      var ribs = Math.max(8, Math.round(roofL / corrugPeriod));
      var zMin = -L / 2 + roofScrewInset;
      var zMax =  L / 2 - roofScrewInset;
      var valleyZs = [];
      var validIdx = 0;
      for (var k = 0; k <= ribs; k++) {
        var vz = roofL * ((k - 0.25) / ribs - 0.5);
        if (vz >= zMin && vz <= zMax) {
          /* Keep every 3rd valley (skip 2 of every 3). */
          if (validIdx % 3 === 0) valleyZs.push(vz);
          validIdx++;
        }
      }

      for (var b2 = 0; b2 < battenCount; b2++) {
        var bX = (battenCount === 1)
          ? rafterExtension/2
          : battenFirstX + battenUsable * b2 / (battenCount - 1);
        for (var vi = 0; vi < valleyZs.length; vi++) {
          var sz = valleyZs[vi];
          /* Roof midline Y at this X — compensates for the tilt pivot
             (roof rotates around rafterExtension/2). */
          var midlineY = roofY + (bX - rafterExtension/2) * Math.sin(rafterTilt);
          var sy = midlineY - corrugAmp + 0.012;   /* head 12mm above valley floor */
          var rs = addT(self._mkRoofScrew(bX, sy, sz, rafterTilt), "screw-roof");
          rs.userData.explodeOffset = [0, 1.40, 0];
        }
      }


      /* ─── FIREWOOD LOGS ─────────────────────────────────────────────────
         Cylindrical logs stacked on the decking. Axis = X, length = W (rack
         width), so logs run along the short side and slightly overhang the
         decking (decking width = W - 2*tw, logs are full W). Stacked in
         layers along Z and Y up to the lower top rail underside, with a
         small head clearance. Only visible in the "Built" states. */
      (function () {
        var T = global.THREE;
        var logRadius = 0.055;                       /* ~11cm diameter typical firewood */
        var logLen = W;                              /* slightly overhangs decking */
        var deckTopY = DIMS.deckHeight + tw / 2;     /* top surface of decking */
        var headClearance = 0.10;
        var ceilingY = Hback - th - headClearance;   /* underside of back top rail - clearance */
        var usableH = ceilingY - deckTopY;

        var gap = 0.005;
        var pitch = 2 * logRadius + gap;             /* center-to-center between adjacent logs */
        var perRowZ = Math.max(1, Math.floor((L - 2 * tw) / pitch));
        var nLayers = Math.max(1, Math.floor(usableH / pitch));
        if (nLayers <= 0 || perRowZ <= 0) return;

        var rowSpanZ = perRowZ * pitch - gap;
        var startZ   = -rowSpanZ / 2 + logRadius;

        var WOOD_COLORS = [0x8d5a2b, 0x9c6a36, 0x7a4d24, 0xa37544, 0x6b4220];
        var BARK_DARK    = 0x3a2613;

        for (var layer = 0; layer < nLayers; layer++) {
          var ly = deckTopY + logRadius + layer * pitch;
          /* Stagger every other layer by half a pitch for a natural look. */
          var stagger = (layer % 2 === 0) ? 0 : pitch / 2;
          var thisRow = (layer % 2 === 0) ? perRowZ : perRowZ - 1;
          var thisStartZ = startZ + stagger;
          for (var i = 0; i < thisRow; i++) {
            var lz = thisStartZ + i * pitch;
            /* Light per-log radius/length variation for variety */
            var r = logRadius * (0.85 + Math.random() * 0.30);
            var lenJitter = (Math.random() - 0.5) * 0.04;
            var len = logLen + lenJitter;
            var geom = new T.CylinderGeometry(r, r, len, 14);
            geom.rotateZ(Math.PI / 2);                /* axis Y → axis X */
            var color = WOOD_COLORS[Math.floor(Math.random() * WOOD_COLORS.length)];
            var mat = new T.MeshStandardMaterial({
              color: color, roughness: 0.92, metalness: 0.0
            });
            var log = new T.Mesh(geom, mat);
            log.position.set(0, ly, lz);
            /* Random rotation around X — visual grain variation. */
            log.rotateX(Math.random() * Math.PI * 2);
            var added = addT(log, 'logs');
            added.userData.explodeOffset = [0, 2.20, 0];  /* fall from high above */
          }
        }
      })();

      /* ─── PERSON (3D scale reference, ~175 cm).
         The actual mesh comes from a Shopify-hosted GLB (loaded once and
         cached). We create the position-anchor group synchronously here so
         _applyAssemblyStep / animation logic can tag it; the GLB scene
         attaches asynchronously when the load resolves. ─────────────── */
      (function () {
        var T = global.THREE;
        var person = new T.Group();
        var bx = (STATE.bodyX != null) ? STATE.bodyX : (halfW + 0.60);
        var bz = (STATE.bodyZ != null) ? STATE.bodyZ : 0;
        person.position.set(bx, 0, bz);
        person.userData.partType = 'person';
        person.userData.basePos  = person.position.clone();
        G.add(person);

        if (_personScene) {
          /* Cached — add immediately. Log only the first few attaches per
             session to avoid filling the debug log when many builds happen
             during drags (each drag move triggers RackRenderer.build, and
             each build re-creates the person group, so we'd otherwise emit
             dozens of identical messages and crowd out useful diagnostics). */
          if (_personAttachLogCount === undefined) _personAttachLogCount = 0;
          if (_personAttachLogCount < 3) {
            _personAttachLogCount++;
            _dbg('attaching cached person to group at (' + bx.toFixed(2) + ',' + bz.toFixed(2) + ')');
          }
          person.add(_personScene);
        } else {
          /* Async load. When it resolves, find the CURRENT (latest) person
             group in the rack group and attach to it. */
          _loadPersonScene().then(function (scene) {
            var rg = global.RackEngine && global.RackEngine.rackGroup;
            if (!rg) { _dbg('no rackGroup at attach time'); return; }
            var attached = false;
            for (var i = 0; i < rg.children.length; i++) {
              var c = rg.children[i];
              if (c.userData && c.userData.partType === 'person' && c.children.length === 0) {
                c.add(scene);
                attached = true;
                _dbg('attached person on async resolve to group at (' +
                     c.position.x.toFixed(2) + ',' + c.position.z.toFixed(2) + ')');
                break;
              }
            }
            if (!attached) _dbg('no empty person group found for async attach (already had child?)');
            /* Force a render so the change is immediately visible even
               if the engine doesn't have a continuous render loop. */
            var eng = global.RackEngine;
            if (eng && eng.renderer && eng.scene && eng.camera) {
              eng.renderer.render(eng.scene, eng.camera);
            }
          }).catch(function (err) {
            _dbg('person load chain caught: ' + (err && err.message ? err.message : err));
          });
        }
      })();

      this._applyAssemblyStep(G, STATE.assemblyStep);

      /* Re-apply viz stage AFTER build so new meshes (esp. per-mesh log
         materials created with default opacity 1.0) pick up the current
         stage's opacity rules. Without this, a rebuild while in stage 2
         would temporarily show new parts at full opacity. */
      if (global.RackEngine && global.RackEngine.applyVizStage) {
        global.RackEngine.applyVizStage();
      }
    },

    _applyAssemblyStep: function (G, step) {
      /* Visible part types per step (20-step assembly sequence).
         Note: 'logs' (firewood) is only visible in the two "Built" states
         (step 0 and step 19) — assembly progression hides them. */
      var ALL = ['frame','decking','post','rail-side','rail-long','rafter','batten','roof','brace',
                 'screw-frame','screw-decking','screw-post-bottom','screw-rail-side',
                 'screw-rail-long','screw-rafter','screw-batten','screw-roof','screw-brace','logs'];
      var ALL_NO_LOGS = ['frame','decking','post','rail-side','rail-long','rafter','batten','roof','brace',
                 'screw-frame','screw-decking','screw-post-bottom','screw-rail-side',
                 'screw-rail-long','screw-rafter','screw-batten','screw-roof','screw-brace'];
      var VISIBLE_AT = {
        0:  ALL_NO_LOGS,                                                                  /* composed — no logs at initial state */
        1:  ALL_NO_LOGS,                                                                  /* exploded, no logs */
        2:  ['frame'],
        3:  ['frame','screw-frame'],
        4:  ['frame','screw-frame','decking'],
        5:  ['frame','screw-frame','decking','screw-decking'],
        6:  ['frame','screw-frame','decking','screw-decking','post'],
        7:  ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom'],
        8:  ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side'],
        9:  ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side'],
        10: ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side','rail-long'],
        11: ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side','rail-long','screw-rail-long'],
        12: ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side','rail-long','screw-rail-long','rafter'],
        13: ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side','rail-long','screw-rail-long','rafter','screw-rafter'],
        14: ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side','rail-long','screw-rail-long','rafter','screw-rafter','batten'],
        15: ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side','rail-long','screw-rail-long','rafter','screw-rafter','batten','screw-batten'],
        16: ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side','rail-long','screw-rail-long','rafter','screw-rafter','batten','screw-batten','roof'],
        17: ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side','rail-long','screw-rail-long','rafter','screw-rafter','batten','screw-batten','roof','screw-roof'],
        18: ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side','rail-long','screw-rail-long','rafter','screw-rafter','batten','screw-batten','roof','screw-roof','brace'],
        19: ALL_NO_LOGS,  /* + brace screws = built (no logs yet) */
        20: ALL           /* + logs (matrix-style drop animation) */
      };
      var visible = VISIBLE_AT[step] || VISIBLE_AT[0];

      /* Default explode offsets per part type (used when step === 1) */
      var EXPLODE_OFFSET = {
        'frame':              [0, -0.30, 0],
        'decking':            [0,  0.20, 0],
        'post':               [0,  0,    0],
        'rail-side':          [0,  0.25, 0],
        'rail-long':          [0,  0.45, 0],
        'rafter':             [0,  0.65, 0],
        'batten':             [0,  0.85, 0],
        'roof':               [0,  1.15, 0],
        'brace':              [-0.40, 0, 0],
        'screw-frame':        [0,  0.35, 0],
        'screw-decking':      [0,  0.35, 0],
        'screw-post-bottom':  [0,  0,    0],
        'screw-rail-side':    [0,  0.30, 0],
        'screw-rail-long':    [0,  0,    0],
        'screw-rafter':       [0,  0.40, 0],
        'screw-batten':       [0,  0.55, 0],
        'screw-roof':         [0,  1.40, 0],
        'screw-brace':        [-0.65, 0, 0],
        'logs':               [0,  3.50, 0]    /* fall from high above — matrix style */
      };

      G.children.forEach(function (child) {
        var pt = child.userData.partType;
        var base = child.userData.basePos;
        /* PERSON: always positioned to base, visibility from STATE.bodyVisible. */
        if (pt === 'person') {
          child.visible = (STATE.bodyVisible !== false);
          if (base) child.position.copy(base);
          return;
        }
        if (visible.indexOf(pt) === -1) {
          child.visible = false;
          return;
        }
        child.visible = true;
        var off = child.userData.explodeOffset || EXPLODE_OFFSET[pt];
        if (step === 1 && off) {
          child.position.set(base.x + off[0], base.y + off[1], base.z + off[2]);
        } else {
          child.position.copy(base);
        }
      });
    },

    _buildCorrugatedRoof: function (width, length, material) {
      var T = global.THREE;
      /* Corrugation runs ALONG the slope so water drains in the valleys.
         Slope is along X (W axis), so ridges go along X, oscillation along Z (length). */
      var period = 0.076;             /* ~3 inches between ridge tops */
      var amplitude = 0.015;          /* 15mm above/below midline */
      var ribs = Math.max(8, Math.round(length / period));
      var segsPerRib = 8;             /* ≥ 4 to render the wave (aliasing otherwise) */
      var segZ = ribs * segsPerRib;
      var segX = 4;                   /* a few segs across width for proper shading */

      var geom = new T.PlaneGeometry(width, length, segX, segZ);
      geom.rotateX(-Math.PI / 2);     /* lay flat in XZ plane */

      var pos = geom.attributes.position;
      for (var i = 0; i < pos.count; i++) {
        var z = pos.getZ(i);
        var phase = (z / length + 0.5) * Math.PI * 2 * ribs;
        pos.setY(i, Math.sin(phase) * amplitude);
      }
      pos.needsUpdate = true;
      geom.computeVertexNormals();

      material.side = T.DoubleSide;
      return new T.Mesh(geom, material);
    }
  };

  /* ----------------------------------------------------------
     DIMENSIONS OVERLAY (SVG) — drawn over the canvas in 2D stage 2.
     Hybrid CAD/brand style: thin gray dim lines (#888), sand-gold
     rounded chips with dark text. Re-projects from world to screen
     each refresh so live-resize during drag would still work
     (drag itself is disabled in stage 2, but resize events still fire).

     Public API:
       RackDimensions.init()    — create SVG once, attach to #cf-root.
       RackDimensions.refresh() — re-project and re-draw. Hides itself
                                  when NOT in 2D stage 2.
     ---------------------------------------------------------- */
  var SVG_NS = 'http://www.w3.org/2000/svg';

  global.RackDimensions = {
    svgEl: null,
    chipLayer: null,
    chipEls: [],

    init: function () {
      var root = document.getElementById('cf-root');
      if (!root) return;

      /* SVG layer for dim lines + extension lines + arrows. */
      if (!this.svgEl) {
        var svg = document.createElementNS(SVG_NS, 'svg');
        svg.id = 'cf-dims-svg';
        svg.style.cssText = [
          'position:absolute', 'top:0', 'left:0',
          'width:100%', 'height:100%',
          'pointer-events:none',
          'display:none',
          'z-index:6'
        ].join(';');
        root.appendChild(svg);
        this.svgEl = svg;

        var self = this;
        window.addEventListener('resize', function () { self.refresh(); });
      }

      /* Separate HTML layer for clickable/draggable dim chips. Lives
         above the SVG layer so chip pointer events aren't blocked by
         the SVG's pointer-events:none. Checked independently so
         hot-reload of lib.js after svgEl was created still gets a chip
         layer. */
      if (!this.chipLayer) {
        var existing = document.getElementById('cf-dim-chips');
        if (existing) {
          this.chipLayer = existing;
        } else {
          var layer = document.createElement('div');
          layer.id = 'cf-dim-chips';
          layer.style.cssText = [
            'position:absolute', 'top:0', 'left:0',
            'width:100%', 'height:100%',
            'pointer-events:none',
            'z-index:7'
          ].join(';');
          root.appendChild(layer);
          this.chipLayer = layer;
        }
      }
    },

    refresh: function () {
      /* Always call init — it now checks svgEl and chipLayer
         independently, so this handles the case where svgEl exists from
         an older session but chipLayer needs to be created (hot-reload). */
      this.init();
      if (!this.svgEl) return;

      var view = STATE.view, stage = STATE.vizStage;
      /* Dimensions visible in any 2D view (both plan and elevations). */
      var shouldShow = (view === '2d');
      if (!shouldShow) {
        this.svgEl.style.display = 'none';
        this.svgEl.innerHTML = '';
        if (this.chipLayer) this.chipLayer.style.display = 'none';
        this._clearChips();
        return;
      }

      this.svgEl.style.display = '';
      this.svgEl.innerHTML = '';
      if (this.chipLayer) this.chipLayer.style.display = '';
      this._clearChips();

      var eng = global.RackEngine;
      if (!eng || !eng.camera || !eng.renderer) return;
      var T = global.THREE;
      var canvas = eng.renderer.domElement;
      var rect = canvas.getBoundingClientRect();
      var w = rect.width, h = rect.height;
      if (w < 10 || h < 10) return;
      this.svgEl.setAttribute('viewBox', '0 0 ' + w + ' ' + h);

      var GRAY = '#888';
      var DARK = '#1a1a1a';
      var GOLD = '#D2A878';
      var DIM_OFFSET = 36;     /* dim line distance from rack edge */
      var EXT_GAP = 4, EXT_OVER = 6, ARROW = 6;
      var L = STATE.L, Wd = STATE.W, Hg = STATE.H;
      var unit = STATE.isMetric ? ' m' : '';
      var lLabel = fmtDim(L)  + unit;
      var wLabel = fmtDim(Wd) + unit;
      var hLabel = fmtDim(Hg) + unit;

      function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

      /* ──────────────────────────────────────────────────────────────
         Stage 2 — BOKORYS (side elevation). Camera at (50, H/2, 0)
         looks along -X. Sees Z × Y plane = L × H. Project rack at
         side face (x=0) so we see the L-length × H-height profile.
         ────────────────────────────────────────────────────────────── */
      if (stage === 2) {
        var cameraS = eng.orthoCamera;
        if (!cameraS) return;

        function projS(x, y, z) {
          var v = new T.Vector3(x, y, z).project(cameraS);
          return {
            x: (v.x * 0.5 + 0.5) * w,
            y: (1 - (v.y * 0.5 + 0.5)) * h
          };
        }

        var halfLs = STATE.L / 2;
        /* Bokorys rack corners: project at x=0, rack base spans
           z=-L/2..+L/2 at y=0; rack top at y=H. */
        var s_blB = projS(0, 0,  -halfLs);    /* bottom -Z corner */
        var s_brB = projS(0, 0,   halfLs);    /* bottom +Z corner */
        var s_brT = projS(0, Hg,  halfLs);    /* top +Z corner */
        var s_blT = projS(0, Hg, -halfLs);    /* top -Z corner */

        /* Outer bbox across all rack parts in bokorys view. */
        var worldBoxS = new T.Box3();
        var tmpBoxS = new T.Box3();
        eng.rackGroup.children.forEach(function (c) {
          if (c.userData && c.userData.partType === 'person') return;
          tmpBoxS.makeEmpty();
          tmpBoxS.setFromObject(c);
          if (!tmpBoxS.isEmpty()) worldBoxS.union(tmpBoxS);
        });

        var bb_minX_S = Infinity, bb_maxX_S = -Infinity;
        var bb_minY_S = Infinity, bb_maxY_S = -Infinity;
        if (!worldBoxS.isEmpty()) {
          [
            projS(0, worldBoxS.min.y, worldBoxS.min.z),
            projS(0, worldBoxS.min.y, worldBoxS.max.z),
            projS(0, worldBoxS.max.y, worldBoxS.min.z),
            projS(0, worldBoxS.max.y, worldBoxS.max.z)
          ].forEach(function (p) {
            if (p.x < bb_minX_S) bb_minX_S = p.x;
            if (p.x > bb_maxX_S) bb_maxX_S = p.x;
            if (p.y < bb_minY_S) bb_minY_S = p.y;
            if (p.y > bb_maxY_S) bb_maxY_S = p.y;
          });
        } else {
          bb_minX_S = Math.min(s_blB.x, s_brB.x, s_brT.x, s_blT.x);
          bb_maxX_S = Math.max(s_blB.x, s_brB.x, s_brT.x, s_blT.x);
          bb_minY_S = Math.min(s_blB.y, s_brB.y, s_brT.y, s_blT.y);
          bb_maxY_S = Math.max(s_blB.y, s_brB.y, s_brT.y, s_blT.y);
        }

        /* ── L dimension (horizontal, BELOW rack) ── */
        var wDimY_S = bb_maxY_S + DIM_OFFSET;
        this._drawDim(s_blB.x, s_blB.y, s_brB.x, s_brB.y, wDimY_S, 'horizontal',
                      lLabel, GRAY, DARK, GOLD, EXT_GAP, EXT_OVER, ARROW, 'length');

        /* ── H dimension (vertical, LEFT of rack) ── */
        var hDimX_S = bb_minX_S - DIM_OFFSET;
        this._drawDim(s_blB.x, s_blB.y, s_blT.x, s_blT.y, hDimX_S, 'vertical',
                      hLabel, GRAY, DARK, GOLD, EXT_GAP, EXT_OVER, ARROW, 'height');

        return;
      }

      /* ──────────────────────────────────────────────────────────────
         Stage 3 — NÁRYS (front elevation). Camera at (0, H/2, 50)
         looks along -Z. Sees X × Y plane = W × H. Project rack at
         front face (z=0) so we see the W-width × H-height profile.
         ────────────────────────────────────────────────────────────── */
      if (stage === 3) {
        var cameraF = eng.orthoCamera;
        if (!cameraF) return;

        function projF(x, y, z) {
          var v = new T.Vector3(x, y, z).project(cameraF);
          return {
            x: (v.x * 0.5 + 0.5) * w,
            y: (1 - (v.y * 0.5 + 0.5)) * h
          };
        }

        var halfWf = STATE.W / 2;
        /* Nárys rack corners: project at z=0, rack base spans
           x=-W/2..+W/2 at y=0; rack top at y=H. */
        var f_blL = projF(-halfWf, 0,  0);    /* bottom -X corner */
        var f_brL = projF( halfWf, 0,  0);    /* bottom +X corner */
        var f_brT = projF( halfWf, Hg, 0);    /* top +X corner */
        var f_blT = projF(-halfWf, Hg, 0);    /* top -X corner */

        /* Outer bbox across all rack parts in nárys view. */
        var worldBoxF = new T.Box3();
        var tmpBoxF = new T.Box3();
        eng.rackGroup.children.forEach(function (c) {
          if (c.userData && c.userData.partType === 'person') return;
          tmpBoxF.makeEmpty();
          tmpBoxF.setFromObject(c);
          if (!tmpBoxF.isEmpty()) worldBoxF.union(tmpBoxF);
        });

        var bb_minX_F = Infinity, bb_maxX_F = -Infinity;
        var bb_minY_F = Infinity, bb_maxY_F = -Infinity;
        if (!worldBoxF.isEmpty()) {
          [
            projF(worldBoxF.min.x, worldBoxF.min.y, 0),
            projF(worldBoxF.max.x, worldBoxF.min.y, 0),
            projF(worldBoxF.min.x, worldBoxF.max.y, 0),
            projF(worldBoxF.max.x, worldBoxF.max.y, 0)
          ].forEach(function (p) {
            if (p.x < bb_minX_F) bb_minX_F = p.x;
            if (p.x > bb_maxX_F) bb_maxX_F = p.x;
            if (p.y < bb_minY_F) bb_minY_F = p.y;
            if (p.y > bb_maxY_F) bb_maxY_F = p.y;
          });
        } else {
          bb_minX_F = Math.min(f_blL.x, f_brL.x, f_brT.x, f_blT.x);
          bb_maxX_F = Math.max(f_blL.x, f_brL.x, f_brT.x, f_blT.x);
          bb_minY_F = Math.min(f_blL.y, f_brL.y, f_brT.y, f_blT.y);
          bb_maxY_F = Math.max(f_blL.y, f_brL.y, f_brT.y, f_blT.y);
        }

        /* ── W dimension (horizontal, BELOW rack) ── */
        var lDimY_F = bb_maxY_F + DIM_OFFSET;
        this._drawDim(f_blL.x, f_blL.y, f_brL.x, f_brL.y, lDimY_F, 'horizontal',
                      wLabel, GRAY, DARK, GOLD, EXT_GAP, EXT_OVER, ARROW, 'width');

        /* ── H dimension (vertical, LEFT of rack) ── */
        var hDimX_F = bb_minX_F - DIM_OFFSET;
        this._drawDim(f_blL.x, f_blL.y, f_blT.x, f_blT.y, hDimX_F, 'vertical',
                      hLabel, GRAY, DARK, GOLD, EXT_GAP, EXT_OVER, ARROW, 'height');

        return;
      }

      /* ──────────────────────────────────────────────────────────────
         Stage 1 — top-down plan. Project rack footprint + bbox through
         the top-down ortho camera and place L + W dim lines around the
         outer screen extent. Person GLB excluded from bbox so its
         async load doesn't shift dim placement.
         ────────────────────────────────────────────────────────────── */
      var camera = eng.orthoCamera;
      function project(x, y, z) {
        var v = new T.Vector3(x, y, z).project(camera);
        return {
          x: (v.x * 0.5 + 0.5) * w,
          y: (1 - (v.y * 0.5 + 0.5)) * h
        };
      }

      var halfL = STATE.L / 2, halfWf = STATE.W / 2;
      /* Footprint corners (y = 0) projected to screen. These are still
         used as the dim-line endpoints — the dim line measures the rack
         BASE (W, L), not the roof overhang. */
      var c_mWmZ = project(-halfWf, 0, -halfL);  /* -X -Z */
      var c_pWmZ = project( halfWf, 0, -halfL);  /* +X -Z */
      var c_pWpZ = project( halfWf, 0,  halfL);  /* +X +Z */
      var c_mWpZ = project(-halfWf, 0,  halfL);  /* -X +Z */

      /* Compute the OUTER screen extent of every visible rack part
         (frame, decking, posts, rails, rafters, battens, roof — including
         its overhangs — fasteners, etc).

         IMPORTANT: Box3.setFromObject does NOT honour Object3D.visible
         (it expands by every descendant's geometry regardless). The
         scale-reference person sits at +X of the rack and is hidden in
         2D, but its bbox would otherwise push the L dim line out into
         nowhere. The fix: iterate the direct children of rackGroup and
         skip the person partType, then union the remaining boxes. */
      var worldBox = new T.Box3();
      var tmpBox = new T.Box3();
      eng.rackGroup.children.forEach(function (c) {
        if (c.userData && c.userData.partType === 'person') return;
        tmpBox.makeEmpty();
        tmpBox.setFromObject(c);
        if (!tmpBox.isEmpty()) worldBox.union(tmpBox);
      });

      var bb_minX = Infinity, bb_maxX = -Infinity;
      var bb_minY = Infinity, bb_maxY = -Infinity;
      if (!worldBox.isEmpty()) {
        [
          project(worldBox.min.x, 0, worldBox.min.z),
          project(worldBox.max.x, 0, worldBox.min.z),
          project(worldBox.max.x, 0, worldBox.max.z),
          project(worldBox.min.x, 0, worldBox.max.z)
        ].forEach(function (p) {
          if (p.x < bb_minX) bb_minX = p.x;
          if (p.x > bb_maxX) bb_maxX = p.x;
          if (p.y < bb_minY) bb_minY = p.y;
          if (p.y > bb_maxY) bb_maxY = p.y;
        });
      } else {
        bb_minX = Math.min(c_mWmZ.x, c_pWmZ.x, c_pWpZ.x, c_mWpZ.x);
        bb_maxX = Math.max(c_mWmZ.x, c_pWmZ.x, c_pWpZ.x, c_mWpZ.x);
        bb_minY = Math.min(c_mWmZ.y, c_pWmZ.y, c_pWpZ.y, c_mWpZ.y);
        bb_maxY = Math.max(c_mWmZ.y, c_pWmZ.y, c_pWpZ.y, c_mWpZ.y);
      }

      /* ── W dimension (along world X) ── */
      var avgYBack  = (c_mWmZ.y + c_pWmZ.y) / 2;
      var avgYFront = (c_mWpZ.y + c_pWpZ.y) / 2;
      var topEdge = (avgYBack < avgYFront) ? [c_mWmZ, c_pWmZ] : [c_mWpZ, c_pWpZ];
      var wDimY = bb_minY - DIM_OFFSET;
      this._drawDim(topEdge[0].x, topEdge[0].y, topEdge[1].x, topEdge[1].y, wDimY, 'horizontal',
                    wLabel, GRAY, DARK, GOLD, EXT_GAP, EXT_OVER, ARROW, 'width');

      /* ── L dimension (along world Z) ── */
      var avgXLeft  = (c_mWmZ.x + c_mWpZ.x) / 2;
      var avgXRight = (c_pWmZ.x + c_pWpZ.x) / 2;
      var rightEdge = (avgXRight > avgXLeft) ? [c_pWmZ, c_pWpZ] : [c_mWmZ, c_mWpZ];
      var lDimX = bb_maxX + DIM_OFFSET;
      this._drawDim(rightEdge[0].x, rightEdge[0].y, rightEdge[1].x, rightEdge[1].y, lDimX, 'vertical',
                    lLabel, GRAY, DARK, GOLD, EXT_GAP, EXT_OVER, ARROW, 'length');
    },

    /* Draw one dimension: two extension lines + dim line with arrows +
       gold chip with label. Generic for horizontal/vertical orientation.
         (cx1, cy1)–(cx2, cy2): corner anchor points
         offsetCoord: the screen Y (horizontal dim) or X (vertical dim)
                      where the dim line is placed
    */
    _drawDim: function (cx1, cy1, cx2, cy2, offsetCoord, orient,
                        label, lineColor, textColor, chipColor,
                        extGap, extOver, arrow, axis) {
      var svg = this.svgEl;
      var ns = SVG_NS;

      function add(tag, attrs) {
        var e = document.createElementNS(ns, tag);
        for (var k in attrs) e.setAttribute(k, attrs[k]);
        svg.appendChild(e);
        return e;
      }

      var p1, p2;   /* dim line endpoints */
      var e1a, e1b, e2a, e2b; /* extension line endpoints */
      var labelX, labelY;

      if (orient === 'horizontal') {
        var dimY = offsetCoord;
        p1 = { x: cx1, y: dimY };
        p2 = { x: cx2, y: dimY };
        /* extension lines: from a bit away from corner, past the dim line */
        var extDir = (dimY < cy1) ? -1 : 1;  /* up or down */
        e1a = { x: cx1, y: cy1 + extDir * extGap };
        e1b = { x: cx1, y: dimY + extDir * extOver };
        e2a = { x: cx2, y: cy2 + extDir * extGap };
        e2b = { x: cx2, y: dimY + extDir * extOver };
        labelX = (p1.x + p2.x) / 2;
        labelY = dimY;
      } else {
        var dimX = offsetCoord;
        p1 = { x: dimX, y: cy1 };
        p2 = { x: dimX, y: cy2 };
        var extDirX = (dimX > cx1) ? 1 : -1;
        e1a = { x: cx1 + extDirX * extGap, y: cy1 };
        e1b = { x: dimX + extDirX * extOver, y: cy1 };
        e2a = { x: cx2 + extDirX * extGap, y: cy2 };
        e2b = { x: dimX + extDirX * extOver, y: cy2 };
        labelX = dimX;
        labelY = (p1.y + p2.y) / 2;
      }

      function line(x1, y1, x2, y2, stroke, width, dash) {
        add('line', {
          x1: x1, y1: y1, x2: x2, y2: y2,
          stroke: stroke || lineColor,
          'stroke-width': width || 1,
          'stroke-linecap': 'round',
          'stroke-dasharray': dash || ''
        });
      }

      /* Extension lines (dashed) */
      line(e1a.x, e1a.y, e1b.x, e1b.y, lineColor, 1, '3 2');
      line(e2a.x, e2a.y, e2b.x, e2b.y, lineColor, 1, '3 2');
      /* Main dim line */
      line(p1.x, p1.y, p2.x, p2.y, lineColor, 1.2);

      /* Arrows at endpoints — both point OUTWARD (away from each other)
         like Czech architectural dim convention: ⇦ ··· ⇨
           horizontal: p1 (left)  → point LEFT  = -1
                       p2 (right) → point RIGHT = +1
           vertical:   p1 (top)   → point UP    = -1
                       p2 (bot)   → point DOWN  = +1 */
      function arrowTri(at, dir) {
        var a = arrow;
        var tri;
        if (orient === 'horizontal') {
          tri = at.x + ',' + at.y + ' ' + (at.x - dir * a) + ',' + (at.y - a * 0.5) +
                ' ' + (at.x - dir * a) + ',' + (at.y + a * 0.5);
        } else {
          tri = at.x + ',' + at.y + ' ' + (at.x - a * 0.5) + ',' + (at.y - dir * a) +
                ' ' + (at.x + a * 0.5) + ',' + (at.y - dir * a);
        }
        add('polygon', { points: tri, fill: lineColor, stroke: 'none' });
      }
      if (orient === 'horizontal') {
        arrowTri(p1, p1.x < p2.x ? -1 :  1);   /* p1 left → point left (outward) */
        arrowTri(p2, p2.x > p1.x ?  1 : -1);   /* p2 right → point right (outward) */
      } else {
        arrowTri(p1, p1.y < p2.y ? -1 :  1);   /* p1 top → point up (outward) */
        arrowTri(p2, p2.y > p1.y ?  1 : -1);   /* p2 bot → point down (outward) */
      }

      /* Chip — pool-style HTML dim label sitting next to each cote.
         Pure label only: no drag behaviour, no 6-dot grip icon. The
         round corner handles (cf-handle-l/w/h on the canvas overlay)
         remain the sole drag affordance.

         Why: on mobile the chips often overlap each other and the
         corner handles, and the user kept missing the round handles
         and grabbing chips instead. Chips show the value being
         displayed; the user touches the corner dot to resize.
         Removing chip drag also kills accidental pinch-to-zoom
         hijacks on touch screens. */
      if (axis && this.chipLayer) {
        var chip = document.createElement('div');
        chip.className = 'cf-dim-chip cf-dim-chip--label-only';
        chip.setAttribute('data-axis', axis);
        chip.textContent = label;
        chip.style.left = labelX + 'px';
        chip.style.top  = labelY + 'px';
        /* Make sure the chip can't intercept a drag meant for the
           round handle underneath if positions overlap. */
        chip.style.pointerEvents = 'none';
        this.chipLayer.appendChild(chip);
        this.chipEls.push(chip);
      }
    },

    _clearChips: function () {
      if (!this.chipEls) return;
      this.chipEls.forEach(function (el) { if (el && el.parentNode) el.parentNode.removeChild(el); });
      this.chipEls = [];
    }
  };

  /* ----------------------------------------------------------
     RACK RAYCASTER — pointer → instance picking

     Active only in 3D stage 1 (the default full-build view). On
     pointermove we ray-test
     against the registered instance meshes and update hover; on
     click we update selection and open the asset drawer.

     Hover and select have DIFFERENT visuals (see RackHighlight):
       • hover  = subtle emissive glow
       • select = strong outline overlay + accent emissive

     Click on empty space deselects + closes drawer. Re-click on
     the same instance is a no-op (selection unchanged, drawer
     stays open). Click on a different instance auto-switches
     the drawer content (Marek's explicit UX choice).
     ---------------------------------------------------------- */
  global.RackRaycaster = {
    _raycaster: null,
    _ndc:       null,
    _hovered:   null,    /* instance currently under cursor */
    _selected:  null,    /* instance currently selected (drawer open) */
    _initialized: false,

    init: function () {
      var T = global.THREE;
      var eng = global.RackEngine;
      if (!T || !eng || !eng.renderer || this._initialized) return;
      this._raycaster = new T.Raycaster();
      this._ndc       = new T.Vector2();
      var canvas      = eng.renderer.domElement;
      var self        = this;
      canvas.addEventListener('pointermove', function (e) { self._onMove(e); });
      canvas.addEventListener('pointerleave', function () { self._clearHover(); });
      canvas.addEventListener('click',       function (e) { self._onClick(e); });
      this._initialized = true;
      if (global.Configurator && global.Configurator.log) {
        global.Configurator.log.log('[raycaster] initialized');
      }
    },

    /* Stage 1 in 3D doubles as the asset-select mode — clicking any
       piece opens the drawer. Stages 2 (inspection) and 3 (profiles)
       are visualisation-only and disable picking so the user can
       study the build without accidentally triggering selects. */
    _isActive: function () {
      return STATE.view === '3d' && STATE.vizStage === 1;
    },

    _pick: function (e) {
      var eng = global.RackEngine;
      if (!eng || !eng.renderer || !eng.camera) return null;
      var canvas = eng.renderer.domElement;
      var rect   = canvas.getBoundingClientRect();
      this._ndc.x =  ((e.clientX - rect.left) / rect.width)  * 2 - 1;
      this._ndc.y = -((e.clientY - rect.top)  / rect.height) * 2 + 1;
      this._raycaster.setFromCamera(this._ndc, eng.camera);
      var meshes = global.RackInstances ? global.RackInstances.meshes() : [];
      if (!meshes.length) return null;
      /* recursive=false: hit only registered top-level meshes, not their
         edge-line children. */
      var hits = this._raycaster.intersectObjects(meshes, false);
      if (!hits.length) return null;
      /* Iterate hits in distance order; pick the first instance whose
         mesh is visible (assembly-step .visible respects intent). */
      for (var i = 0; i < hits.length; i++) {
        var m = hits[i].object;
        if (m.visible === false) continue;
        var inst = global.RackInstances.findByMesh(m);
        if (inst) return inst;
      }
      return null;
    },

    _onMove: function (e) {
      if (!this._isActive()) { this._clearHover(); return; }
      var inst = this._pick(e);
      var newHover = inst;
      if (this._hovered && this._hovered.id === (inst && inst.id)) return;
      /* leave previous hover */
      if (this._hovered) {
        if (!this._selected || this._selected.id !== this._hovered.id) {
          global.RackHighlight.unhover(this._hovered);
        }
        this._hovered = null;
      }
      /* enter new hover (skip if it's the already-selected instance) */
      if (newHover) {
        this._hovered = newHover;
        if (!this._selected || this._selected.id !== newHover.id) {
          global.RackHighlight.hover(newHover);
        }
      }
      /* canvas cursor hint */
      var canvas = global.RackEngine.renderer.domElement;
      canvas.classList.toggle('cf-asset-hover', !!newHover);
    },

    _clearHover: function () {
      if (this._hovered) {
        if (!this._selected || this._selected.id !== this._hovered.id) {
          global.RackHighlight.unhover(this._hovered);
        }
        this._hovered = null;
      }
      var eng = global.RackEngine;
      if (eng && eng.renderer) {
        eng.renderer.domElement.classList.remove('cf-asset-hover');
      }
    },

    _onClick: function (e) {
      if (!this._isActive()) return;
      var inst = this._pick(e);
      if (inst) {
        /* Auto-switch: clear previous selection highlight, set new. */
        if (this._selected && this._selected.id !== inst.id) {
          global.RackHighlight.deselect(this._selected);
        }
        /* Demote hover on the newly-selected instance so its hover
           emissive doesn't clash with the select outline. */
        if (this._hovered && this._hovered.id === inst.id) {
          global.RackHighlight.unhover(inst);
          this._hovered = null;
        }
        this._selected = inst;
        global.RackHighlight.select(inst);
        if (global.ConfiguratorAssetDrawer && global.ConfiguratorAssetDrawer.open) {
          global.ConfiguratorAssetDrawer.open(inst.id);
        }
      } else {
        /* Click on empty space — deselect and close drawer. */
        this._clearSelection();
      }
    },

    /* External clear (called by drawer's onClose so the highlight
       follows the drawer's close button / ESC / outside-click). */
    _clearSelection: function () {
      if (this._selected) {
        global.RackHighlight.deselect(this._selected);
        this._selected = null;
      }
      if (global.ConfiguratorAssetDrawer && global.ConfiguratorAssetDrawer.isOpen &&
          global.ConfiguratorAssetDrawer.isOpen()) {
        global.ConfiguratorAssetDrawer.close();
      }
    },

    /* Called from RackRenderer.build() — geometry is gone, so any
       hovered/selected instance reference is now stale. */
    _reset: function () {
      this._hovered  = null;
      this._selected = null;
    },

    /* Stage transition hook — called by RackEngine.applyVizStage().
       When leaving stage 1, drop hover/select and close drawer. */
    setActive: function (active) {
      var canvas = global.RackEngine && global.RackEngine.renderer
                 ? global.RackEngine.renderer.domElement : null;
      if (canvas) {
        canvas.classList.toggle('cf-asset-pickable', !!active);
        if (!active) canvas.classList.remove('cf-asset-hover');
      }
      if (!active) this._clearSelection();
    }
  };

  /* ----------------------------------------------------------
     RACK HIGHLIGHT — visual marking for hover & select

     Hover: subtle warm emissive glow (#554433 @ 0.35). The mesh
            material is CLONED for the duration of the hover so
            that the shared lumber material (used by multiple
            instances) isn't globally tinted.

     Select: strong sand-gold outline overlay + emissive boost on
             the picked mesh. The outline is a BackSide-rendered
             clone of the mesh geometry, scaled 1.04× — gives a
             clean silhouette even on overlapping pieces and
             respects the depth buffer so it can't be over-drawn
             by nearby meshes.

     Hover and select never coexist on the same mesh: if a hovered
     mesh is then selected, hover is removed first.
     ---------------------------------------------------------- */
  var HOVER_EMISSIVE        = 0x554433;
  var HOVER_INTENSITY       = 0.35;
  var SELECT_EMISSIVE       = 0xff8a00;
  var SELECT_INTENSITY      = 0.45;
  var SELECT_OUTLINE_COLOR  = 0xff8a00;
  var SELECT_OUTLINE_SCALE  = 1.04;

  global.RackHighlight = {
    hover: function (instance) {
      if (!instance || !instance.mesh || !instance.mesh.material) return;
      if (instance._hoverActive || instance._selectActive) return;
      var mesh = instance.mesh;
      /* Stash the shared material reference, then swap in a clone so
         emissive tinting doesn't bleed to sibling instances. */
      instance._sharedMat = mesh.material;
      mesh.material = mesh.material.clone();
      if (mesh.material.emissive) {
        instance._hoverPrevEmissive  = mesh.material.emissive.getHex();
        instance._hoverPrevIntensity = mesh.material.emissiveIntensity || 0;
        mesh.material.emissive.setHex(HOVER_EMISSIVE);
        mesh.material.emissiveIntensity = HOVER_INTENSITY;
        mesh.material.needsUpdate = true;
      }
      instance._hoverActive = true;
    },

    unhover: function (instance) {
      if (!instance || !instance._hoverActive) return;
      var mesh = instance.mesh;
      if (mesh && mesh.material) {
        try { mesh.material.dispose(); } catch (e) {}
        if (instance._sharedMat) {
          mesh.material = instance._sharedMat;
          delete instance._sharedMat;
        }
      }
      delete instance._hoverPrevEmissive;
      delete instance._hoverPrevIntensity;
      instance._hoverActive = false;
    },

    select: function (instance) {
      if (!instance || !instance.mesh || instance._selectActive) return;
      var T = global.THREE;
      if (!T) return;
      var mesh = instance.mesh;

      /* Emissive boost — same clone-and-replace pattern as hover so
         we don't tint other meshes sharing the same material. */
      instance._sharedMatSel = mesh.material;
      mesh.material = mesh.material.clone();
      if (mesh.material.emissive) {
        mesh.material.emissive.setHex(SELECT_EMISSIVE);
        mesh.material.emissiveIntensity = SELECT_INTENSITY;
        mesh.material.needsUpdate = true;
      }

      /* Outline overlay — BackSide-rendered clone, scaled up slightly.
         Added as a sibling in the same parent (rackGroup) so it inherits
         transforms automatically. */
      if (mesh.geometry) {
        var outlineGeom = mesh.geometry;   /* share geometry — no clone needed */
        var outlineMat  = new T.MeshBasicMaterial({
          color:        SELECT_OUTLINE_COLOR,
          side:         T.BackSide,
          transparent:  false,
          depthWrite:   false,
          polygonOffset: true,
          polygonOffsetFactor: -1,
          polygonOffsetUnits:  -1
        });
        var outline = new T.Mesh(outlineGeom, outlineMat);
        outline.position.copy(mesh.position);
        outline.rotation.copy(mesh.rotation);
        outline.scale.copy(mesh.scale).multiplyScalar(SELECT_OUTLINE_SCALE);
        outline.renderOrder = (mesh.renderOrder || 0) - 1;
        outline.userData.isSelectOutline = true;
        if (mesh.parent) mesh.parent.add(outline);
        instance._outlineMesh = outline;
      }

      instance._selectActive = true;
    },

    deselect: function (instance) {
      if (!instance || !instance._selectActive) return;
      var mesh = instance.mesh;
      /* Remove outline overlay. */
      if (instance._outlineMesh) {
        if (instance._outlineMesh.parent) {
          instance._outlineMesh.parent.remove(instance._outlineMesh);
        }
        if (instance._outlineMesh.material) {
          try { instance._outlineMesh.material.dispose(); } catch (e) {}
        }
        /* Geometry was SHARED — do not dispose. */
        delete instance._outlineMesh;
      }
      /* Restore shared material on the mesh. */
      if (mesh && mesh.material) {
        try { mesh.material.dispose(); } catch (e) {}
        if (instance._sharedMatSel) {
          mesh.material = instance._sharedMatSel;
          delete instance._sharedMatSel;
        }
      }
      instance._selectActive = false;
    },

    /* Called by RackRenderer.build() right before clearing the group —
       state machine reset for the cases where the user rebuilt while
       a hover/select was active. */
    _reset: function () {
      /* RackInstances are wiped right after this, so we can't restore
         materials. Just drop our cached references so the upcoming
         clean instances start fresh. */
      if (global.RackRaycaster && global.RackRaycaster._reset) {
        global.RackRaycaster._reset();
      }
    }
  };

  /* ----------------------------------------------------------
     DRAG HANDLES  (DOM positioning + drag logic)
     ---------------------------------------------------------- */
  global.RackHandles = {
    el: { L: null, W: null, H: null, label: null },
    dragging: null,
    dragStart: { x: 0, y: 0, value: 0 },

    init: function () {
      this.el.L     = document.getElementById('cf-handle-l');
      this.el.W     = document.getElementById('cf-handle-w');
      this.el.H     = document.getElementById('cf-handle-h');
      this.el.label = document.getElementById('cf-handle-label');
      /* Stage 2 elevation-specific handles — pure DOM references for now,
         positioning is added in repositionElevations(). Drag listeners
         come in a later refactor step. */
      this.el.elevFrontL = document.getElementById('cf-handle-elev-front-l');
      this.el.elevFrontH = document.getElementById('cf-handle-elev-front-h');
      this.el.elevSideW  = document.getElementById('cf-handle-elev-side-w');
      this.el.elevSideH  = document.getElementById('cf-handle-elev-side-h');
      if (!this.el.L) return;

      var self = this;
      ['L', 'W', 'H'].forEach(function (k) {
        var el = self.el[k];
        var axis = k === 'L' ? 'length' : k === 'W' ? 'width' : 'height';
        el.addEventListener('mousedown',  function (e) { self._start(axis, e); });
        el.addEventListener('touchstart', function (e) { self._start(axis, e); }, { passive: false });
      });

      /* Drag listeners on elevation handles. Each handle has a data-axis
         attribute mapping to length/width/height; we reuse the same
         _start / _move / _end pipeline so STATE.L/W/H updates flow
         through the existing render+refresh logic identically.
         _start branches on STATE.view + vizStage to pick the right
         camera for screen-axis projection — that branch already exists
         in this file, so no duplication needed. */
      [
        ['elevFrontL', 'length'],
        ['elevFrontH', 'height'],
        ['elevSideW',  'width'],
        ['elevSideH',  'height']
      ].forEach(function (pair) {
        var el = self.el[pair[0]];
        var axis = pair[1];
        if (!el) return;
        el.addEventListener('mousedown',  function (e) { self._start(axis, e); });
        el.addEventListener('touchstart', function (e) { self._start(axis, e); }, { passive: false });
      });

      window.addEventListener('mousemove', function (e) { self._move(e); });
      window.addEventListener('touchmove', function (e) { self._move(e); }, { passive: false });
      window.addEventListener('mouseup',   function ()  { self._end(); });
      window.addEventListener('touchend',  function ()  { self._end(); });
    },

    /* Position the elevation handles for 2D stage 2 (side view / bokorys).
       Single camera (orthoCameraSide), full canvas — no viewport halving.
       Only W + H handles are used; front-L and front-H stay hidden until
       stage 3 (front elevation) is added.
         side-w : world (0, 0, -W/2)      → bottom-right corner of side rack
         side-h : world (0, H/2, -W/2)    → middle-right edge of side rack
       Called every frame in stage 2 by the animation loop AFTER
       _fitSideElevationCamera so projection uses up-to-date frustum. */
    repositionElevations: function () {
      var eng = global.RackEngine;
      if (!eng || !eng.orthoCameraSide) return;
      if (!this.el.elevSideW) return;
      var T = global.THREE;
      var canvas = eng.renderer.domElement;
      var rect = canvas.getBoundingClientRect();
      var W_ = rect.width;
      var H_ = rect.height;

      function project(cam, x, y, z) {
        var v = new T.Vector3(x, y, z).project(cam);
        return {
          x: (v.x * 0.5 + 0.5) * W_,
          y: (1 - (v.y * 0.5 + 0.5)) * H_
        };
      }

      var Wd = STATE.W, Hg = STATE.H;
      var cS = eng.orthoCameraSide;

      var pSW = project(cS, 0, 0,        -Wd / 2);
      var pSH = project(cS, 0, Hg / 2,   -Wd / 2);

      this.el.elevSideW.style.left = pSW.x + 'px'; this.el.elevSideW.style.top = pSW.y + 'px';
      this.el.elevSideH.style.left = pSH.x + 'px'; this.el.elevSideH.style.top = pSH.y + 'px';

      /* Front handles unused in single-view side elevation — hide them. */
      if (this.el.elevFrontL) this.el.elevFrontL.classList.remove('cf-elev-visible');
      if (this.el.elevFrontH) this.el.elevFrontH.classList.remove('cf-elev-visible');

      /* One-shot diagnostic. */
      if (!this._elevHandlesLogged) {
        this._elevHandlesLogged = true;
        var DL = global.Configurator && global.Configurator.log;
        if (DL) {
          DL.log('[elev-handles] (single side view) canvas=' + Math.round(W_) + 'x' + Math.round(H_) +
                 ' side-W=(' + Math.round(pSW.x) + ',' + Math.round(pSW.y) +
                 ') side-H=(' + Math.round(pSH.x) + ',' + Math.round(pSH.y) + ')');
        }
      }
    },

    /* Position the LEGACY handles (cf-handle-l/w/h). Used only in 3D and
       2D stage 1 — in 2D stage 2 they are hidden and repositionElevations()
       handles the stage-specific elevation handle set instead.

       Camera: perspective (3D) or top-down ortho (2D plan). Single camera,
       full canvas — no viewport halving, no multi-camera switching. */
    reposition: function () {
      var eng = global.RackEngine;
      if (!eng || !eng.camera || !this.el.L) return;

      /* Visibility gate — MUST mirror _refreshHandlesVisibility()'s
         hideAll rule. The animation loop calls reposition() every frame
         and below would force display='' back on, re-showing the round
         drag handles that assembly mode (assemblyStep != 0) and the 3D
         inspection/profile stages (vizStage != 1) intend to hide. Bail
         and hide all three legacy handles when they must not be shown. */
      var handlesHidden = (STATE.assemblyStep !== 0)
                          || (STATE.view === '3d' && STATE.vizStage !== 1);
      if (handlesHidden) {
        this.el.L.style.display = 'none';
        this.el.W.style.display = 'none';
        this.el.H.style.display = 'none';
        return;
      }

      var T = global.THREE;
      var canvas = eng.renderer.domElement;
      var rect = canvas.getBoundingClientRect();
      var camera = (STATE.view === '2d' && eng.orthoCamera) ? eng.orthoCamera : eng.camera;

      function project(x, y, z) {
        var v = new T.Vector3(x, y, z).project(camera);
        return {
          x: (v.x * 0.5 + 0.5) * rect.width,
          y: (1 - (v.y * 0.5 + 0.5)) * rect.height
        };
      }

      var W = STATE.W, L = STATE.L, H = STATE.H;
      var pL, pW, pH;
      var hideH = false;
      var hideW = false;
      var hideL = false;

      if (STATE.view === '2d' && STATE.vizStage === 1) {
        /* Plan — W dim on TOP edge, L dim on RIGHT edge → shared corner
           is TOP-RIGHT. Move W handle to TOP-LEFT (only W dim) and L
           handle to BOTTOM-RIGHT (only L dim). H is hidden — pointless
           in plan AND it overlaps L (DOM order would steal clicks). */
        pW = project(-W / 2, 0, -L / 2);
        pL = project( W / 2, 0,  L / 2);
        pH = null; hideH = true;
      } else if (STATE.view === '2d' && STATE.vizStage === 2) {
        /* Bokorys (side elevation) — sees L × H. H dim on LEFT, L dim on
           BOTTOM. H handle on top-left, L handle on bottom-right. W is
           hidden (degenerate from this view) so it can't steal clicks
           from L which would otherwise sit on the same corner. */
        pH = project(0,  H,     L / 2);
        pL = project(0,  0,    -L / 2);
        pW = null; hideW = true;
      } else if (STATE.view === '2d' && STATE.vizStage === 3) {
        /* Nárys (front elevation) — sees W × H. H dim on LEFT, W dim on
           BOTTOM. H handle on top-left, W handle on bottom-right. L is
           hidden (degenerate from this view). */
        pH = project(-W / 2,  H,   0);
        pW = project( W / 2,  0,   0);
        pL = null; hideL = true;
      } else {
        /* 3D — handles at the +W/+L corner of the rack. */
        pL = project( W/2, H * 0.4,  L/2);
        pW = project( W/2, H * 0.4, -L/2);
        pH = project( W/2, H,        L/2);
      }

      if (pL) { this.el.L.style.display = ''; this.el.L.style.left = pL.x + 'px'; this.el.L.style.top = pL.y + 'px'; }
      else    { this.el.L.style.display = 'none'; }

      if (pW) { this.el.W.style.display = ''; this.el.W.style.left = pW.x + 'px'; this.el.W.style.top = pW.y + 'px'; }
      else    { this.el.W.style.display = 'none'; }

      if (pH) { this.el.H.style.display = ''; this.el.H.style.left = pH.x + 'px'; this.el.H.style.top = pH.y + 'px'; }
      else    { this.el.H.style.display = 'none'; }
    },

    _start: function (axis, ev) {
      ev.preventDefault();
      this.dragging = axis;
      var pt = ev.touches ? ev.touches[0] : ev;
      this.dragStart.x = pt.clientX;
      this.dragStart.y = pt.clientY;
      var key = axis === 'length' ? 'L' : axis === 'width' ? 'W' : 'H';
      this.dragStart.value = STATE[key];

      /* Capture the screen-space projection of the world axis we're dragging.
         The drag math projects two world points (handle + 0.5m offset along
         the drag axis) to screen, then computes a screen-direction vector.
         _move converts mouse/touch drag delta into world units via dot
         product with this direction.

         Branch on view + stage to pick the correct camera. In stage 2 each
         axis maps to a different elevation camera (length → front, width →
         side, height → either). The elevation handles call this same
         pipeline via their drag listeners. */
      if (axis === 'length' || axis === 'width') {
        var T = global.THREE;
        var eng = global.RackEngine;
        var W = STATE.W, L = STATE.L, H = STATE.H;
        var p0, p1, camera;
        var canvas = eng.renderer.domElement;
        var rect = canvas.getBoundingClientRect();
        var halfW = rect.width / 2;
        var viewportW = rect.width;

        /* Sample point convention: p0 is the corner the user is
           grabbing, p1 is the position the SAME corner would move to
           if STATE.{key} grew by 0.5 m. The drag's screen delta is
           then dot-projected onto (p1 - p0) in screen space, so
           moving the pointer in the direction the corner expands
           always increases the dimension — regardless of camera
           orientation or which side of the rack the handle sits on.
           This was previously broken in 2D plan view: the W handle
           lives on -W/2 corner, but the sample p0/p1 used +W/2, so
           the screen-direction vector pointed the wrong way and
           drag-left shrank W (user expected drag-left to grow W
           because the -W/2 corner moves left when W increases). */
        if (STATE.view === '2d' && STATE.vizStage === 1) {
          /* Plan — W handle is at top-left corner (-W/2, -L/2),
             L handle at bottom-right (+W/2, +L/2). */
          camera = eng.orthoCamera;
          viewportW = rect.width;
          if (axis === 'width') {
            /* -W/2 corner moves further in -X when W grows. */
            p0 = new T.Vector3(-W / 2,        0, -L / 2);
            p1 = new T.Vector3(-W / 2 - 0.5,  0, -L / 2);
          } else {
            /* +L/2 corner moves further in +Z when L grows. */
            p0 = new T.Vector3( W / 2, 0,  L / 2);
            p1 = new T.Vector3( W / 2, 0,  L / 2 + 0.5);
          }
        } else if (STATE.view === '2d' && STATE.vizStage === 2) {
          /* Bokorys (side) — H handle on left corner of L/2 line,
             L handle on right. Camera looks along +X. */
          camera = eng.orthoCamera;
          viewportW = rect.width;
          if (axis === 'length') {
            /* -L/2 corner moves further in -Z when L grows. */
            p0 = new T.Vector3(0, 0, -L / 2);
            p1 = new T.Vector3(0, 0, -L / 2 - 0.5);
          } else {
            /* W handle co-located with L on this stage (degenerate
               from front view). Default to +X growth direction. */
            p0 = new T.Vector3(0, 0, 0);
            p1 = new T.Vector3(-0.5, 0, 0);
          }
        } else if (STATE.view === '2d' && STATE.vizStage === 3) {
          /* Nárys (front) — H handle top-left, W handle bottom-right.
             Camera looks along -Z. */
          camera = eng.orthoCamera;
          viewportW = rect.width;
          if (axis === 'width') {
            /* +W/2 corner moves further in +X when W grows. */
            p0 = new T.Vector3( W / 2,       0, 0);
            p1 = new T.Vector3( W / 2 + 0.5, 0, 0);
          } else {
            /* L handle co-located on stage 3 (degenerate). */
            p0 = new T.Vector3(0, 0, 0);
            p1 = new T.Vector3(0.5, 0, 0);
          }
        } else {
          /* 3D — handles at +W/2, +L/2 corners. */
          if (axis === 'length') {
            p0 = new T.Vector3(W/2, H * 0.4,  L/2);
            p1 = new T.Vector3(W/2, H * 0.4,  L/2 + 0.5);
          } else {
            p0 = new T.Vector3( W/2,       H * 0.4, -L/2);
            p1 = new T.Vector3( W/2 + 0.5, H * 0.4, -L/2);
          }
          camera = eng.camera;
        }

        function proj(p) {
          var v = p.clone().project(camera);
          return { x: (v.x * 0.5 + 0.5) * viewportW, y: (1 - (v.y * 0.5 + 0.5)) * rect.height };
        }
        var s0 = proj(p0), s1 = proj(p1);
        var dx = s1.x - s0.x, dy = s1.y - s0.y;
        this.dragStart.screenDirX = dx;
        this.dragStart.screenDirY = dy;
        this.dragStart.screenLenSq = dx * dx + dy * dy;
        this.dragStart.worldStep = 0.5;
      }

      if (global.RackEngine && global.RackEngine.controls) global.RackEngine.controls.enabled = false;
      this.el.label.classList.add('active');
      this._updateLabel(axis);
      this._positionLabel(ev);
    },

    _move: function (ev) {
      if (!this.dragging) return;
      var pt = ev.touches ? ev.touches[0] : ev;
      var key = this.dragging === 'length' ? 'L' : this.dragging === 'width' ? 'W' : 'H';
      var delta = 0;
      if (this.dragging === 'height') {
        delta = -(pt.clientY - this.dragStart.y) / 100;
      } else {
        /* Screen-space projection: project drag vector onto screen direction of world axis,
           scale by world step ÷ |screen_dir|². This gives correct sign regardless of camera angle. */
        var dragDx = pt.clientX - this.dragStart.x;
        var dragDy = pt.clientY - this.dragStart.y;
        var dot = dragDx * this.dragStart.screenDirX + dragDy * this.dragStart.screenDirY;
        delta = (this.dragStart.screenLenSq > 0)
          ? (dot * this.dragStart.worldStep / this.dragStart.screenLenSq)
          : 0;
      }
      var r = RANGES[key];
      var raw = Math.max(r.min, Math.min(r.max, this.dragStart.value + delta));
      /* rack-v10-docs: on the unit's grid (1/2 in, 10 mm) -- the decking
         spreads evenly over any length, so a woodworker gets round numbers
         (an even 72 in) instead of a length made of board widths */
      var stp = DIMS.snap || 0.01;
      raw = Math.round(raw / stp) * stp;
      if (raw < r.min) raw = Math.ceil(r.min / stp) * stp;
      if (raw > r.max) raw = Math.floor(r.max / stp) * stp;
      STATE[key] = Math.round(raw * 1e6) / 1e6;
      global.RackRenderer.build();
      if (global.RackLib && global.RackLib.refreshUI) global.RackLib.refreshUI();
      this._positionLabel(ev);
      this._updateLabel(this.dragging);
    },

    _end: function () {
      this.dragging = null;
      if (global.RackEngine && global.RackEngine.controls) global.RackEngine.controls.enabled = true;
      this.el.label.classList.remove('active');
      /* Persist dimensions on drag end so user's L/W/H survive reload. */
      if (global.RackLib && global.RackLib.savePref) global.RackLib.savePref();
    },

    _updateLabel: function (axis) {
      var key = axis === 'length' ? 'L' : axis === 'width' ? 'W' : 'H';
      var T   = (global.RK_BOOT && global.RK_BOOT.i18n) || {};
      var nm  = axis === 'length' ? (T.lengthLabel || 'LENGTH')
              : axis === 'width'  ? (T.widthLabel  || 'WIDTH')
              :                     (T.heightLabel || 'HEIGHT');
      var u   = STATE.isMetric ? ' m' : '';
      this.el.label.textContent = nm + ': ' + global.RackLib.fmtDim(STATE[key]) + u;
    },

    _positionLabel: function (ev) {
      var pt = ev.touches ? ev.touches[0] : ev;
      var root = document.getElementById('cf-root');
      var rect = root.getBoundingClientRect();
      this.el.label.style.left = (pt.clientX - rect.left) + 'px';
      this.el.label.style.top  = (pt.clientY - rect.top - 30) + 'px';
    }
  };

  /* ----------------------------------------------------------
     LIB CONTROLLER  (boot, formatters, handlers)
     ---------------------------------------------------------- */
  function detectUnits() {
    try {
      if (global.Shopify && Shopify.currency && Shopify.currency.active === 'USD') return false;
    } catch (e) {}
    return true;
  }

  function fmtLen(meters) {
    if (STATE.isMetric) return meters.toFixed(2);
    return (meters * 3.28084).toFixed(1);
  }
  /* a size the way it is built (rack-v10-docs): feet-inches to 1/16 in,
     metres in metric -- never 5.91 ft */
  function fmtFtIn(m) {
    if (!isFinite(m)) return '';
    var neg = (m < 0) ? '-' : '';
    var six = Math.round(Math.abs(m) / IN_M * 16);
    var ft = Math.floor(six / 192), rest = six - ft * 192, inch = Math.floor(rest / 16);
    var num = rest - inch * 16, den = 16, fs = '';
    if (num) { while (num % 2 === 0) { num /= 2; den /= 2; } fs = num + '/' + den; }
    var ins = (inch && fs) ? (inch + ' ' + fs) : (fs || String(inch));
    return neg + ((ft > 0) ? (ft + "'-" + ins + '"') : (ins + '"'));
  }
  function fmtDim(meters) {
    if (STATE.isMetric) return meters.toFixed(2);
    return fmtFtIn(meters);
  }
  function fmtArea(m2) {
    if (STATE.isMetric) return m2.toFixed(2);
    return (m2 * 10.7639).toFixed(1);
  }

  /* ----------------------------------------------------------
     BUDGET INTEGRATION — publish current BoQ to window.ConfiguratorBoQ.
     budget-prd-cat-selector.liquid reads this and renders accordingly.
     The variantData shape mirrors the Google Sheet rows the budget app expects:
       [emptyHeaderRow, emptyHeaderRow, [id, metricQty, imperialQty], ...]
     (budget skips i=0,1 — they are headers).

     We ALSO publish via ConfiguratorBudgetBridge (when loaded) so the
     asset drawer can read byMaterial for project-quantity display in
     the Product section without recomputing — same source of truth.
     ---------------------------------------------------------- */
  var _budgetRefreshTimer = null;
  function publishBoQToBudget() {
    var b = global.RackBoQ.compute();
    var rows = [[], []];   /* 2 header rows skipped by budget loadCSV */
    var M_TO_FT  = 3.2808;
    var M2_TO_FT2 = 10.7639;
    function round2(n) { return Math.round(n * 100) / 100; }
    Object.keys(b.byMaterial).forEach(function (id) {
      var mat = MATERIALS[id];
      var qty = b.byMaterial[id];
      var metric, imperial;
      if (mat && mat.unit === 'm') {
        /* Linear meters: 2 decimals (cm precision is plenty for BoQ). */
        metric   = round2(qty);
        imperial = round2(qty * M_TO_FT);
      } else if (mat && mat.unit === 'm²') {
        /* Area: 2 decimals. */
        metric   = round2(qty);
        imperial = round2(qty * M2_TO_FT2);
      } else {
        /* Pieces (fasteners): integer. */
        metric   = Math.round(qty);
        imperial = Math.round(qty);
      }
      rows.push([id, metric, imperial]);
    });

    /* Prefer the bridge publisher (sets variantData AND byMaterial AND
       fires the shared event). Fall back to direct global assignment
       so legacy budget integration still works without the bridge. */
    if (global.ConfiguratorBudgetBridge && global.ConfiguratorBudgetBridge.publishVariantData) {
      global.ConfiguratorBudgetBridge.publishVariantData(b.byMaterial);
      /* Bridge sets variantData via Object.keys order; we want the
         metric/imperial rounding logic above to be authoritative.
         Overwrite variantData with the rounded version. */
      global.ConfiguratorBoQ.variantData = rows;
      global.ConfiguratorBoQ.source      = 'rack';
    } else {
      global.ConfiguratorBoQ = {
        source:      'rack',
        variantData: rows,
        byMaterial:  b.byMaterial
      };
    }

    /* Debounce the (potentially expensive) budget re-render. 600ms is enough
       for a user to stop dragging before we trigger a render. */
    if (_budgetRefreshTimer) clearTimeout(_budgetRefreshTimer);
    _budgetRefreshTimer = setTimeout(function () {
      if (typeof global.loadVariantCSV === 'function') {
        try { global.loadVariantCSV(); } catch (e) { /* budget not loaded yet */ }
      }
    }, 600);
  }

  function refreshUI(skipPublish) {
    var $ = function (id) { return document.getElementById(id); };
    if ($('cf-stat-l')) $('cf-stat-l').textContent = fmtDim(STATE.L);
    if ($('cf-stat-w')) $('cf-stat-w').textContent = fmtDim(STATE.W);
    if ($('cf-stat-h')) $('cf-stat-h').textContent = fmtDim(STATE.H);
    if ($('cf-stat-size-unit')) $('cf-stat-size-unit').textContent = STATE.isMetric ? 'm' : '';

    var b = global.RackBoQ.compute();
    /* "Lumber" total in the info banner: sum of EVERY linear-meter
       lumber profile (currently KVH 60×100 + KVL battens). If new
       profiles are ever added to RackBoQ.byMaterial that are also
       measured in linear m (codes starting with KV*), they're
       picked up automatically. Roof (ROR) is m² and decking (TST)
       has its own row — both stay separate.
       Defensive: wrap in try/catch so any unexpected shape from
       RackBoQ doesn't blow up the whole refreshUI pipeline (which
       would freeze the model load). */
    var lumberTotal = 0;
    try {
      if (b && b.byMaterial && typeof b.byMaterial === 'object') {
        var keys = Object.keys(b.byMaterial);
        for (var i = 0; i < keys.length; i++) {
          var code = keys[i];
          if (code && code.indexOf('KV') === 0) {
            var v = +b.byMaterial[code];
            if (isFinite(v)) lumberTotal += v;
          }
        }
      } else if (b && typeof b.kvh === 'number') {
        lumberTotal = b.kvh;
      }
    } catch (e) {
      lumberTotal = (b && +b.kvh) || 0;
    }
    if ($('cf-stat-kvh'))      $('cf-stat-kvh').textContent      = fmtLen(lumberTotal);
    if ($('cf-stat-tst'))      $('cf-stat-tst').textContent      = fmtLen(b.tst);
    if ($('cf-stat-ror'))      $('cf-stat-ror').textContent      = fmtArea(b.ror);
    if ($('cf-stat-kvh-unit')) $('cf-stat-kvh-unit').textContent = STATE.isMetric ? 'm' : 'ft';
    if ($('cf-stat-tst-unit')) $('cf-stat-tst-unit').textContent = STATE.isMetric ? 'm' : 'ft';
    if ($('cf-stat-ror-unit')) $('cf-stat-ror-unit').textContent = STATE.isMetric ? 'm²' : 'ft²';

    /* Assembly step label */
    if ($('cf-stat-assembly')) {
      /* Read from RK_BOOT.i18n with English fallback (keeps lib usable in
         isolation, e.g. when running the snippet outside Shopify). */
      var T = (global.RK_BOOT && global.RK_BOOT.i18n) || {};
      var labels = [
        T.assembly_0  || 'Built',          /* 0 */
        T.assembly_1  || 'Exploded',       /* 1 */
        T.assembly_2  || 'Frame',          /* 2 */
        T.assembly_3  || 'Frame screws',   /* 3 */
        T.assembly_4  || 'Decking',        /* 4 */
        T.assembly_5  || 'Decking nails',  /* 5 */
        T.assembly_6  || 'Posts',          /* 6 */
        T.assembly_7  || 'Post screws',    /* 7 */
        T.assembly_8  || 'Side rails',     /* 8 */
        T.assembly_9  || 'Side screws',    /* 9 */
        T.assembly_10 || 'Long rails',     /* 10 */
        T.assembly_11 || 'Long screws',    /* 11 */
        T.assembly_12 || 'Rafters',        /* 12 */
        T.assembly_13 || 'Rafter screws',  /* 13 */
        T.assembly_14 || 'Battens',        /* 14 */
        T.assembly_15 || 'Batten nails',   /* 15 */
        T.assembly_16 || 'Roofing',        /* 16 */
        T.assembly_17 || 'Roof screws',    /* 17 */
        T.assembly_18 || 'Brace',          /* 18 */
        T.assembly_19 || 'Stocked'         /* 19 (= brace screws → done, firewood stacked) */
      ];
      $('cf-stat-assembly').textContent = STATE.assemblyStep + '/20';
      var titleEl = $('cf-btn-assembly');
      if (titleEl) titleEl.title = (T.assemblyPrefix || 'Assembly:') + ' ' + labels[STATE.assemblyStep];
    }

    /* View + viz stage labels */
    if ($('cf-stat-viz')) {
      $('cf-stat-viz').textContent = STATE.vizStage;
    }
    var vizBtn = $('cf-btn-viz');
    if (vizBtn) {
      var T2 = (global.RK_BOOT && global.RK_BOOT.i18n) || {};
      /* Helper: treat "Translation missing: ..." strings as missing too.
         Shopify's `t` filter returns this string (not empty) when the key
         is absent, so a plain `|| fallback` in Liquid wouldn't trigger.
         We catch that here so the UI shows a clean fallback instead of the
         raw missing-translation marker. */
      function tt(val, fallback) {
        if (!val) return fallback;
        if (typeof val === 'string' && val.indexOf('Translation missing') === 0) return fallback;
        return val;
      }
      var STAGE_LABELS = {
        '3d:1': tt(T2.viz_3d_1, 'Full build'),
        '3d:2': tt(T2.viz_3d_2, 'Inspection (structure 30 %)'),
        '3d:3': tt(T2.viz_3d_3, 'Profiles (color-coded)'),
        '2d:1': tt(T2.viz_2d_1, 'Top view (plan)'),
        '2d:2': tt(T2.viz_2d_2, 'Side elevation (bokorys)'),
        '2d:3': tt(T2.viz_2d_3, 'Front elevation (nárys)')
      };
      var key = STATE.view + ':' + STATE.vizStage;
      var vizFullTitle = tt(T2.vizPrefix, 'Visualisation:') + ' ' +
                         (STAGE_LABELS[key] || ('Stage ' + STATE.vizStage));
      vizBtn.title = vizFullTitle;
      vizBtn.setAttribute('aria-pressed', STATE.vizStage !== 1 ? 'true' : 'false');
      vizBtn.style.opacity = '';
      vizBtn.style.pointerEvents = '';
    }
    var viewBtn = $('cf-btn-view');
    if (viewBtn) {
      var viewT = (global.RK_BOOT && global.RK_BOOT.i18n) || {};
      viewBtn.title = STATE.view === '3d'
        ? (viewT.toggleTo2D || 'Switch to 2D top view')
        : (viewT.toggleTo3D || 'Switch to 3D view');
      viewBtn.setAttribute('aria-pressed', STATE.view === '2d' ? 'true' : 'false');
    }

    /* Refresh dimension overlay — fires on every state change that goes
       through refreshUI (drag, unit toggle, view switch). The refresh
       internally exits cheaply when not in 2D, so the cost in 3D is just
       a single function call + early return. */
    if (global.RackDimensions && global.RackDimensions.refresh) {
      global.RackDimensions.refresh();
    }

    /* Publish to budget on every refresh (debounced inside).
       Skip when only unit changed — published data is unchanged (always contains
       both metric AND imperial values), and budget handles unit switching
       internally via the unitChange event. Skipping avoids a costly
       processSheetLines pass on every unit toggle. */
    if (!skipPublish) {
      publishBoQToBudget();
    }
  }

  function reset() {
    /* Full reset to initial page-load state. Returns dimensions,
       view (3D), visualisation stage (1), assembly step (0 = composed),
       2D fill opacity, body-visible toggle, and visualisation cycle
       label all to defaults. Matches what a user sees on a fresh page
       load with no prior saved preferences. */
    STATE.L = DEFAULTS.L;
    STATE.W = DEFAULTS.W;
    STATE.H = DEFAULTS.H;
    STATE.view         = '3d';
    STATE.vizStage     = 1;
    STATE.assemblyStep = 0;
    STATE.opacity2D    = 0.45;
    STATE.bodyVisible  = false;
    STATE.bodyX        = null;
    STATE.bodyZ        = null;

    /* Reflect on opacity slider DOM if present. */
    var opSlider = document.getElementById('cf-2d-opacity-slider');
    if (opSlider) {
      opSlider.value = Math.round(STATE.opacity2D * 100);
      var maxV = parseInt(opSlider.max, 10) || 100;
      opSlider.style.setProperty('--cfg-fill',
        (100 - (STATE.opacity2D * 100 / maxV * 100)) + '%');
    }
    var opVal = document.getElementById('cf-2d-opacity-value');
    if (opVal) opVal.textContent = Math.round(STATE.opacity2D * 100) + '%';

    /* Rebuild scene with new dims + assembly step. */
    global.RackRenderer.build();

    /* Re-apply view + viz stage (camera reposition, controls, dim chips,
       handle visibility, cf-view-2d class, opacity slider visibility). */
    if (global.RackEngine) {
      if (global.RackEngine.applyView)     global.RackEngine.applyView();
      if (global.RackEngine.applyVizStage) global.RackEngine.applyVizStage();
    }

    refreshUI();

    /* Persist the reset so reload keeps it (matches initial state). */
    if (global.RackLib && global.RackLib.savePref) global.RackLib.savePref();
  }

  /* ----------------------------------------------------------
     UNIT SYNC — bidirectional binding between configurator and budget.
     The page has two unit toggles: configurator's own (calls toggleUnits)
     and budget's #unitSelector (clickUnitSelector fires 'unitChange' event).
     We make them stay in lockstep:
       - Configurator toggle  → updates #unitSelector + fires 'unitChange'
       - Budget toggle        → updates STATE.isMetric + refreshUI
     ---------------------------------------------------------- */
  function _broadcastUnitToBudget(isMetric) {
    var unitSel = document.getElementById('unitSelector');
    if (!unitSel) return;
    var newUnit = isMetric ? 'metric' : 'imperial';
    if (unitSel.getAttribute('data-current-unit') === newUnit) return;
    unitSel.setAttribute('data-current-unit', newUnit);
    var display = document.getElementById('currentUnitDisplay');
    if (display) display.textContent = newUnit.charAt(0).toUpperCase() + newUnit.slice(1);
    /* Fire the same event budget-prd-cat-selector.liquid uses. budget.liquid
       listens for this and calls renderItems() to re-render with new units. */
    try {
      window.dispatchEvent(new CustomEvent('unitChange', { detail: { unit: newUnit } }));
    } catch (e) {}
  }

  /* rack-v10-docs: the units are the lumber -- switching them rebuilds the
     rack from the unit's profiles (US 2x4 = 1 1/2 x 3 1/2 in) on the unit's
     grid, so the quantities change and the bill is published again */
  function _setUnits(isMetric) {
    STATE.isMetric = !!isMetric;
    _applyUnitDims();
    _snapDims();
    if (global.RackRenderer && global.RackEngine && global.RackEngine.rackGroup) global.RackRenderer.build();
    if (global.RackHandles && global.RackHandles.reposition) { try { global.RackHandles.reposition(); } catch (e) {} }
    refreshUI();
    savePref();
    if (global.Configurator && global.Configurator.log) {
      global.Configurator.log.log('[rack-units] ' + (STATE.isMetric ? 'metric' : 'imperial (US lumber)') +
        ' L=' + fmtDim(STATE.L) + ' W=' + fmtDim(STATE.W) + ' H=' + fmtDim(STATE.H) +
        ' 2x4=' + (DIMS.twoByFour.w * 1000).toFixed(1) + 'x' + (DIMS.twoByFour.h * 1000).toFixed(1) + ' mm');
    }
  }

  function _syncFromBudgetUnit() {
    var unitSel = document.getElementById('unitSelector');
    if (!unitSel) return;
    var unit = unitSel.getAttribute('data-current-unit') || 'metric';
    var shouldBeMetric = (unit === 'metric');
    if (STATE.isMetric !== shouldBeMetric) _setUnits(shouldBeMetric);
  }

  function toggleUnits() {
    _setUnits(!STATE.isMetric);
    _broadcastUnitToBudget(STATE.isMetric);
  }

  function share() {
    /* Universal share flow: if configurator-share.js is loaded (the
       snippet adds it via <script src>), open the rich share modal —
       image card with totals, WhatsApp / Download / Copy-Link actions.
       The modal reads window.ConfiguratorShareLabels (i18n) and
       window.ConfiguratorShareGetTotals (data callback) set by the
       snippet's inline IIFE.

       If the share module isn't loaded yet, fall back to
       navigator.share / clipboard so the button never feels broken.
       Fallback strings come from RK_BOOT.i18n.share.* (which
       configurator-locales.liquid populates) — no hardcoded labels. */
    var T  = (global.RK_BOOT && global.RK_BOOT.i18n) || {};
    var SH = T.share || {};

    if (global.ConfiguratorShare && typeof global.ConfiguratorShare.open === 'function') {
      try { global.ConfiguratorShare.open(); return; }
      catch (err) {
        _dbg('ConfiguratorShare.open threw: ' + (err && err.message ? err.message : err));
      }
    }

    /* Fallback for browsers without the share modal loaded. Builds a
       plain-text summary using the product title; no hardcoded
       "Lumber:/Decking:/Roof:" labels — the dimensions and totals
       speak for themselves cross-language. */
    var b = global.RackBoQ.compute();
    var unit = STATE.isMetric ? ' m' : ' ft';
    var txt = (BOOT.productTitle || 'Firewood Rack') + '\n' +
              fmtLen(STATE.L) + ' × ' + fmtLen(STATE.W) + ' × ' + fmtLen(STATE.H) +
              ' ' + (STATE.isMetric ? 'm' : 'ft') + '\n' +
              'KVH10060: ' + fmtLen(b.kvh) + unit + '\n' +
              'TST00001: ' + fmtLen(b.tst) + unit + '\n' +
              'ROR12004: ' + fmtArea(b.ror) + (STATE.isMetric ? ' m²' : ' ft²');

    if (navigator.share) {
      navigator.share({ title: BOOT.productTitle || 'Firewood Rack', text: txt })
        .catch(function () {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(txt);
      alert(T.copiedToClipboard || 'Copied to clipboard');
    }
  }

  /* ─────────────────────────────────────────────────────────────
     PDF EXPORT — assembles all data the universal PDF exporter
     needs (cover totals, 3D snapshots, 2D snapshots, cut list, BoQ)
     and hands them off to ConfiguratorPdfExporter.export().

     The exporter knows NOTHING about rack — every shape it draws is
     described by the args object below. Clone this for new
     configurators by replacing only the data-collection helpers.
     ───────────────────────────────────────────────────────────── */
  /* ─────────────────────────────────────────────────────────────
     PDF EXPORT — assembles all data the universal PDF exporter
     needs (cover totals, iso + 3 ortho snapshots with vector dim
     overlays, cut list, BoQ) and hands them off to
     ConfiguratorPdfExporter.export().

     The exporter knows NOTHING about rack — every shape it draws is
     described by the args object below. Clone this for new
     configurators by replacing only the data-collection helpers.
     ───────────────────────────────────────────────────────────── */
  /* THE PDF (rack-v10-docs): the drawings modelled on Matt Thompson's
     original set (RackDoc above), then the cut list, the bill and the
     shopping list -- and, as on every other project, it opens on the
     drawings page (configurator-core pdf-pkg: /pages/blueprints?doc=<id>),
     every page visible, the first five sharp. This is a royalty product: the
     purchase is the PDF, never tokens -- a buyer (or an admin) has every page
     and the download at once, anybody else sees the pages and is offered the
     purchase there (rec.buy). Where the package cannot be kept (no
     indexedDB) the PDF is made here as before, behind the purchase gate. */
  var _docNote = '';
  async function exportPdf() {
    var T = (global.RK_BOOT && global.RK_BOOT.i18n) || {};
    var X = window.ConfiguratorPdfExporter;
    if (!X || !X.export) {
      alert(T.pdfLibLoading || 'PDF library not yet loaded -- try again in a moment.');
      return;
    }
    var BOOT = global.RK_BOOT || {};
    var bridge = global.ConfiguratorBudgetBridge || null;
    var btn = document.getElementById('cf-btn-pdf');
    if (btn) btn.classList.add('active');
    var LOG = function (m) { if (global.Configurator && global.Configurator.log) global.Configurator.log.log('[rack-pdf] ' + m); };

    /* the central BoQ first, or every row prints as a bare code */
    if (bridge && typeof bridge.isReady === 'function' && !bridge.isReady()) {
      var waited = 0;
      while (!bridge.isReady() && waited < 5000) {
        await new Promise(function (res) { setTimeout(res, 100); });
        waited += 100;
      }
      if (!bridge.isReady()) {
        if (btn) btn.classList.remove('active');
        alert(T.pdfWaitingForData || 'Configuration data is still loading. Please wait a moment and try again.');
        return;
      }
    }

    try {
      var owner = false;
      try { owner = !!(global.Configurator && global.Configurator.purchaseGate && global.Configurator.purchaseGate.isPurchased()); } catch (eO) { owner = false; }
      var pkg = !!(X._pkgPage && X._pkgPage() && typeof X.socialGate === 'function');
      if (pkg) {
        var buy = null;
        if (!owner) {
          var vid = BOOT.variantId || '';
          var root = '/';
          try { root = (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/'; } catch (eR) {}
          buy = {
            text: T.pdfPaywallMsg || 'To download the PDF documentation, please purchase this product first.',
            btn: T.addToCart || 'Add to Cart',
            url: global.location.origin + root + (vid ? ('cart/add?id=' + vid + '&quantity=1') : ('products/' + (BOOT.productHandle || 'firewood-rack')))
          };
        }
        await X.socialGate({ key: _pdfKey(), paid: owner, buy: buy });
      }

      var t0 = Date.now();
      var okDoc = false;
      try { okDoc = await RackDoc.prepare(); } catch (eP) { okDoc = false; LOG('drawings prepare FAILED ' + ((eP && eP.message) || eP)); }
      var boq = buildBoq(bridge);
      var cutList = buildCutList(bridge);
      var shop = [];
      try { shop = buildShopping(bridge, boq); } catch (eS) { LOG('shopping FAILED ' + ((eS && eS.message) || eS)); }
      var totals = buildTotals(boq);
      var pages = okDoc ? RackDoc.pages() : [];
      _docNote = 'units ' + (STATE.isMetric ? 'metric' : 'imperial') + ', ' + pages.length + ' drawing sheet(s), ' + cutList.length + ' cut group(s), ' +
        boq.length + ' bill row(s), ' + shop.length + ' shopping row(s), owner ' + owner + ', package ' + pkg + ', ' + (Date.now() - t0) + ' ms -- ' + RackDoc.note();
      LOG(_docNote);

      var res = await X.export({
        productTitle: BOOT.productTitle || 'Firewood Rack',
        customerName: BOOT.customerName || null,
        coverImage:   BOOT.coverImage   || null,
        shareUrl:     buildShareUrl(),
        totals:       totals,
        customPages:  pages,
        customFirst:  true,
        cutList:      cutList,
        boq:          boq,
        shoppingList: shop,
        meta: {
          locale:        BOOT.locale         || 'en',
          productHandle: BOOT.productHandle  || 'firewood-rack',
          variantId:     BOOT.variantId      || null,
          isMetric:      !!STATE.isMetric,
          date:          new Date(),
          i18n:          {
            pageCutListTitle:T.pdfPageCutListTitle|| 'Cut list',
            pageBoqTitle:    T.pdfPageBoqTitle    || 'Bill of quantities',
            continued:       T.pdfContinued      || 'continued',
            totalsTitle:     T.pdfTotalsTitle    || 'Project totals',
            materialsLabel:  T.pdfMaterials      || 'Materials',
            executionLabel:  T.pdfExecution      || 'Execution',
            grandTotalLabel: T.pdfGrandTotal     || 'Grand total',
            pcs:             T.pcs               || 'pcs',
            widget: {
              total:        T.widgetTotal    || T.total       || 'Total',
              material:     T.widgetMaterial || T.material    || 'Material',
              execution:    T.widgetExecution|| T.execution   || 'Execution',
              capacity:     T.rackWoodCapacity|| 'Wood capacity',
              capacityNote: T.rackWoodFillNote|| 'at 80% fill'
            },
            cutList: {
              headerPart:   T.pdfCutListPart   || 'Part',
              headerLength: T.pdfCutListLength || 'Length',
              headerQty:    T.pdfCutListQty    || 'Qty',
              headerId:     T.pdfCutListId     || 'ID',
              headerDesc:   T.pdfCutListDesc   || 'Description'
            },
            boq: {
              headerCode:      T.pdfBoqCode      || 'Code',
              headerName:      T.pdfBoqName      || 'Name',
              headerQty:       T.pdfBoqQty       || 'Quantity',
              headerUnitPrice: T.pdfBoqUnitPrice || 'Unit price',
              headerTotal:     T.pdfBoqTotal     || 'Total',
              grandTotal:      T.pdfGrandTotal   || 'Grand total'
            }
          }
        }
      });
      LOG('export ' + (res && res.package ? ('package ' + res.package) : (res && res.blocked ? 'blocked (' + res.reason + ')' : 'saved here')));
      return res;
    } catch (e) {
      console.error('[exportPdf] failed', e);
      LOG('FAILED ' + ((e && e.message) || e));
      alert((T.pdfFailed || 'PDF export failed') + ': ' + (e && e.message ? e.message : e));
    } finally {
      if (btn) btn.classList.remove('active');
    }
  }

  /* Render the rack scene from 4 dedicated PDF cameras and snapshot
     each. Does NOT touch STATE or applyVizStage — the live canvas
     gets temporarily repurposed (rendered into with custom cameras)
     but no DOM mutations happen, so the user sees at most a brief
     flicker that lasts ~200 ms while we cycle through the 4 views.

     Each snapshot includes the camera params used to render it, so
     the PDF exporter can project world-space rack corners back to
     pixel coordinates to draw dim lines on top.

     ISO uses the same WebGL renderer as the live view (with
     preserveDrawingBuffer: true). ORTHO uses temporary cameras
     fitted to the rack bbox for clean elevation drawings. */
  /* Render the rack scene through Three.js SVGRenderer from 4 fixed
     cameras and serialize each rendered <svg> element to an SVG
     markup string. Pure vector output: small file size, infinite
     zoom sharpness, no canvas / WebGL involvement at all.

     The live WebGL renderer is NOT touched — we instantiate a
     separate SVGRenderer just for the export, run it, dispose of
     its DOM. No flicker, no STATE mutation.

     If SVGRenderer fails to load (window.SVGRenderer not exposed
     from the boot module) we return an empty array — the PDF
     exporter then falls back to its instances-based vector
     reconstruction. */
  async function captureForPdf() {
    var T = global.THREE;
    var eng = global.RackEngine;
    if (!eng || !eng.scene) return [];
    if (!global.SVGRenderer) {
      console.warn('[exportPdf] SVGRenderer not loaded — skipping snapshots');
      return [];
    }

    var W = STATE.W, L = STATE.L, H = STATE.H;

    /* Real rendered bounding box (roof + overhangs included) so the
       camera frames the WHOLE rack — framing on STATE.H alone clipped
       the roof on some configs. We KEEP the L/H (front) and W/H (side)
       aspect ratios so the PDF box sizing (which uses the structural
       rackDims L/W/H) still matches the image with no distortion; the
       frustum is just expanded enough to contain the real bbox. Logs +
       person are excluded so the frame stays on the rack structure. */
    var bbMin = null, bbMax = null;
    try {
      eng.scene.traverse(function (obj) {
        if (!obj.isMesh || !obj.geometry || !obj.visible) return;
        if (!obj.userData || !obj.userData.partType) return;
        if (obj.userData.partType === 'logs' || obj.userData.partType === 'person') return;
        obj.updateMatrixWorld(true);
        if (!obj.geometry.boundingBox) obj.geometry.computeBoundingBox();
        var bb = obj.geometry.boundingBox.clone().applyMatrix4(obj.matrixWorld);
        if (!bbMin) { bbMin = bb.min.clone(); bbMax = bb.max.clone(); }
        else { bbMin.min(bb.min); bbMax.max(bb.max); }
      });
    } catch (e) { bbMin = null; }

    var xExt, yExt, zExt, cX, cY, cZ;
    if (bbMin && bbMax) {
      xExt = Math.max(0.01, bbMax.x - bbMin.x);   /* depth  (side horizontal)  */
      yExt = Math.max(0.01, bbMax.y - bbMin.y);   /* height (vertical)         */
      zExt = Math.max(0.01, bbMax.z - bbMin.z);   /* length (front horizontal) */
      cX = (bbMin.x + bbMax.x) / 2;
      cY = (bbMin.y + bbMax.y) / 2;
      cZ = (bbMin.z + bbMax.z) / 2;
    } else {
      xExt = W; yExt = H; zExt = L; cX = 0; cY = H / 2; cZ = 0;
    }

    var bbMargin = 1.06;
    /* FRONT (Z horizontal = L, Y vertical = H) — preserve aspect L/H. */
    var frontHalfH = Math.max(yExt / 2, (zExt / 2) * (H / L)) * bbMargin;
    var frontHalfW = frontHalfH * (L / H);
    /* SIDE (X horizontal = W, Y vertical = H) — preserve aspect W/H. */
    var sideHalfH = Math.max(yExt / 2, (xExt / 2) * (H / W)) * bbMargin;
    var sideHalfW = sideHalfH * (W / H);

    /* Two ortho captures only (front + side). The 3D iso page was
       dropped from the PDF — full-quality 3D lives on the product
       page (linked from PDF via QR code) and the rasterised
       snapshot was always clipping at edges due to overhangs,
       multi-mesh geometry, and renderer-canvas size mismatches. */
    var captures = [
      {
        /* FRONT VIEW (nárys) in LEVA convention — looking ALONG the
           rack's short axis (X), so we see L × H. Camera sits on
           the +X side. */
        key:   'front',
        label: 'Front view',
        aspect: L / H,
        /* World dimensions the snapshot encodes — PDF uses these
           for the dynamic-layout sizing (cells proportional to
           world widths). */
        worldW: L,
        worldH: H,
        camera: function () {
          var cam = new T.OrthographicCamera(-frontHalfW, frontHalfW, frontHalfH, -frontHalfH, 0.1, 200);
          cam.position.set(50, cY, cZ);
          cam.lookAt(cX, cY, cZ);
          cam.updateProjectionMatrix();
          return cam;
        }
      },
      {
        /* SIDE VIEW (bokorys) in LEVA convention — looking ALONG
           the rack's long axis (Z), so we see W × H. Camera sits
           on the +Z side. */
        key:   'side',
        label: 'Side view',
        aspect: W / H,
        worldW: W,
        worldH: H,
        camera: function () {
          var cam = new T.OrthographicCamera(-sideHalfW, sideHalfW, sideHalfH, -sideHalfH, 0.1, 200);
          cam.position.set(cX, cY, 50);
          cam.lookAt(cX, cY, cZ);
          cam.updateProjectionMatrix();
          return cam;
        }
      }
    ];

    /* Build a parallel "engineering drawing" scene from the live
       rack scene's meshes. For each mesh we add:
         • A white-filled clone (same geometry, MeshBasicMaterial
           white) — paints over anything behind in the painter's
           order, hiding back-side edges.
         • A black EdgesGeometry overlay (LineSegments) with
           thresholdAngle ~20° so only sharp silhouette + corner
           edges remain. Triangulation noise inside flat panels is
           filtered out.
       Result is a clean technical drawing: front-side outlines on
       white, no fill colours, no fragmented triangle edges. */
    /* Per-capture HLR scenes. ISO uses the original mesh materials
       (colored wood, dark roof) plus black edge overlays — gives a
       readable 3D feel while still drawing crisp outlines. ORTHO
       views use pure white fills + black edges (classic engineering
       drawing where colour would just be noise). */

    /* Shared edge material — thin black lines. */
    var hlrLine = new T.LineBasicMaterial({ color: 0x1a1a1a });
    /* Shared white material for ortho fills. */
    var hlrWhite = new T.MeshBasicMaterial({
      color:    0xffffff,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits:  1
    });

    /* Build the HLR scene used by ortho captures only — iso now
       uses the live WebGL renderer directly with full materials &
       lighting, so we don't need a colored variant any more. */
    function buildFlatScene() {
      var s = new T.Scene();
      s.background = new T.Color(0xffffff);
      eng.scene.traverse(function (obj) {
        if (!obj.isMesh || !obj.geometry || !obj.visible) return;
        if (!obj.userData || !obj.userData.partType) return;
        if (obj.userData.partType === 'logs') return;

        /* White fill — hides back-side geometry via painter's order. */
        var fillMesh = new T.Mesh(obj.geometry, hlrWhite);
        obj.updateMatrixWorld(true);
        fillMesh.matrixAutoUpdate = false;
        fillMesh.matrix.copy(obj.matrixWorld);
        s.add(fillMesh);

        /* Black edge overlay (sharp edges only). */
        try {
          var edges = new T.EdgesGeometry(obj.geometry, 20);
          var lines = new T.LineSegments(edges, hlrLine);
          lines.matrixAutoUpdate = false;
          lines.matrix.copy(obj.matrixWorld);
          s.add(lines);
        } catch (e) {}
      });
      return s;
    }

    var hlrSceneFlat = buildFlatScene();

    /* One SVG renderer, reused per capture. setSize controls the
       viewBox dimensions on its <svg> domElement. */
    var svgRenderer = new global.SVGRenderer();
    svgRenderer.setQuality('high');
    /* Use the SVG element off-DOM — never attached, no layout
       interaction with the live page. */
    var out = [];
    for (var i = 0; i < captures.length; i++) {
      var c = captures[i];
      /* Render each view at a generous logical size — SVG is
         resolution-independent, so this only affects coordinate
         precision, not file size. Keep ratio sensible per camera. */
      /* Render each ortho at A4-page-level resolution. SVG is
         resolution-independent in principle but jsPDF rasterises
         it during embed, so the underlying viewBox size affects
         line crispness at print. 2400 px ≈ 8.4 inches at 280 DPI,
         which prints sharp on A4 landscape. */
      var longEdge = 2400;
      var w, h;
      if (c.aspect >= 1) { w = longEdge; h = Math.round(longEdge / c.aspect); }
      else                { h = longEdge; w = Math.round(longEdge * c.aspect); }

      try {
        /* All captures are ortho line drawings. */
        svgRenderer.setSize(w, h, false);
        var cam = c.camera();
        svgRenderer.render(hlrSceneFlat, cam);

        /* Serialize the rendered <svg> element to a markup string.
           SVGRenderer paints into its own DOM element (svgRenderer.domElement),
           which is a real <svg> we can serialize directly. */
        var svgEl = svgRenderer.domElement;
        var svgMarkup = new XMLSerializer().serializeToString(svgEl);

        /* Ensure xmlns is present — XMLSerializer sometimes drops it
           on documents that were never attached. */
        if (svgMarkup.indexOf('xmlns="http://www.w3.org/2000/svg"') === -1) {
          svgMarkup = svgMarkup.replace('<svg ',
            '<svg xmlns="http://www.w3.org/2000/svg" ');
        }

        /* White fills + black edges line-art post-process. */
        svgMarkup = postProcessSvgToLineArt(svgMarkup, 'flat');

        out.push({
          key:    c.key,
          label:  c.label,
          svg:    svgMarkup,
          width:  w,
          height: h,
          /* Camera matrices for vector dim overlays in the PDF
             (same projection PDF exporter will use). */
          projectionMatrix: Array.from(cam.projectionMatrix.elements),
          viewMatrix:       Array.from(cam.matrixWorldInverse.elements)
        });
      } catch (e) {
        console.warn('[exportPdf] snapshot failed for', c.key, e);
        out.push({ key: c.key, label: c.label, svg: null });
      }
    }

    return out;
  }

  /* Convert SVGRenderer output into a clean technical drawing:
     • Strip all fills (no solid wood colours)
     • Force every path to a thin black stroke
     • Remove any colour-coded inline styles that conflict
     This is purely a string transform — no DOM, no parser. */
  /* Post-process the SVG produced by Three.js SVGRenderer into a
     clean technical drawing:

       • <path style="fill:rgb(255,255,255)..."> — white mesh fills
         from the HLR scene. Keep the white fill (it overpaints
         hidden geometry behind it) but force stroke:none so no
         outline appears around each triangle (those edges come
         from the separate EdgesGeometry layer).

       • <line> / <path> with `stroke:rgb(...)` and no fill — edge
         segments emitted by SVGRenderer for LineSegments objects.
         Force stroke to thin black.

     Goal: triangles inside flat panels disappear into white fills,
     and only the EdgesGeometry-derived sharp edges show through
     as crisp black lines. */
  /* Post-process SVG output from Three.js SVGRenderer into a clean
     drawing. Two modes:

     `mode === 'flat'`  (ortho engineering views):
        – Mesh fills → solid white (overpaints hidden geom behind).
        – Mesh strokes → none (no triangulation outlines).
        – LineSegments → thin black stroke.

     `mode === 'colored'` (iso view):
        – Mesh fills → preserved (original wood / roof colours).
        – Mesh strokes → none.
        – LineSegments → thin black stroke (overlay outlines on top
          of the colored fill).
  */

  function postProcessSvgToLineArt(svg, mode) {
    var coloured = (mode === 'colored');

    if (!coloured) {
      /* Flat / white mode — flatten every mesh fill to white. */
      svg = svg.replace(/fill\s*:\s*rgb\(\s*255\s*,\s*255\s*,\s*255\s*\)/gi, 'fill:#ffffff');
      svg = svg.replace(/fill\s*:\s*rgb\([^)]+\)/g, 'fill:#ffffff');
      svg = svg.replace(/fill="rgb\([^)]+\)"/g, 'fill="#ffffff"');
    }
    /* In colored mode we keep `fill:rgb(...)` as-is. */

    /* For all <path>/<polygon> with a fill, kill stroke so we don't
       outline each individual triangle of a flat panel. The crisp
       outlines come from the separate EdgesGeometry layer. */
    svg = svg.replace(/(<(?:path|polygon)[^>]*?style=")([^"]*)"/g,
      function (m, prefix, style) {
        if (/fill\s*:\s*(?!none)/.test(style)) {
          style = style.replace(/stroke\s*:\s*[^;"]+;?/g, '');
          style = style.replace(/stroke-width\s*:\s*[^;"]+;?/g, '');
          style = style.replace(/stroke-opacity\s*:\s*[^;"]+;?/g, '');
          if (!style.endsWith(';') && style.length) style += ';';
          style += 'stroke:none';
        } else {
          /* No fill → line. Force thin black. */
          style = style.replace(/stroke\s*:\s*[^;"]+/g, 'stroke:#1a1a1a');
          style = style.replace(/stroke-width\s*:\s*[^;"]+/g, 'stroke-width:1');
          if (!/stroke\s*:/.test(style)) {
            if (style.length && !style.endsWith(';')) style += ';';
            style += 'stroke:#1a1a1a;stroke-width:1;fill:none';
          }
        }
        return prefix + style + '"';
      });

    /* Bare <line> elements from LineSegments — thin black. */
    svg = svg.replace(/<line\b([^>]*?)\/>/g, function (m, attrs) {
      attrs = attrs.replace(/\sstroke="[^"]*"/g, '');
      attrs = attrs.replace(/\sstroke-width="[^"]*"/g, '');
      return '<line' + attrs + ' stroke="#1a1a1a" stroke-width="1.2"/>';
    });

    return svg;
  }

  /* Collect dimension annotations the PDF exporter overlays on
     the snapshots. Medium density:
       • Outer L, W, H
       • Post heights (front + back) — useful for fabrication
       • Decking board count
     Values stay in metres internally; the PDF formats with the unit
     system the user chose (STATE.isMetric). */
  function buildRackDimensions() {
    var b = global.RackBoQ && global.RackBoQ.compute ? global.RackBoQ.compute() : {};
    /* Front + back post heights come from DIMS / constants in
       rack-lib. We re-derive from STATE rather than reading mesh
       lengths so we get the user-visible values (which may differ
       by a few mm from rendered geometry due to roof slope). */
    var Hfront, Hback;
    try {
      Hfront = STATE.H;
      Hback  = STATE.H - STATE.W * 0.18;   /* matches DIMS.roof slope */
      if (!isFinite(Hback) || Hback <= 0) Hback = STATE.H * 0.85;
    } catch (e) { Hfront = STATE.H; Hback = STATE.H * 0.85; }

    /* Roof + rafter geometry — the roof boards run from the back rail
       (lower, at Hback) to the front rail (higher, at Hfront), with
       a small overhang on the front and side overhangs on the roof.
       The PDF side view annotates BOTH lengths so the user knows
       how long to cut the rafter lumber AND the roofing sheet:

         Rafter (KVH 60×100):  back rail outer → front rail + extension
           horizontalSpan = W + 2*tw + rafterExtension
           verticalRise   = Hfront - Hback
           length         = sqrt(h² + v²)

         Roof (corrugated sheet): wider — adds overhangX both ends
           horizontalSpan_roof = rafter span + 2 * overhangX
           length_roof         = sqrt(h_roof² + v_roof²)
       
       Both share the same slope angle. Endpoints are returned in
       world coords (x = sideways in side view, y = up, z = unused)
       so the PDF can project and draw the dim lines. */
    var tw = (DIMS && typeof DIMS.tw === 'number') ? DIMS.tw : 0.06;
    var rafterExtension = 0.15;
    var overhangX = 0.05;
    var rafterHorizontalSpan = STATE.W + 2 * tw + rafterExtension;
    var rafterVerticalRise   = Hfront - Hback;
    var rafterLength = Math.sqrt(
      rafterHorizontalSpan * rafterHorizontalSpan +
      rafterVerticalRise   * rafterVerticalRise
    );
    var slopeRatio = rafterHorizontalSpan > 0 ? rafterVerticalRise / rafterHorizontalSpan : 0;
    var rafterAngleDeg = Math.atan2(rafterVerticalRise, rafterHorizontalSpan) * 180 / Math.PI;

    /* Rafter endpoints — at the rail tops, extending front overhang.
       Back is at (-W/2 - tw, Hback). Front is at (+W/2 + tw + ext, Hfront). */
    var rafterBackPt  = { x: -(STATE.W / 2 + tw),                    y: Hback };
    var rafterFrontPt = { x:  (STATE.W / 2 + tw + rafterExtension),  y: Hfront };

    /* Roof endpoints — extended by overhangX along the rafter axis on
       both sides. The overhang follows the slope so Y shifts a bit too. */
    var roofBackPt  = {
      x: rafterBackPt.x  - overhangX,
      y: rafterBackPt.y  - overhangX * slopeRatio
    };
    var roofFrontPt = {
      x: rafterFrontPt.x + overhangX,
      y: rafterFrontPt.y + overhangX * slopeRatio
    };
    var roofHorizontalSpan = roofFrontPt.x - roofBackPt.x;
    var roofVerticalRise   = roofFrontPt.y - roofBackPt.y;
    var roofLength = Math.sqrt(
      roofHorizontalSpan * roofHorizontalSpan +
      roofVerticalRise   * roofVerticalRise
    );

    return {
      L: STATE.L,
      W: STATE.W,
      H: STATE.H,
      isMetric: STATE.isMetric,
      postFront: Hfront,
      postBack:  Hback,
      rafterLength:         rafterLength,
      rafterHorizontalSpan: rafterHorizontalSpan,
      rafterVerticalRise:   rafterVerticalRise,
      rafterAngleDeg:       rafterAngleDeg,
      rafterExtension:      rafterExtension,
      rafterBackPt:         rafterBackPt,
      rafterFrontPt:        rafterFrontPt,
      roofLength:           roofLength,
      roofBackPt:           roofBackPt,
      roofFrontPt:          roofFrontPt,
      overhangX:            overhangX,
      deckingCount: (b && typeof b.decking_count === 'number') ? b.decking_count : null,
      rafterCount:  (b && typeof b.rafter_count  === 'number') ? b.rafter_count  : null
    };
  }

  /* Build the instances payload the PDF exporter needs. We pull the
     real box geometry (width/height/depth) and world-space rotation
     from each tracked mesh, so the PDF renderer can draw oriented
     bounding boxes instead of guessing axis-aligned ones (which
     produced the garbled iso view on Marek's first PDF). */
  function buildInstancePayload() {
    if (!global.RackInstances || typeof global.RackInstances.all !== 'function') return [];
    var THREE = global.THREE;
    return global.RackInstances.all().map(function (inst) {
      var mesh = inst.mesh;
      var dims = null;
      var matrix = null;

      if (mesh && mesh.geometry && mesh.geometry.parameters) {
        var p = mesh.geometry.parameters;
        if (typeof p.width === 'number') {
          /* BoxGeometry case — the rack uses BoxGeometry exclusively. */
          dims = { w: p.width, h: p.height, d: p.depth };
        }
      }

      if (mesh && THREE) {
        /* Ensure world matrix is up to date (parent rackGroup may have
           had transforms applied). */
        try { mesh.updateMatrixWorld(true); } catch (e) {}
        /* Send the world matrix as a 16-float array. The exporter
           reconstructs the orientation by applying it to the 8
           corners of a unit cube scaled by (w, h, d). */
        if (mesh.matrixWorld && mesh.matrixWorld.elements) {
          matrix = Array.from(mesh.matrixWorld.elements);
        }
      }

      var worldPos = inst.position;
      if (mesh && mesh.getWorldPosition && THREE) {
        try {
          var v = new THREE.Vector3();
          mesh.getWorldPosition(v);
          worldPos = { x: v.x, y: v.y, z: v.z };
        } catch (e) {}
      }

      return {
        id:       inst.id,
        partType: inst.partType,
        boqCode:  inst.boqCode,
        length:   inst.length,
        position: worldPos
                  ? { x: worldPos.x, y: worldPos.y, z: worldPos.z }
                  : null,
        /* Real geometry box dims (m). null if mesh isn't a BoxGeometry. */
        dims:     dims,
        /* 4×4 world transform as flat array (column-major, Three.js
           native order). Lets the exporter rotate corners exactly
           the way they sit in the scene. */
        matrix:   matrix
      };
    }).filter(function (i) { return i.position; });
  }

  /* Pre-resolve every BoQ code in the model to a localized name so
     the PDF doesn't need to re-call the bridge per instance. */
  function buildBoqNamesMap(bridge) {
    var map = {};
    if (!global.RackInstances || typeof global.RackInstances.all !== 'function') return map;
    var seen = {};
    var missing = [];
    global.RackInstances.all().forEach(function (inst) {
      if (!inst.boqCode || seen[inst.boqCode]) return;
      seen[inst.boqCode] = true;
      var item = bridge && bridge.getBoqItem ? bridge.getBoqItem(inst.boqCode) : null;
      if (!item || !item.name) {
        /* Track unresolved codes so we can surface them in the
           console — easier than asking the user "why does the PDF
           show KVH10060 instead of '2×4 Lumber'". The export still
           proceeds with the bare code as a fallback, but at least
           Marek sees what's missing in the central BoQ sheet. */
        missing.push(inst.boqCode);
      }
      map[inst.boqCode] = (item && item.name) || inst.boqCode;
    });
    if (missing.length && global.console && global.console.warn) {
      console.warn('[exportPdf] BoQ codes without resolved names (will show as IDs):',
        missing.join(', '));
    }
    return map;
  }

  /* Group all selectable RackInstances by BoQ code. Within each
     group, AGGREGATE pieces by length: identical-length cuts of the
     same profile collapse to one row with a qty count. A typical
     rack has 8 identical rafters → one "1.17 m × 8 ks" row instead
     of eight separate rows.

     CUT LIST is a workshop document — only lumber profiles belong here.
     Roofing (ROR*) and fasteners (FAS*) are excluded; they still
     appear in the BoQ totals on the last page. */
  /* Nominal cross-sections for each profile family used in the rack.
     Mesh geometries don't reliably expose box dims for tracking, so
     for the visual cut list we hardcode the canonical product
     dimensions. width × thickness in METRES (always). The visual PDF
     page draws each piece as a rectangle whose proportions match
     these dimensions (so a 60×100 KVH chunky piece visually differs
     from a 20×40 KVL batten). */
  var CUT_LIST_PROFILES = {
    'KVH10060':  { width: 0.060, thickness: 0.100, family: 'lumber'   },
    'KVL20040':  { width: 0.020, thickness: 0.040, family: 'lumber'   },
    'TST00001':  { width: 0.150, thickness: 0.028, family: 'decking'  },
    'ROR12004':  { width: 0.660, thickness: 0.004, family: 'roof'     }
  };

  /* Metric→imperial dimension tokens per timber code. When the BoQ name
     carries the metric cross-section and units are imperial, swap it for
     the imperial size so the cut list never shows mm on an imperial doc.
     Pure ASCII ("in.") — jsPDF base fonts render the ″ prime as "?". */
  var CUT_LIST_DIM = {
    'KVH10060': { metric: '60 × 100', imperial: '2 in. x 4 in.' },
    'KVL20040': { metric: '20 × 40',  imperial: '1 in. x 2 in.' },
    'TST00001': { metric: '25 × 152', imperial: '1 in. x 6 in.' }
  };

  function _swapDimToken(name, metricDim, imperialDim) {
    if (!name) return name;
    var esc = function (s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); };
    var core = String(metricDim).replace(/\s*(mm|cm|m)\s*$/i, '').trim();
    var pat = esc(core).replace(/[×x]/gi, '[×x]').replace(/\s+/g, '\\s*');
    var re = new RegExp(pat + '(\\s*(?:mm|cm|m))?', 'i');
    if (re.test(name)) {
      return name.replace(re, imperialDim).replace(/\s+/g, ' ').trim();
    }
    return (name + ' ' + imperialDim).replace(/\s+/g, ' ').trim();
  }

  function _cutListProfileName(code, rawName) {
    var name = rawName || code;
    var dm = CUT_LIST_DIM[code];
    if (!STATE.isMetric && dm) name = _swapDimToken(name, dm.metric, dm.imperial);
    return name;
  }

  /* THE CUT LIST IN THE UNITS A SAW READS (rack-v10-docs). It was decimal
     feet -- "5.91 ft" -- which the buyer of Matt's plans rightly called
     useless. Now millimetres in metric and inches to 1/16 in imperial (the
     exporter prints them as feet-inches, 6'-0" / 5'-11 15/16"), the profile
     sizes from the model's own lumber (US sizes in imperial), and the roof
     sheet sized from the same numbers the model is built from. */
  function buildCutList(bridge) {
    if (!global.RackInstances || typeof global.RackInstances.all !== 'function') return [];
    var all = global.RackInstances.all() || [];
    var groups = {};
    var order  = [];
    var met = !!STATE.isMetric;
    var K = met ? 1000 : (1 / IN_M);                  /* metres -> display units */
    function disp(m) { return met ? Math.round(m * 1000) : Math.round(m / IN_M * 16) / 16; }
    var PROF = {
      'KVH10060': { width: DIMS.twoByFour.w, thickness: DIMS.twoByFour.h, family: 'lumber'  },
      'KVL20040': { width: DIMS.oneByTwo.w,  thickness: DIMS.oneByTwo.h,  family: 'lumber'  },
      'TST00001': { width: DIMS.decking.h,   thickness: DIMS.decking.w,   family: 'decking' },
      'ROR12004': { width: 0.660, thickness: 0.004, family: 'roof' }
    };

    function ensureGroup(code) {
      if (groups[code]) return groups[code];
      var item = bridge && bridge.getBoqItem ? bridge.getBoqItem(code) : null;
      var prof = PROF[code] || CUT_LIST_PROFILES[code] || {};
      groups[code] = {
        profile:       _cutListProfileName(code, item && item.name),
        profileCode:   code,
        unit:          met ? 'mm' : 'in',
        profileWidth:  prof.width || null,        /* metres, narrow dim */
        profileHeight: prof.thickness || null,    /* metres, deep dim   */
        family:        prof.family || 'lumber',   /* lumber | decking | roof */
        pieces:      [],
        _byLen:      {}
      };
      order.push(code);
      return groups[code];
    }

    /* ROOF -- the surface to cover, one rectangle: across the slope the
       sheet is as wide as the model's sheet (rafter span + both side
       overhangs), along the rack its length plus both end overhangs. */
    var tw = DIMS.twoByFour.w;
    var roofXSpan = STATE.W + 2 * tw + DIMS.rafterExt + 2 * DIMS.roofOverX;
    var roofZSpan = STATE.L + 2 * DIMS.roofOverZ;
    var roofGroup = ensureGroup('ROR12004');
    var xSpanIsLength = roofXSpan >= roofZSpan;
    roofGroup.pieces.push({
      length: disp(Math.max(roofXSpan, roofZSpan)),
      width:  disp(Math.min(roofXSpan, roofZSpan)),
      qty:    1,
      label:  'roof',
      suppressQty: true,
      slopeAxis:   xSpanIsLength ? 'horizontal' : 'vertical'
    });

    all.forEach(function (inst) {
      if (!inst || !inst.boqCode) return;
      if (/^FAS/i.test(inst.boqCode)) return;
      if (/screw|fastener|nail/i.test(inst.partType || '')) return;
      if (inst.boqCode === 'ROR12004') return;
      var g = ensureGroup(inst.boqCode);
      var len = disp((typeof inst.length === 'number') ? inst.length : 0);
      var cutAngle = (typeof inst.angle === 'number') ? inst.angle : 0;
      var key = len + '|' + cutAngle;
      var bucket = g._byLen[key];
      if (!bucket) {
        bucket = { length: len, width: null, qty: 0, label: inst.partType || '', angle: cutAngle };
        g._byLen[key] = bucket;
        g.pieces.push(bucket);
      }
      bucket.qty += 1;
    });

    order.forEach(function (k) {
      var g = groups[k];
      if (g.family !== 'roof' && g.profileWidth) {
        var wDisp = met ? Math.round(g.profileWidth * K) : Math.round(g.profileWidth * K * 16) / 16;
        g.pieces.forEach(function (p) { if (p.width == null) p.width = wDisp; });
      }
      g.pieces.sort(function (a, b) { return b.length - a.length; });
      delete g._byLen;
    });
    return order.map(function (k) { return groups[k]; });
  }

  /* Per-material BoQ totals — exactly what RackBoQ.compute().byMaterial
     contains, enriched with localized names and pricing from the bridge. */
  function buildBoq(bridge) {
    var b = global.RackBoQ.compute();
    var out = [];
    Object.keys(b.byMaterial).forEach(function (code) {
      var qty = b.byMaterial[code];
      if (qty == null) return;
      var item = bridge && bridge.getBoqItem ? bridge.getBoqItem(code) : null;
      var localUnit = item ? (STATE.isMetric ? item.unitMetric : item.unitImperial) : null;
      /* Convert qty for display if needed. RackBoQ.byMaterial holds
         metric values; convert linear/area to imperial when STATE
         says so. PCS stays as-is. */
      var displayQty = qty;
      if (item && !STATE.isMetric) {
        var lbl = String(item.unitMetric || '').trim().toUpperCase();
        if      (lbl === 'M')  displayQty = qty * 3.281;
        else if (lbl === 'M2') displayQty = qty * 10.764;
        else if (lbl === 'M3') displayQty = qty * (String(item.unitImperial || '').trim().toUpperCase() === 'YD3' ? 1.30795 : 35.315);
      }
      var userPrice = bridge && bridge.getUserPrice ? bridge.getUserPrice(code) : null;
      var unitPrice = userPrice && typeof userPrice.price === 'number' ? userPrice.price : null;
      var total = (unitPrice != null) ? unitPrice * displayQty : null;
      out.push({
        code:      code,
        name:      (item && item.name) || code,
        qty:       displayQty,
        unit:      localUnit || (STATE.isMetric ? 'm' : 'ft'),
        unitPrice: unitPrice,
        total:     total
      });
    });
    return out;
  }

  /* Build the totals payload for the PDF cover. Strategy: read the
     exact formatted strings the live on-page budget widget is
     showing right now. Those values come from
     `[data-budget-total-*]` elements (populated by budget.liquid)
     OR fall back to the configurator's numerical globals
     (window.lastMaterialsTotal / lastDeliveryTotal). The PDF
     widget renders these strings verbatim, so what the user sees
     on-page is what they see in the PDF — no parsing, no rounding
     drift, no localisation mismatch.

     Wood capacity is computed locally (same logic as
     cf-bw-capacity refresh in rack-liquid). */
  function buildTotals(boqRows) {
    /* Pre-formatted strings — the cover-page widget consumes these
       directly as Strings, not numbers. */
    function $domText(selector) {
      var el = document.querySelector(selector);
      if (!el || !el.textContent) return '';
      var t = el.textContent.trim();
      if (!t || t === '—' || t === '-') return '';
      return t;
    }
    function $idText(id) {
      var el = document.getElementById(id);
      if (!el || !el.textContent) return '';
      var t = el.textContent.trim();
      if (!t || t === '—' || t === '-') return '';
      return t;
    }
    var fmtPrice = (typeof window.formatPrice === 'function')
      ? window.formatPrice
      : function (n) { return Math.round(+n || 0).toLocaleString() + ' ' +
            ((window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD'); };

    /* Primary source: live widget cells (same as on-page popup
       Marek wants mirrored). Two possible widget IDs depending on
       which template painted them. */
    var totalText    = $idText('cf-bw-total');
    var materialText = $idText('cf-bw-material');
    var execText     = $idText('cf-bw-execution');
    var timeText     = $idText('cf-bw-time');
    var capacityText = $idText('cf-bw-capacity');

    /* Secondary source: budget.liquid raw output elements. */
    if (!totalText)    totalText    = $domText('[data-budget-total-cost]');
    if (!materialText) materialText = $domText('[data-budget-total-material]');
    if (!execText)     execText     = $domText('[data-budget-total-execution]');
    if (!timeText)     timeText     = $domText('[data-budget-total-time]');

    /* Tertiary source: window globals (incognito / anonymous flow
       where the DOM elements aren't populated). */
    if (!materialText && typeof window.lastMaterialsTotal === 'number') {
      materialText = fmtPrice(window.lastMaterialsTotal);
    }
    if (!execText && typeof window.lastDeliveryTotal === 'number') {
      execText = fmtPrice(window.lastDeliveryTotal);
    }
    if (!totalText) {
      var mNum = (typeof window.lastMaterialsTotal === 'number') ? window.lastMaterialsTotal : 0;
      var eNum = (typeof window.lastDeliveryTotal  === 'number') ? window.lastDeliveryTotal  : 0;
      if (mNum || eNum) totalText = fmtPrice(mNum + eNum);
    }
    if (!timeText && typeof window.lastDurationTotal === 'number') {
      var mins = window.lastDurationTotal;
      timeText = (mins >= 60)
        ? (Math.round(mins / 60 * 10) / 10) + ' h'
        : Math.round(mins) + ' min';
    }

    /* Last-resort: synthesise materials from boqRows we just built
       (PDF always has these even if widget isn't visible yet). */
    if (!materialText) {
      var matSum = 0;
      boqRows.forEach(function (r) { if (r.total != null) matSum += r.total; });
      if (matSum) materialText = fmtPrice(matSum);
    }
    if (!totalText && materialText) totalText = materialText;

    /* Wood capacity — local computation matching the widget
       (cf-bw-capacity refresh in rack-liquid). Configurator-only
       metric specific to firewood racks. */
    if (!capacityText) {
      try {
        var usefulH = STATE.H - (DIMS.deckHeight || 0.20);
        if (usefulH > 0) {
          var gross_m3 = STATE.W * STATE.L * usefulH;
          var wood_m3  = 0.80 * gross_m3;   /* 80% fill rate, stacked firewood */
          capacityText = STATE.isMetric
            ? wood_m3.toFixed(2) + ' m³'
            : (wood_m3 * 35.3147).toFixed(1) + ' ft³';
        }
      } catch (e) {}
    }

    return {
      /* Live text snapshot — preferred by PDF widget renderer. */
      texts: {
        total:    totalText    || '',
        material: materialText || '',
        execution: execText    || '',
        time:     timeText     || '',
        capacity: capacityText || ''
      },
      /* Legacy numeric shape kept for backward compat with any other
         consumers (other configurators / older PDF code paths). */
      materials: { count: 0, value: 0 },
      execution: { count: 0, value: 0, hours: 0 },
      total:     0,
      currency:  (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD'
    };
  }

  /* Build a URL that re-opens the configurator with the current STATE
     pre-loaded. Falls back to current page URL if the share helper
     isn't available yet (e.g. configurator-share hasn't loaded). */
  function buildShareUrl() {
    if (global.ConfiguratorShare && typeof global.ConfiguratorShare.getShareUrl === 'function') {
      try { return global.ConfiguratorShare.getShareUrl(); } catch (e) {}
    }
    return window.location.href;
  }

  function saveConfig() {
    var T = (global.RK_BOOT && global.RK_BOOT.i18n) || {};
    try {
      localStorage.setItem('rack_config', JSON.stringify({
        L: STATE.L, W: STATE.W, H: STATE.H,
        isMetric: STATE.isMetric,
        view: STATE.view, vizStage: STATE.vizStage,
        ts: Date.now()
      }));
      alert(T.saveOk || 'Configuration saved.');
    } catch (e) {
      alert((T.saveFail || 'Failed to save configuration.') + ' ' + e.message);
    }
  }

  /* savePref — quiet auto-save for preferences that should persist across
     sessions: dimensions, units, view, viz stage. Merges into the existing
     rack_config payload so manual saveConfig() still works. */
  /* THE DESIGN, NOT THE VIEW (rack-v8-pdfkey): the key the PDF is paid for, so the
     same design downloads again for free for 30 days (configurator-pdf.liquid
     pdf-pay-v4, worker leva-ai) */
  function _pdfKey() {
    try { return 'rack|' + JSON.stringify(global.RackBoQ.compute().byMaterial || {}); } catch (e) { return ''; }
  }
  function savePref() {
    try {
      var existing = {};
      try {
        var raw = localStorage.getItem('rack_config');
        if (raw) existing = JSON.parse(raw) || {};
      } catch (e) {}
      existing.L = STATE.L;
      existing.W = STATE.W;
      existing.H = STATE.H;
      existing.dimV = 2;            /* rack-v10-docs: sizes saved on Matt's default */
      existing.isMetric = STATE.isMetric;
      existing.view = STATE.view;
      existing.vizStage = STATE.vizStage;
      existing.opacity2D = STATE.opacity2D;
      existing.ts = Date.now();
      localStorage.setItem('rack_config', JSON.stringify(existing));
    } catch (e) { /* localStorage quota / disabled — silent */ }
  }

  function loadSaved() {
    try {
      var raw = localStorage.getItem('rack_config');
      if (!raw) return;
      var d = JSON.parse(raw);
      /* rack-v10-docs: sizes saved before the default became Matt's original
         (dimV < 2) are dropped once -- the rack opens as he designed it */
      if (d.dimV === 2 && typeof d.L === 'number' && typeof d.W === 'number' && typeof d.H === 'number') {
        STATE.L = d.L; STATE.W = d.W; STATE.H = d.H;
        STATE.__savedDims = true;
      }
      /* isMetric: respect saved value UNLESS the shop is hard-locked to a
         specific unit system (USD-priced stores must be imperial, since
         all prices in the catalog are expressed per imperial unit).
         detectUnits() already returned the shop-mandated value into
         STATE.isMetric at boot — if it's false (USD/imperial), keep it
         and don't let a stale localStorage flip it. */
      var shopForcesImperial = (typeof global.Shopify !== 'undefined') &&
                               global.Shopify.currency &&
                               global.Shopify.currency.active === 'USD';
      if (typeof d.isMetric === 'boolean' && !shopForcesImperial) {
        STATE.isMetric = d.isMetric;
      }
      if (d.view === '3d' || d.view === '2d') STATE.view = d.view;
      if (d.vizStage === 1 || d.vizStage === 2 || d.vizStage === 3) STATE.vizStage = d.vizStage;
      if (typeof d.opacity2D === 'number' && d.opacity2D >= 0 && d.opacity2D <= 0.80) {
        STATE.opacity2D = d.opacity2D;
      }
      /* Clamp to valid range — both 2D and 3D now have up to 3 stages. */
      if (STATE.vizStage > 3) STATE.vizStage = 1;
    } catch (e) {}
  }

  /* PDF is the primary deliverable. Until the central BoQ data has loaded
     (names + prices), an export would be incomplete — so the button stays
     visually disabled (greyed, non-clickable). Once data is ready it is
     promoted to the single RED primary action (white icon) so it stands out. */
  var PDF_BTN_RED = '#D8232A';   /* LEVA red */

  function _initPdfButtonState() {
    var btn = document.getElementById('cf-btn-pdf');
    if (!btn) return;

    if (!document.getElementById('cf-pdf-btn-style')) {
      var st = document.createElement('style');
      st.id = 'cf-pdf-btn-style';
      st.textContent =
        '#cf-btn-pdf.cf-pdf-loading{opacity:.4;pointer-events:none;cursor:default;filter:grayscale(1);}' +
        '#cf-btn-pdf.cf-pdf-ready{background:' + PDF_BTN_RED + ' !important;border-color:' + PDF_BTN_RED + ' !important;color:#fff !important;}' +
        /* White icon regardless of how it is rendered (inline SVG,
           <img>, or a CSS mask / background-image child): a
           brightness(0)+invert(1) filter turns any opaque icon
           pixels into solid white. Applied to the button's children
           so the red background itself is not affected. */
        '#cf-btn-pdf.cf-pdf-ready > *{filter:brightness(0) invert(1) !important;}';
      document.head.appendChild(st);
    }

    var bridge = global.ConfiguratorBudgetBridge || null;
    function isReady() {
      return !(bridge && typeof bridge.isReady === 'function') || bridge.isReady();
    }
    function promote() {
      btn.classList.remove('cf-pdf-loading');
      btn.classList.add('cf-pdf-ready');
    }

    if (isReady()) { promote(); return; }

    btn.classList.add('cf-pdf-loading');
    if (bridge && typeof bridge.onReady === 'function') {
      try { bridge.onReady(promote); } catch (e) {}
    }
    var waited = 0;
    var iv = setInterval(function () {
      if (isReady()) { clearInterval(iv); promote(); }
      else if ((waited += 300) >= 30000) { clearInterval(iv); promote(); }  /* fail-open after 30 s */
    }, 300);
  }

  /* ───────────────────────── AR (View in your space) ──────────────
     Ported from the foundations (base) configurator. Click → export the
     current rack Three.js model as a GLB → upload to R2 via the
     Cloudflare Worker → show a QR pointing at the <model-viewer> page.
     Phone scans QR → "View in AR" drops the bench into the room.

     Flow:
       1. GET  {worker}/upload-url   → { uuid, uploadUrl, viewerUrl }
       2. GLTFExporter.parse(bench)  → ArrayBuffer (GLB binary)
       3. PUT  {uploadUrl} body=glb  → R2 stores {uuid}.glb
       4. render QR(viewerUrl)

     SECTION PREREQUISITES (same three the base section already has):
       • RK_BOOT.arWorkerUrl  — the Worker base URL (copy the value
         from the foundations section; identical worker).
       • the GLTFExporter script must be loaded (window.GLTFExporter).
       • the QRCode script (already loaded for the PDF export).
     If any are missing the modal shows a clear error instead of failing
     silently. The AR button + modal markup are injected by THIS lib, so
     no section HTML change is needed. ───────────────────────────────── */
  function _setArStatus(msg) {
    var el = document.getElementById('cf-ar-modal-status');
    if (el) el.textContent = msg || '';
  }

  function _setArQR(dataUrl) {
    var el = document.getElementById('cf-ar-modal-qr');
    if (!el) return;
    el.innerHTML = '';
    if (!dataUrl) return;
    var img = document.createElement('img');
    img.src = dataUrl;
    img.alt = 'AR QR code';
    img.style.width = '200px';
    img.style.height = '200px';
    img.style.borderRadius = '8px';
    img.style.background = '#fff';
    img.style.padding = '8px';
    el.appendChild(img);
  }

  function _setArLink(viewerUrl) {
    var el = document.getElementById('cf-ar-modal-link');
    if (!el) return;
    if (viewerUrl) {
      el.style.display = 'block';
      el.innerHTML = '<a href="' + viewerUrl + '" target="_blank" rel="noopener noreferrer" style="color:inherit;text-decoration:underline;">' + viewerUrl + '</a>';
    } else {
      el.style.display = 'none';
      el.innerHTML = '';
    }
  }

  function _arExportScene() {
    /* Build a clean GLB from the current rack model. Export the meshes
       in RackEngine.rackGroup, skipping the scale person and the
       pocket-hole viz markers. Each mesh gets its OWN geometry clone —
       Mesh.clone() is shallow and keeps the geometry reference, so
       without a deep clone applyMatrix4 would mutate the shared buffer
       and every instance would collapse onto the last transform.
       Returns Promise<ArrayBuffer> of the GLB bytes. */
    return new Promise(function (resolve, reject) {
      var T = global.THREE;
      var Exporter = global.GLTFExporter;
      var eng = global.RackEngine;
      if (!T || !eng || !eng.rackGroup) {
        reject(new Error('THREE / bench scene not ready'));
        return;
      }
      if (!Exporter) {
        reject(new Error('GLTFExporter not available'));
        return;
      }

      /* The configurator lights the scene with a soft Ambient(0.65) +
         Directional(0.85) rig, while model-viewer shows the model under a
         bright neutral environment — so the same baseColor reads LIGHTER
         in AR. Bake a darkening factor into the exported materials so AR
         matches the darker on-screen wood. Relative tones are preserved.
         Materials are cloned (never mutate the live scene) and de-duped by
         uuid so the GLB keeps one material per original. Tune AR_TONE if
         needed (lower = darker). */
      var AR_TONE = 0.7;
      var matCache = {};
      function _toneMat(mat) {
        if (!mat) return mat;
        if (matCache[mat.uuid]) return matCache[mat.uuid];
        var c = mat.clone();
        if (c.color && c.color.multiplyScalar) c.color.multiplyScalar(AR_TONE);
        matCache[mat.uuid] = c;
        return c;
      }

      var exportRoot = new T.Group();
      eng.rackGroup.traverse(function (node) {
        if (!node.isMesh || !node.geometry || !node.material) return;
        var pt = node.userData && node.userData.partType;
        if (pt === 'person') return;          /* scale figure, not part of the product */
        if (pt === 'logs') return;            /* decorative firewood, not the rack */
        if (!node.visible) return;
        node.updateMatrixWorld(true);
        var geom = node.geometry.clone();
        geom.applyMatrix4(node.matrixWorld);
        var mat = Array.isArray(node.material) ? node.material.map(_toneMat) : _toneMat(node.material);
        var cloned = new T.Mesh(geom, mat);
        cloned.name = node.name || (pt || 'mesh') + '_' + exportRoot.children.length;
        cloned.userData = Object.assign({}, node.userData || {});
        exportRoot.add(cloned);
      });

      if (exportRoot.children.length === 0) {
        reject(new Error('No exportable meshes in scene'));
        return;
      }

      var exporter = new Exporter();
      exporter.parse(
        exportRoot,
        function (glb) {
          if (glb instanceof ArrayBuffer) {
            resolve(glb);
          } else {
            reject(new Error('Expected GLB ArrayBuffer, got JSON'));
          }
        },
        function (err) { reject(err); },
        { binary: true }
      );
    });
  }

  async function _arUpload(glbBuffer) {
    var BOOT = global.RK_BOOT || {};
    var T = BOOT.i18n || {};
    /* Same Cloudflare Worker the foundations configurator uses (its
       ALLOWED_ORIGIN already covers www.theleva.com, so CORS is fine).
       RK_BOOT.arWorkerUrl overrides this if the section sets it. */
    var AR_WORKER_FALLBACK = 'https://soft-art-55b9.long-cloud-d006.workers.dev';
    var workerUrl = BOOT.arWorkerUrl || (T.arWorkerUrl) || AR_WORKER_FALLBACK;
    if (!workerUrl) throw new Error('Worker URL missing');
    _dbg('[ar] worker URL = ' + workerUrl);

    _dbg('[ar] GET ' + workerUrl + '/upload-url');
    var r1;
    try {
      r1 = await fetch(workerUrl + '/upload-url');
    } catch (netErr) {
      _dbg('[ar] network error on /upload-url: ' + netErr);
      throw new Error('Network error: ' + (netErr && netErr.message ? netErr.message : netErr));
    }
    _dbg('[ar] /upload-url response status=' + r1.status + ' ok=' + r1.ok);
    if (!r1.ok) throw new Error('upload-url failed: HTTP ' + r1.status);
    var info = await r1.json();
    _dbg('[ar] /upload-url payload: ' + JSON.stringify(info));
    if (!info || !info.uploadUrl) throw new Error('upload-url response invalid');

    _dbg('[ar] PUT ' + info.uploadUrl + ' (' + (glbBuffer.byteLength / 1024).toFixed(1) + ' KB)');
    var r2;
    try {
      r2 = await fetch(info.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'model/gltf-binary' },
        body: glbBuffer
      });
    } catch (netErr) {
      _dbg('[ar] network error on PUT: ' + netErr);
      throw new Error('Network error on PUT: ' + (netErr && netErr.message ? netErr.message : netErr));
    }
    _dbg('[ar] PUT response status=' + r2.status + ' ok=' + r2.ok);
    if (!r2.ok) {
      var errBody = '';
      try { errBody = await r2.text(); } catch (_) {}
      _dbg('[ar] PUT error body: ' + errBody);
      throw new Error('PUT failed: HTTP ' + r2.status + ' ' + errBody);
    }

    return info;  /* { uuid, uploadUrl, publicUrl, viewerUrl } */
  }

  function _arGenerateQR(url) {
    return new Promise(function (resolve) {
      function tryLib() {
        if (global.QRCode && global.QRCode.toDataURL) {
          global.QRCode.toDataURL(url, { width: 400, margin: 1, errorCorrectionLevel: 'M' }, function (err, dataUrl) {
            if (err || !dataUrl) {
              resolve('https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=' + encodeURIComponent(url));
            } else {
              resolve(dataUrl);
            }
          });
          return true;
        }
        return false;
      }
      if (tryLib()) return;
      var attempts = 0;
      var iv = setInterval(function () {
        attempts++;
        if (tryLib()) { clearInterval(iv); return; }
        if (attempts > 20) {
          clearInterval(iv);
          resolve('https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=' + encodeURIComponent(url));
        }
      }, 100);
    });
  }

  /* Self-contained AR modal — injected once so the bench section needs
     no extra HTML. Mirrors the foundations modal structure / ids so the
     ported _setAr* helpers target the same nodes. */
  /* The bench section loads THREE + OrbitControls but NOT the
     GLTFExporter (foundations does). Rather than require a section
     edit, pull the matching exporter build straight from esm.sh — the
     SAME three@0.160.0 the section already imports, so it is binary
     compatible. Dynamic import() works fine inside this classic script. */
  var _arExporterPromise = null;
  function _ensureGLTFExporter() {
    if (global.GLTFExporter) return Promise.resolve();
    if (_arExporterPromise) return _arExporterPromise;
    _arExporterPromise = import('https://esm.sh/three@0.160.0/examples/jsm/exporters/GLTFExporter.js')
      .then(function (mod) {
        global.GLTFExporter = mod.GLTFExporter || (mod.default && mod.default.GLTFExporter) || mod.default;
        if (!global.GLTFExporter) throw new Error('GLTFExporter import returned no export');
        _dbg('[ar] GLTFExporter loaded dynamically');
      })
      .catch(function (e) {
        _arExporterPromise = null;   /* allow retry on next AR open */
        throw new Error('Could not load GLTFExporter: ' + (e && e.message ? e.message : e));
      });
    return _arExporterPromise;
  }

  /* Self-contained AR modal — injected once, styled to match the
     foundations modal (cube badge, title, status, QR, bottom Close).
     The bench section has no .cf-onboard-modal CSS, so we ship our own
     scoped styles under #cf-ar-modal. Visibility is driven by the
     [hidden] attribute (the !important rule beats the base display:flex). */
  function _ensureArModal() {
    if (document.getElementById('cf-ar-modal')) return;
    var T = (global.RK_BOOT && global.RK_BOOT.i18n) || {};
    var title    = T.scan_to_view_in_ar    || 'Scan to view in AR';
    var subtitle = T.point_your_phone_camera_at_t || 'Point your phone camera at the QR code to place this model in your space.';
    var closeTxt = T.arClose         || 'Close';

    if (!document.getElementById('cf-ar-modal-style')) {
      var st = document.createElement('style');
      st.id = 'cf-ar-modal-style';
      st.textContent =
        '#cf-ar-modal{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;z-index:100000;}' +
        '#cf-ar-modal[hidden]{display:none !important;}' +
        '#cf-ar-modal .cf-arm-backdrop{position:absolute;inset:0;background:rgba(0,0,0,.55);backdrop-filter:blur(2px);}' +
        '#cf-ar-modal .cf-arm-card{position:relative;background:#fff;border-radius:16px;padding:32px 28px 24px;max-width:380px;width:calc(100% - 32px);box-shadow:0 20px 60px rgba(0,0,0,.25);text-align:center;animation:cfArModalIn .25s cubic-bezier(.16,1,.3,1);}' +
        '@keyframes cfArModalIn{from{opacity:0;transform:translateY(8px) scale(.98);}to{opacity:1;transform:none;}}' +
        '#cf-ar-modal .cf-arm-close{position:absolute;top:12px;right:12px;width:32px;height:32px;border:none;background:transparent;cursor:pointer;padding:0;display:flex;align-items:center;justify-content:center;color:#888;border-radius:8px;transition:background .15s ease,color .15s ease;}' +
        '#cf-ar-modal .cf-arm-close:hover{background:#f4f4f4;color:#222;}' +
        '#cf-ar-modal .cf-arm-icon{width:56px;height:56px;margin:0 auto 14px;border-radius:50%;background:linear-gradient(135deg,#d2a878 0%,#b88752 100%);display:flex;align-items:center;justify-content:center;color:#fff;}' +
        '#cf-ar-modal .cf-arm-title{font:700 18px/1.3 var(--cf-font-display,sans-serif);color:#1a1a1a;margin:0 24px 6px;letter-spacing:-.01em;}' +
        '#cf-ar-modal .cf-arm-subtitle{font:500 13px/1.5 var(--cf-font-display,sans-serif);color:#555;margin:0 0 20px;}' +
        '#cf-ar-modal #cf-ar-modal-status{font:500 12px/1.4 var(--cf-font-display,sans-serif);color:#888;margin-bottom:12px;min-height:16px;}' +
        '#cf-ar-modal #cf-ar-modal-qr{display:flex;justify-content:center;align-items:center;min-height:200px;margin:8px 0 14px;}' +
        '#cf-ar-modal #cf-ar-modal-link{font:500 11px/1.4 var(--cf-font-display,sans-serif);color:#b88752;word-break:break-all;margin-bottom:14px;display:none;}' +
        '#cf-ar-modal .cf-arm-actions{display:flex;gap:10px;justify-content:center;margin-top:6px;}' +
        '#cf-ar-modal .cf-arm-btn{border:none;border-radius:10px;padding:12px 18px;font:600 13px/1.2 var(--cf-font-display,sans-serif);cursor:pointer;transition:background .15s ease,transform .1s ease;min-width:110px;background:#f4f4f4;color:#333;}' +
        '#cf-ar-modal .cf-arm-btn:hover{background:#e8e8e8;}' +
        '#cf-ar-modal .cf-arm-btn:active{transform:translateY(1px);}';
      document.head.appendChild(st);
    }

    var wrap = document.createElement('div');
    wrap.id = 'cf-ar-modal';
    wrap.hidden = true;
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    wrap.innerHTML =
      '<div class="cf-arm-backdrop" data-cf-ar-close></div>' +
      '<div class="cf-arm-card">' +
        '<button class="cf-arm-close" type="button" data-cf-ar-close aria-label="' + closeTxt + '">' +
          '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>' +
        '</button>' +
        '<div class="cf-arm-icon" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">' +
          '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>' +
          '<polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>' +
          '<line x1="12" y1="22.08" x2="12" y2="12"></line></svg>' +
        '</div>' +
        '<h3 class="cf-arm-title">' + title + '</h3>' +
        '<p class="cf-arm-subtitle">' + subtitle + '</p>' +
        '<div id="cf-ar-modal-status"></div>' +
        '<div id="cf-ar-modal-qr"></div>' +
        '<div id="cf-ar-modal-link"></div>' +
        '<div class="cf-arm-actions">' +
          '<button class="cf-arm-btn" type="button" data-cf-ar-close>' + closeTxt + '</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(wrap);
  }

  async function _openArModal() {
    _ensureArModal();
    var modal = document.getElementById('cf-ar-modal');
    if (!modal) return;
    var T = (global.RK_BOOT && global.RK_BOOT.i18n) || {};

    _setArQR(null);
    _setArLink(null);
    _setArStatus(T.arPreparing || 'Preparing scene…');
    modal.hidden = false;

    function close() {
      modal.hidden = true;
      modal.removeEventListener('click', onClick);
    }
    function onClick(e) {
      if (e.target.closest('[data-cf-ar-close]')) {
        e.preventDefault();
        close();
      }
    }
    modal.addEventListener('click', onClick);

    try {
      await _ensureGLTFExporter();
      var glb = await _arExportScene();
      _dbg('[ar] GLB size = ' + (glb.byteLength / 1024).toFixed(1) + ' KB');
      _setArStatus(T.arUploading || 'Uploading to cloud…');

      var info = await _arUpload(glb);
      _dbg('[ar] uploaded uuid=' + info.uuid + ' viewer=' + info.viewerUrl);

      var qrDataUrl = await _arGenerateQR(info.viewerUrl);
      _setArQR(qrDataUrl);
      _setArLink(info.viewerUrl);
      _setArStatus(T.arReady || 'Ready — scan with your phone');
    } catch (e) {
      console.error('[ar] export failed', e);
      _setArStatus((T.ar_export_failed || 'AR export failed: ') + (e && e.message ? e.message : e));
    }
  }

  /* AR button. If the bench section already exposes #cf-btn-ar we just
     bind it; otherwise we clone an existing sidebar button (cf-btn-share)
     so the new one inherits the section's button styling, swap in an AR
     cube icon, and drop it in next to Share. */
  function _initArButton() {
    var openAr = function (e) {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      _openArModal();
    };

    var existing = document.getElementById('cf-btn-ar');
    if (existing) {
      existing.addEventListener('click', openAr);
      return;
    }

    var tmpl = document.getElementById('cf-btn-share') ||
               document.getElementById('cf-btn-pdf')   ||
               document.getElementById('cf-btn-view');
    /* Preferred slot: directly ABOVE the assembly-steps button (kept
       consistent across all configurators). Fall back to next to Share. */
    var anchor = document.getElementById('cf-btn-assembly');
    var styleSrc = tmpl || anchor;
    var parent = (anchor && anchor.parentNode) || (styleSrc && styleSrc.parentNode);
    if (!parent) return;   /* no sidebar to attach to */

    var T = (global.RK_BOOT && global.RK_BOOT.i18n) || {};
    var label = T.arButton || 'View in AR';

    /* Build a FRESH element rather than cloning a template: a clone
       carries inline onclick / data-* attributes (cloning Share made the
       AR button fire Share). We set only the generic icon-button class so
       it inherits sidebar styling but nothing behavioural. The click
       handler also stops propagation in case the sidebar uses a delegated
       (class-based) click router. */
    var btn = document.createElement((styleSrc || { tagName: 'BUTTON' }).tagName);
    btn.id = 'cf-btn-ar';
    /* ONLY the generic icon-button class — never cf-share-btn. The share
       module attaches a DELEGATED click listener that matches .cf-share-btn,
       so inheriting that class made the AR button fire Share. */
    btn.className = 'cf-icon-btn';
    if (btn.tagName === 'BUTTON') btn.type = 'button';
    btn.setAttribute('title', label);
    btn.setAttribute('aria-label', label);
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round" style="width:22px;height:22px;display:block;margin:auto;pointer-events:none;">' +
      '<path d="M12 2 3 7v10l9 5 9-5V7z"></path>' +
      '<path d="M12 22V12"></path>' +
      '<path d="M3 7l9 5 9-5"></path>' +
      '</svg>';

    if (anchor && anchor.parentNode === parent) {
      parent.insertBefore(btn, anchor);                    /* directly above Assembly */
    } else if (tmpl && tmpl.parentNode) {
      tmpl.parentNode.insertBefore(btn, tmpl.nextSibling); /* fallback: next to Share */
    } else {
      parent.appendChild(btn);
    }
    btn.addEventListener('click', openAr);
  }

  function wireSidebar() {
    var on = function (id, fn) {
      var el = document.getElementById(id);
      if (el) el.addEventListener('click', fn);
    };
    on('cf-btn-view',     function () { global.RackEngine.toggleView(); });
    on('cf-btn-viz',      function () { global.RackEngine.cycleVizStage(); });
    on('cf-btn-reset',    reset);
    on('cf-btn-unit',     toggleUnits);
    on('cf-btn-budget',   openBudgetDrawer);
    on('cf-btn-share',    share);
    /* rack-v9-purchase (Marek: Firewood rack vratit, tam platime provize
       autorovi): a royalty product -- the PDF is the purchase, never tokens.
       No pay step; the purchase gate of configurator-core.js lets a buyer
       through and shows "Purchase to unlock" to everyone else. */
    on('cf-btn-pdf',      function () {
      exportPdf().catch(function (e) { console.error('[exportPdf]', e); });
    });
    on('cf-btn-body',     toggleBody);
    on('cf-btn-assembly', cycleAssembly);

    /* PDF button: disabled until data loads, then promoted to red primary. */
    _initPdfButtonState();

    /* AR: inject + bind the "View in AR" button (above Assembly) + modal. */
    _initArButton();

    /* 2D fill-opacity slider — updates STATE.opacity2D and re-applies
       stage so material opacities pick up the new value. Pointer events
       are 'input' (live update during drag) for instant feedback.
       Drives --cfg-fill CSS var so the gradient track shows colored
       fill below the thumb (pool-style). Slider max is 80, so the
       fill is normalised against that for a 0–100% visual range. */
    var op2dSlider = document.getElementById('cf-2d-opacity-slider');
    var op2dValue  = document.getElementById('cf-2d-opacity-value');
    if (op2dSlider) {
      var op2dMax = parseInt(op2dSlider.max, 10) || 100;
      var updateFill = function (pct) {
        /* Slider is writing-mode tb-lr → thumb at BOTTOM = max value.
           Fill grows from the thumb downward; the gradient (to top:
           accent..gray) means accent fills 0..--cfg-fill from bottom.
           So fill must be SMALL when value is HIGH (thumb low) — invert. */
        var fillPct = 100 - (pct / op2dMax) * 100;
        op2dSlider.style.setProperty('--cfg-fill', fillPct + '%');
      };
      var onOp2dChange = function () {
        var pct = parseInt(op2dSlider.value, 10) || 0;
        STATE.opacity2D = pct / 100;
        if (op2dValue) op2dValue.textContent = pct + '%';
        updateFill(pct);
        if (global.RackEngine && global.RackEngine.applyVizStage) {
          global.RackEngine.applyVizStage();
        }
        if (global.RackLib && global.RackLib.savePref) global.RackLib.savePref();
      };
      op2dSlider.addEventListener('input', onOp2dChange);
      op2dSlider.addEventListener('change', onOp2dChange);
      /* Initialize from STATE (loadSaved may have set non-default). */
      var initPct = Math.round(STATE.opacity2D * 100);
      op2dSlider.value = initPct;
      if (op2dValue) op2dValue.textContent = initPct + '%';
      updateFill(initPct);
    }

    _initBodyDragHandle();
    _startBodyHandleTracker();
  }

  /* ── Open the budget "Share / Your data / Available budgets" drawer
        from the configurator's money icon. budget-sharing.liquid exposes
        window.openShareBudgetDrawer(initialTab) — on product pages the
        'data' tab is the relevant one (becomes "Available budgets"). */
  function openBudgetDrawer() {
    if (typeof global.openShareBudgetDrawer === 'function') {
      global.openShareBudgetDrawer('data');
    } else if (typeof global.openRefreshDrawer === 'function') {
      global.openRefreshDrawer();
    } else {
      _dbg('no budget drawer opener on window — budget-sharing.liquid may not be loaded yet');
    }
  }

  /* ── Body (scale-figure) toggle & drag ───────────────────────────── */
  function toggleBody() {
    STATE.bodyVisible = !STATE.bodyVisible;
    var btn = document.getElementById('cf-btn-body');
    if (btn) {
      btn.setAttribute('aria-pressed', STATE.bodyVisible ? 'true' : 'false');
      btn.style.opacity = STATE.bodyVisible ? '' : '0.45';
    }
    var handle = document.getElementById('cf-body-handle');
    if (handle) handle.classList.toggle('active', STATE.bodyVisible);
    global.RackRenderer.build();
  }

  function _initBodyDragHandle() {
    var handle = document.getElementById('cf-body-handle');
    if (!handle) return;
    var dragging = false;
    var T = global.THREE;
    var plane = null, raycaster = null, ndc = null, hit = null;

    function getCanvasRect() {
      var cnv = global.RackEngine && global.RackEngine.renderer
                ? global.RackEngine.renderer.domElement : null;
      return cnv ? cnv.getBoundingClientRect() : null;
    }

    function pointerToWorld(clientX, clientY) {
      var eng = global.RackEngine;
      if (!eng || !eng.camera || !eng.renderer) return null;
      var rect = getCanvasRect(); if (!rect) return null;
      if (!plane) plane = new T.Plane(new T.Vector3(0, 1, 0), 0);  /* Y=0 ground */
      if (!raycaster) raycaster = new T.Raycaster();
      if (!ndc) ndc = new T.Vector2();
      if (!hit) hit = new T.Vector3();
      ndc.x =  ((clientX - rect.left) / rect.width)  * 2 - 1;
      ndc.y = -((clientY - rect.top)  / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, eng.camera);
      var p = raycaster.ray.intersectPlane(plane, hit);
      return p;  /* world Vector3 with y=0 */
    }

    function onDown(e) {
      if (!STATE.bodyVisible) return;
      e.preventDefault();
      dragging = true;
      handle.setPointerCapture && handle.setPointerCapture(e.pointerId);
      /* Disable orbit controls during drag so the camera doesn't move. */
      if (global.RackEngine && global.RackEngine.controls) {
        global.RackEngine.controls.enabled = false;
      }
    }
    function onMove(e) {
      if (!dragging) return;
      var p = pointerToWorld(e.clientX, e.clientY);
      if (!p) return;
      STATE.bodyX = p.x;
      STATE.bodyZ = p.z;
      /* Move person live without full rebuild — fast & smooth. */
      var rg = global.RackEngine && global.RackEngine.rackGroup;
      if (rg) {
        for (var i = 0; i < rg.children.length; i++) {
          var c = rg.children[i];
          if (c.userData && c.userData.partType === 'person') {
            c.position.set(STATE.bodyX, 0, STATE.bodyZ);
            if (c.userData.basePos) c.userData.basePos.set(STATE.bodyX, 0, STATE.bodyZ);
            break;
          }
        }
      }
    }
    function onUp(e) {
      if (!dragging) return;
      dragging = false;
      try { handle.releasePointerCapture && handle.releasePointerCapture(e.pointerId); } catch (_) {}
      if (global.RackEngine && global.RackEngine.controls) {
        global.RackEngine.controls.enabled = true;
      }
    }
    handle.addEventListener('pointerdown',   onDown);
    handle.addEventListener('pointermove',   onMove);
    handle.addEventListener('pointerup',     onUp);
    handle.addEventListener('pointercancel', onUp);
  }

  function _startBodyHandleTracker() {
    /* Project the person's torso (Y ≈ 1.2 m above feet) to screen, and
       position the HTML drag-handle there each frame. */
    var T = global.THREE;
    var v3 = new T.Vector3();
    function tick() {
      var handle = document.getElementById('cf-body-handle');
      var eng = global.RackEngine;
      if (handle && eng && eng.camera && eng.renderer && STATE.bodyVisible) {
        var bx = (STATE.bodyX != null) ? STATE.bodyX : (STATE.W / 2 + 0.60);
        var bz = (STATE.bodyZ != null) ? STATE.bodyZ : 0;
        v3.set(bx, 1.20, bz);   /* near the torso for nice ergonomic grab */
        v3.project(eng.camera);
        var rect = eng.renderer.domElement.getBoundingClientRect();
        var canvasRect = eng.renderer.domElement.getBoundingClientRect();
        var parent = handle.offsetParent;
        var parentRect = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 };
        var x = canvasRect.left - parentRect.left + (v3.x + 1) * 0.5 * canvasRect.width;
        var y = canvasRect.top  - parentRect.top  + (1 - (v3.y + 1) * 0.5) * canvasRect.height;
        handle.style.left = x + 'px';
        handle.style.top  = y + 'px';
        /* Hide if behind camera (z > 1 in NDC means clipped). */
        var visible = v3.z > -1 && v3.z < 1;
        handle.style.opacity = visible ? '' : '0';
      }
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  /* Visible part types per assembly step (mirrors RackRenderer._applyAssemblyStep). */
  var ALL_PARTS = ['frame','decking','post','rail-side','rail-long','rafter','batten','roof','brace',
                   'screw-frame','screw-decking','screw-post-bottom','screw-rail-side',
                   'screw-rail-long','screw-rafter','screw-batten','screw-roof','screw-brace'];
  var ALL_PARTS_WITH_LOGS = ALL_PARTS.concat(['logs']);
  var STEP_VISIBLE = {
    0:  ALL_PARTS,                            /* composed — no logs at initial state */
    1:  ALL_PARTS,                            /* exploded — no logs */
    2:  ['frame'],
    3:  ['frame','screw-frame'],
    4:  ['frame','screw-frame','decking'],
    5:  ['frame','screw-frame','decking','screw-decking'],
    6:  ['frame','screw-frame','decking','screw-decking','post'],
    7:  ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom'],
    8:  ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side'],
    9:  ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side'],
    10: ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side','rail-long'],
    11: ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side','rail-long','screw-rail-long'],
    12: ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side','rail-long','screw-rail-long','rafter'],
    13: ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side','rail-long','screw-rail-long','rafter','screw-rafter'],
    14: ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side','rail-long','screw-rail-long','rafter','screw-rafter','batten'],
    15: ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side','rail-long','screw-rail-long','rafter','screw-rafter','batten','screw-batten'],
    16: ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side','rail-long','screw-rail-long','rafter','screw-rafter','batten','screw-batten','roof'],
    17: ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side','rail-long','screw-rail-long','rafter','screw-rafter','batten','screw-batten','roof','screw-roof'],
    18: ['frame','screw-frame','decking','screw-decking','post','screw-post-bottom','rail-side','screw-rail-side','rail-long','screw-rail-long','rafter','screw-rafter','batten','screw-batten','roof','screw-roof','brace'],
    19: ALL_PARTS,                            /* all parts assembled, no logs */
    20: ALL_PARTS_WITH_LOGS                   /* logs added — matrix-style drop */
  };

  function _diffNewParts(prevStep, newStep) {
    var prev = STEP_VISIBLE[prevStep] || [];
    var now  = STEP_VISIBLE[newStep] || [];
    var diff = [];
    now.forEach(function (pt) { if (prev.indexOf(pt) === -1) diff.push(pt); });
    return diff;
  }

  function cycleAssembly() {
    var prevStep = STATE.assemblyStep;
    var newStep = (prevStep + 1) % 21;
    STATE.assemblyStep = newStep;

    var isExplodeIn  = (newStep === 1);
    var isExplodeOut = (prevStep === 1);
    var newParts = _diffNewParts(prevStep, newStep);
    var hasNewParts = newParts.length > 0;
    var doAnimate = isExplodeIn || isExplodeOut || hasNewParts;

    if (!doAnimate) {
      global.RackRenderer.build();
      refreshUI();
      _toggleHandlesForStep(newStep);
      return;
    }

    var eng = global.RackEngine;
    if (!eng || !eng.rackGroup) {
      global.RackRenderer.build();
      refreshUI();
      _toggleHandlesForStep(newStep);
      return;
    }

    /* Rebuild — children get repopulated, _applyAssemblyStep sets positions at end. */
    global.RackRenderer.build();
    refreshUI();
    _toggleHandlesForStep(newStep);

    var T = global.THREE;
    var DEFAULT_OFFSET = {
      'frame':              [0, -0.30, 0],
      'decking':            [0,  0.20, 0],
      'post':               [0,  0,    0],
      'rail-side':          [0,  0.25, 0],
      'rail-long':          [0,  0.45, 0],
      'rafter':             [0,  0.65, 0],
      'batten':             [0,  0.85, 0],
      'roof':               [0,  1.15, 0],
      'brace':              [-0.40, 0, 0],
      'screw-frame':        [0,  0.35, 0],
      'screw-decking':      [0,  0.35, 0],
      'screw-post-bottom':  [0,  0,    0],
      'screw-rail-side':    [0,  0.30, 0],
      'screw-rail-long':    [0,  0,    0],
      'screw-rafter':       [0,  0.40, 0],
      'screw-batten':       [0,  0.55, 0],
      'screw-roof':         [0,  1.40, 0],
      'screw-brace':        [-0.65, 0, 0],
      'logs':               [0,  3.50, 0]
    };

    var targets = [];
    eng.rackGroup.children.forEach(function (c) {
      var base = c.userData.basePos;
      var pt = c.userData.partType;
      if (!base || !pt || !c.visible) return;
      /* PERSON: scale reference, never animates. */
      if (pt === 'person') return;

      var off = c.userData.explodeOffset || DEFAULT_OFFSET[pt] || [0,0,0];
      var basePos = base.clone();
      var explodedPos = new T.Vector3(base.x + off[0], base.y + off[1], base.z + off[2]);
      var endPos = c.position.clone();   /* where _applyAssemblyStep placed it */

      /* Decide where to START the animation: */
      var startPos;
      if (isExplodeIn) {
        /* Going INTO exploded — start from base, end at exploded */
        startPos = basePos;
      } else if (isExplodeOut) {
        /* Coming OUT of exploded — start from exploded, end at base */
        startPos = explodedPos;
      } else if (newParts.indexOf(pt) !== -1) {
        /* This part is newly visible in the new step — start from exploded, fly into base */
        startPos = explodedPos;
      } else {
        /* Already visible in previous step — no animation, snap */
        startPos = endPos;
      }

      c.position.copy(startPos);
      targets.push({ obj: c, from: startPos, to: endPos, staggerDelay: 0 });
    });

    /* ── Logs animation: MATRIX-STYLE drop. Each log falls independently
          from a tall start position, with a randomized delay so they don't
          fall in a wave but appear one-by-one as scattered raindrops. */
    var logTargets = targets.filter(function (t) {
      return t.obj.userData && t.obj.userData.partType === 'logs';
    });
    /* Deterministic pseudo-random ordering based on log index/position so
       it looks scattered but is reproducible across runs. */
    logTargets.forEach(function (t, i) {
      var seed = (i * 9301 + 49297) % 233280;
      t.staggerDelay = (seed / 233280) * 1400;   /* spread across 1.4s */
    });
    var maxStagger = logTargets.length > 0
      ? Math.max.apply(null, logTargets.map(function (t) { return t.staggerDelay; }))
      : 0;

    /* requestAnimationFrame animation loop */
    var DURATION = 900;
    var LOG_DURATION = 280;  /* per-log fall time (snappy matrix drop) */
    var startTime = performance.now();
    function easeInOutCubic(t) { return t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t + 2, 3) / 2; }
    /* Logs use gravity-like ease-in (slow start, accelerating) for a
       satisfying matrix-rain drop. */
    function easeInQuad(t) { return t * t; }
    function tick() {
      var elapsed = performance.now() - startTime;
      var allDone = true;
      targets.forEach(function (tg) {
        var isLog = (tg.obj.userData && tg.obj.userData.partType === 'logs');
        var delay = tg.staggerDelay || 0;
        var dur = isLog ? LOG_DURATION : DURATION;
        var local = elapsed - delay;
        var t;
        if (local < 0) { t = 0; allDone = false; }
        else if (local >= dur) { t = 1; }
        else { t = local / dur; allDone = false; }
        var k = isLog ? easeInQuad(t) : easeInOutCubic(t);
        tg.obj.position.set(
          tg.from.x + (tg.to.x - tg.from.x) * k,
          tg.from.y + (tg.to.y - tg.from.y) * k,
          tg.from.z + (tg.to.z - tg.from.z) * k
        );
      });
      if (!allDone) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  /* Refresh drag-handle visibility based on combined state.

     There are TWO sets of handles:
       LEGACY set (cf-handle-l/w/h): used in 3D and 2D stage 1.
       ELEVATION set (cf-handle-elev-front-l/h, cf-handle-elev-side-w/h):
         used ONLY in 2D stage 2. Visible via `.cf-elev-visible` class
         (toggles `display: block`).

     Visibility matrix:
       assemblyStep != 0    → ALL hidden (no editing during assembly anim)
       3D stage 1           → legacy L, W, H visible; elev all hidden
       3D stage 2, 3        → ALL hidden (inspection/profile views)
       2D stage 1 (plan)    → legacy L, W visible (H hidden — can't edit height
                              from top-down); elev all hidden
       2D stage 2 (elev)    → legacy ALL hidden; elev all visible

     Called from cycleAssembly, applyVizStage, toggleView. */
  function _refreshHandlesVisibility() {
    var l = document.getElementById('cf-handle-l');
    var w = document.getElementById('cf-handle-w');
    var h = document.getElementById('cf-handle-h');
    var efL = document.getElementById('cf-handle-elev-front-l');
    var efH = document.getElementById('cf-handle-elev-front-h');
    var esW = document.getElementById('cf-handle-elev-side-w');
    var esH = document.getElementById('cf-handle-elev-side-h');
    var elevSet = [efL, efH, esW, esH];

    function hideLegacy() {
      if (l) l.style.display = 'none';
      if (w) w.style.display = 'none';
      if (h) h.style.display = 'none';
    }
    function showLegacy(showH_) {
      if (l) l.style.display = '';
      if (w) w.style.display = '';
      if (h) h.style.display = showH_ ? '' : 'none';
    }
    function hideElev() {
      elevSet.forEach(function (el) { if (el) el.classList.remove('cf-elev-visible'); });
    }
    function showElev() {
      elevSet.forEach(function (el) { if (el) el.classList.add('cf-elev-visible'); });
    }

    var hideAll = (STATE.assemblyStep !== 0)
                  || (STATE.view === '3d' && STATE.vizStage !== 1);

    if (hideAll) {
      hideLegacy();
      hideElev();
      return;
    }

    if (STATE.view === '2d') {
      /* In 2D, show BOTH chips and round corner handles. Chips show
         dim values; round handles mark the rack corners that can be
         dragged. Both pipe to the same _start/_move pipeline.
           stage 1 (plan)   → L + W handles
           stage 2 (bokorys)→ L + H handles (only L,H editable from side)
           stage 3 (nárys)  → W + H handles (only W,H editable from front) */
      hideElev();
      var s = STATE.vizStage;
      if (l) l.style.display = (s === 1 || s === 2) ? '' : 'none';
      if (w) w.style.display = (s === 1 || s === 3) ? '' : 'none';
      if (h) h.style.display = (s === 2 || s === 3) ? '' : 'none';
    } else {
      /* 3D — legacy round handles. */
      hideElev();
      showLegacy(true);
    }
  }

  /* Backward-compat alias — existing call sites in cycleAssembly. */
  function _toggleHandlesForStep(/* step */) {
    _refreshHandlesVisibility();
  }

  /* ----------------------------------------------------------
     ICON TOOLTIPS — touch flash + diagnostic
     Tooltips themselves are pure CSS (::after reads attr(title))
     so they appear instantly on desktop hover with no JS needed.
     On touch devices `:hover` and `:active` don't behave the same,
     so we briefly toggle `.show-tip` on touchstart for ~1.5 s so a
     tap reveals the label. Mirror of the pool-lib pattern.

     The function also dumps a one-time diagnostic snapshot of the
     first three icon buttons to the debug panel — exposes their
     title attribute, ::after computed content/opacity/position, and
     bounding-box. Lets us identify why tooltips might be invisible
     (missing title, clipped by overflow, opacity:0 stuck, etc.)
     without DevTools console access on tablet.
     ---------------------------------------------------------- */
  /* ----------------------------------------------------------
     ICON TOOLTIPS — runtime CSS injection + touch flash + diagnostic

     IMPORTANT: a previous diagnostic showed that the snippet's <style>
     block had its `.cf-icon-btn::after` rules silently dropped (sheet
     scan found only the base .cf-icon-btn rule, not the ::after). We
     don't know exactly why — Shopify Theme Editor minifier, or some
     storefront preprocessing step. As a workaround, this function
     injects the same CSS at runtime via document.head, which bypasses
     whatever was stripping it from the rendered snippet.

     The function also dumps a stylesheet-scan + computed-style
     snapshot to the debug panel so we can verify injection succeeded.
     ---------------------------------------------------------- */
  function _injectTooltipCSS() {
    if (document.getElementById('cf-tooltip-runtime-css')) return;
    var css = [
      '#cf-root .cf-icon-btn { position: relative !important; overflow: visible !important; }',
      '#cf-root .cf-sidebar { overflow: visible !important; }',
      '#cf-root .cf-icon-btn::after {',
      '  content: attr(title) !important;',
      '  position: absolute !important;',
      '  right: calc(100% + 8px) !important;',
      '  top: 50% !important;',
      '  transform: translateY(-50%) !important;',
      '  background: rgba(20, 20, 20, 0.92) !important;',
      '  color: #fff !important;',
      '  padding: 6px 10px !important;',
      '  border-radius: 6px !important;',
      '  font: 600 11px/1.2 Montserrat, -apple-system, sans-serif !important;',
      '  letter-spacing: 0.02em !important;',
      '  white-space: nowrap !important;',
      '  pointer-events: none !important;',
      '  opacity: 0 !important;',
      '  transition: opacity 0.15s ease !important;',
      '  z-index: 9999 !important;',
      '  display: block !important;',
      '  visibility: visible !important;',
      '  width: auto !important;',
      '  height: auto !important;',
      '}',
      '#cf-root .cf-icon-btn:hover::after,',
      '#cf-root .cf-icon-btn:active::after,',
      '#cf-root .cf-icon-btn.show-tip::after { opacity: 1 !important; }'
    ].join('\n');
    var style = document.createElement('style');
    style.id = 'cf-tooltip-runtime-css';
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
  }

  function _bindIconTooltips() {
    /* STEP 1: inject CSS from JS (bypass Liquid/Shopify minifier). */
    _injectTooltipCSS();

    var btns = document.querySelectorAll('.cf-icon-btn');
    var L = (global.Configurator && global.Configurator.log) || null;

    if (L) {
      L.log('[tooltip-debug] found ' + btns.length + ' .cf-icon-btn elements');
      L.log('[tooltip-debug] runtime-CSS injected = ' +
            (!!document.getElementById('cf-tooltip-runtime-css')));
    }

    /* Scan all stylesheets for ::after rules touching cf-icon-btn.
       Reveals which sheet/selector is winning the cascade. */
    if (L) {
      var rulesFound = [];
      var sheetsBlocked = 0;
      for (var s = 0; s < document.styleSheets.length; s++) {
        var sheet = document.styleSheets[s];
        try {
          var rules = sheet.cssRules || sheet.rules;
          if (!rules) continue;
          for (var r = 0; r < rules.length; r++) {
            var rule = rules[r];
            if (!rule.selectorText) continue;
            if (rule.selectorText.indexOf('cf-icon-btn') === -1) continue;
            rulesFound.push(
              'sheet[' + s + ']: ' + rule.selectorText +
              ' { ' + rule.style.cssText.slice(0, 120) + ' }'
            );
          }
        } catch (e) { sheetsBlocked++; }
      }
      L.log('[tooltip-debug] scanned ' + document.styleSheets.length +
            ' stylesheets, blocked=' + sheetsBlocked +
            ', cf-icon-btn rules found=' + rulesFound.length);
      for (var i2 = 0; i2 < Math.min(rulesFound.length, 5); i2++) {
        L.log('[tooltip-rule] ' + rulesFound[i2]);
      }
    }

    /* Diagnostic dump of first 2 buttons. */
    for (var i = 0; i < Math.min(btns.length, 2); i++) {
      var btn = btns[i];
      if (!L) break;
      var title  = btn.getAttribute('title') || '(none)';
      var id     = btn.id || '(no-id)';
      var after  = window.getComputedStyle(btn, '::after');
      L.log('[tooltip-debug] btn #' + id +
            ' title="' + title + '"' +
            ' ::after content=' + (after.content || '(empty)') +
            ' position=' + after.position +
            ' right=' + after.right +
            ' opacity=' + after.opacity);
    }

    var tipTimer = null;
    btns.forEach(function (btn) {
      btn.addEventListener('touchstart', function () {
        if (tipTimer) clearTimeout(tipTimer);
        document.querySelectorAll('.cf-icon-btn.show-tip').forEach(function (b) {
          b.classList.remove('show-tip');
        });
        btn.classList.add('show-tip');
        if (L) L.log('[tooltip-debug] touch flash on #' + (btn.id || '?'));
        tipTimer = setTimeout(function () { btn.classList.remove('show-tip'); }, 1500);
      }, { passive: true });
    });
  }

  function boot() {
    STATE.isMetric = detectUnits();
    loadSaved();
    _applyUnitDims();
    if (!STATE.__savedDims) { STATE.L = DEFAULTS.L; STATE.W = DEFAULTS.W; STATE.H = DEFAULTS.H; }
    _snapDims();

    if (!global.RackEngine.init()) {
      if (global.Configurator && global.Configurator.log) {
        global.Configurator.log.log('[rack-lib] RackEngine.init failed');
      }
      return;
    }
    global.RackRenderer.build();
    global.RackHandles.init();
    if (global.RackDimensions && global.RackDimensions.init) global.RackDimensions.init();
    wireSidebar();

    /* Stage 4 (Select) raycaster — initialise canvas listeners ONCE,
       after the renderer's canvas exists. setActive() inside
       applyVizStage() then toggles its picking behaviour per stage. */
    if (global.RackRaycaster && global.RackRaycaster.init) {
      global.RackRaycaster.init();
    }

    /* Asset drawer (shared module) — mount DOM and wire callbacks.
       Registry returns the enriched instance (BoQ totals + product
       info from RK_BOOT.boqProducts). onClose drops the selection
       highlight so the visual state always matches the drawer. */
    if (global.ConfiguratorAssetDrawer && global.ConfiguratorAssetDrawer.init) {
      var BOOT_I18N = (global.RK_BOOT && global.RK_BOOT.i18n) || {};
      var drawerLabels = {};
      /* Pass through any asset-drawer labels supplied by the snippet
         under RK_BOOT.i18n.assetDrawer.{key}. Keys not provided fall
         back to defaults inside the module. */
      var ad = BOOT_I18N.assetDrawer || {};
      for (var k in ad) { if (ad.hasOwnProperty(k)) drawerLabels[k] = ad[k]; }
      global.ConfiguratorAssetDrawer.init({
        registry: function (id) {
          if (!global.RackInstances) return null;
          var raw = global.RackInstances.get(id);
          return raw ? global.RackInstances.enrichForDrawer(raw) : null;
        },
        onClose: function () {
          if (global.RackRaycaster && global.RackRaycaster._selected) {
            global.RackHighlight.deselect(global.RackRaycaster._selected);
            global.RackRaycaster._selected = null;
          }
        },
        labels: drawerLabels,
        drawerScopeClass: 'rack-asset-drawer'
      });
    }

    /* Apply restored view + vizStage from localStorage. Engine.init left the
       camera in default 3D position; if user had 2D saved, reposition it. */
    global.RackEngine.applyView();
    global.RackEngine.applyVizStage();

    refreshUI();

    /* Bind icon tooltips (CSS ::after reads data-tip; on touch we toggle
       .show-tip for ~1.5 s so taps reveal the label). Pattern mirrors
       pool-lib so behaviour is consistent across configurators. */
    _bindIconTooltips();

    /* Listen for budget-side unit toggles and follow them.
       Budget fires this event from clickUnitSelector(). */
    global.addEventListener('unitChange', _syncFromBudgetUnit);
    /* Initial sync: if budget already has a unit set (e.g. via currency
       detection on DOMContentLoaded), adopt it. */
    _syncFromBudgetUnit();

    if (global.Configurator && global.Configurator.log) {
      global.Configurator.log.log('[rack-units] ' + (STATE.isMetric ? 'metric' : 'imperial (US lumber)') +
        ' L=' + fmtDim(STATE.L) + ' W=' + fmtDim(STATE.W) + ' H=' + fmtDim(STATE.H) +
        (STATE.__savedDims ? ' (saved sizes)' : ' (default = Matt Thompson original)'));
      global.Configurator.log.log('[rack-lib] boot OK, version=' + VERSION +
                                  ', view=' + STATE.view + ', vizStage=' + STATE.vizStage);

      /* ── BOOT diagnostic: dump full state so we can see if something is
         restored wrong from localStorage. Includes camera positions,
         canvas size, and STATE dimensions. */
      var eng = global.RackEngine;
      if (eng && eng.camera && eng.renderer) {
        var canvas = eng.renderer.domElement;
        var rect = canvas.getBoundingClientRect();
        global.Configurator.log.log('[boot-diag] STATE.L=' + STATE.L.toFixed(3) +
                                    ' W=' + STATE.W.toFixed(3) +
                                    ' H=' + STATE.H.toFixed(3) +
                                    ' assemblyStep=' + STATE.assemblyStep);
        global.Configurator.log.log('[boot-diag] canvas device=' + canvas.width + 'x' + canvas.height +
                                    ' css=' + Math.round(rect.width) + 'x' + Math.round(rect.height));
        global.Configurator.log.log('[boot-diag] persp camera: pos=(' +
                                    eng.camera.position.x.toFixed(2) + ',' +
                                    eng.camera.position.y.toFixed(2) + ',' +
                                    eng.camera.position.z.toFixed(2) + ')' +
                                    ' fov=' + eng.camera.fov +
                                    ' aspect=' + eng.camera.aspect.toFixed(3));
        global.Configurator.log.log('[boot-diag] controls.target=(' +
                                    eng.controls.target.x.toFixed(2) + ',' +
                                    eng.controls.target.y.toFixed(2) + ',' +
                                    eng.controls.target.z.toFixed(2) + ')' +
                                    ' dist=' + eng.controls.getDistance().toFixed(2));
        global.Configurator.log.log('[boot-diag] ortho frustum L/R=' +
                                    eng.orthoCamera.left.toFixed(2) + '/' +
                                    eng.orthoCamera.right.toFixed(2) +
                                    ' T/B=' + eng.orthoCamera.top.toFixed(2) + '/' +
                                    eng.orthoCamera.bottom.toFixed(2) +
                                    ' zoom=' + eng.orthoCamera.zoom.toFixed(2));
      }
    }
  }

  /* =========================================================================
     THE DOCUMENTATION -- MATT'S SET, DRAWN FROM THIS MODEL (rack-v10-docs)

     Marek 2026-10-27, after a buyer asked for a refund ("no representation of
     the assembly process, not enough views ... cuts like 5.91 ft aren't
     useful"): our documentation follows how Matt Thompson's original PDF is
     put together -- plan with a 3D scheme and the general notes, overall
     dimensions, elevations, sections, 3D views, the assembly drawing with the
     parts marked, the parts schedule with each piece drawn -- in the LEVA
     look (the product picture on the cover, the list of pages, the cut list,
     the bill and the shopping list after the drawings), and every number is
     the configurator's: the drawings are made from the meshes of the rack on
     the screen, so a rack of any size gets its own drawings.

     Square-on views are vector: every face of every part, painted far to
     near, cut parts hatched. The 3D views are pictures of the model through
     an orthographic camera whose axes are read back, so the dimensions and
     the part marks laid over them land on the parts. Imperial in feet-inches
     to 1/16 in (6'-0", 5'-11 15/16"), metric in millimetres.

     RackDoc.prepare() (async: the pictures) -> RackDoc.pages() = the
     exporter's customPages, drawn before the cut list (args.customFirst).
     ========================================================================= */
  var RackDoc = (function () {
    var DRAW = { frame: 1, decking: 1, post: 1, 'rail-long': 1, 'rail-side': 1, rafter: 1, batten: 1, roof: 1, brace: 1 };
    var INK = [40, 32, 24];
    var FILL = {
      post: [205, 160, 105], frame: [205, 160, 105], 'rail-long': [205, 160, 105], 'rail-side': [205, 160, 105],
      rafter: [205, 160, 105], brace: [205, 160, 105], batten: [216, 182, 132], decking: [166, 116, 70], roof: [112, 118, 126]
    };
    var _pics = null, _sol = null, _marks = null, _note = '';

    /* ---------- small vector maths ---------- */
    function dot(a, b) { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]; }
    function sub(a, b) { return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]; }
    function add(a, b) { return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]; }
    function mul(a, k) { return [a[0] * k, a[1] * k, a[2] * k]; }
    function cross(a, b) { return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]; }
    function norm(a) { var l = Math.sqrt(dot(a, a)); return l > 1e-12 ? [a[0] / l, a[1] / l, a[2] / l] : null; }
    function lerp(a, b, t) { return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]; }

    /* ---------- words ---------- */
    function T_(k, fb) {
      var T = (global.RK_BOOT && global.RK_BOOT.i18n) || {}, v = T[k];
      if (typeof v !== 'string' || !v || /translation missing/i.test(v)) return fb;
      return v.replace(/&amp;/g, '&').replace(/&#39;/g, "'").replace(/&quot;/g, '"');
    }
    function catName(code) {
      var b = global.ConfiguratorBudgetBridge, it = null;
      try { it = b && b.getBoqItem ? b.getBoqItem(code) : null; } catch (e) { it = null; }
      var n = (it && it.name) || (MATERIALS[code] && MATERIALS[code].name) || code;
      if (!STATE.isMetric && CUT_LIST_DIM[code]) n = _swapDimToken(n, CUT_LIST_DIM[code].metric, CUT_LIST_DIM[code].imperial);
      return String(n).replace(/\u00d7/g, 'x');
    }
    var PART_KEY = { post: ['pdfPartPost', 'Post'], frame: ['pdfPartRailBottom', 'Bottom rail'], 'rail-long': ['pdfPartRailLong', 'Long rail'],
      'rail-side': ['pdfPartRailSide', 'Side rail'], rafter: ['pdfPartRafter', 'Rafter'], batten: ['pdfPartBatten', 'Batten'],
      decking: ['pdfPartDecking', 'Decking board'], roof: ['pdfPartRoof', 'Roof panel'], brace: ['brace', 'Brace'] };
    function partName(pt) { var k = PART_KEY[pt]; return k ? T_(k[0], k[1]) : pt; }
    function profName(pt) {
      if (STATE.isMetric) {
        if (pt === 'batten') return Math.round(DIMS.oneByTwo.w * 1000) + ' x ' + Math.round(DIMS.oneByTwo.h * 1000);
        if (pt === 'decking') return Math.round(DIMS.decking.w * 1000) + ' x ' + Math.round(DIMS.decking.h * 1000);
        if (pt === 'roof') return '';
        return Math.round(DIMS.twoByFour.w * 1000) + ' x ' + Math.round(DIMS.twoByFour.h * 1000);
      }
      if (pt === 'batten') return '1x2';
      if (pt === 'decking') return '1x6';
      if (pt === 'roof') return '';
      return '2x4';
    }

    /* ---------- units ---------- */
    function fD(m) { return STATE.isMetric ? String(Math.round(m * 1000)) : fmtFtIn(m); }
    function fL(m) {
      if (STATE.isMetric) return (m >= 0 ? '+' : '-') + Math.abs(m).toFixed(3);
      return (m >= 0 ? '+' : '-') + fmtFtIn(Math.abs(m));
    }
    function unitsLine() { return STATE.isMetric ? T_('bpDimsMm', 'Dimensions in mm') : T_('bpDimsFtin', 'Dimensions in feet and inches'); }
    var SC_MET = [5, 10, 15, 20, 25, 30, 40, 50, 75, 100, 150, 200];
    var SC_IMP = [[4, '3"'], [8, '1 1/2"'], [12, '1"'], [16, '3/4"'], [24, '1/2"'], [32, '3/8"'], [48, '1/4"'], [64, '3/16"'], [96, '1/8"'], [192, '1/16"']];
    /* the largest drawing scale that still fits: s = page mm per metre */
    function pickScale(sFit) {
      var i;
      if (STATE.isMetric) { for (i = 0; i < SC_MET.length; i++) if (1000 / SC_MET[i] <= sFit + 1e-9) return { s: 1000 / SC_MET[i], label: '1:' + SC_MET[i] }; return { s: sFit, label: '' }; }
      for (i = 0; i < SC_IMP.length; i++) if (1000 / SC_IMP[i][0] <= sFit + 1e-9) return { s: 1000 / SC_IMP[i][0], label: SC_IMP[i][1] + " = 1'-0\"" };
      return { s: sFit, label: '' };
    }

    /* =====================================================================
       THE PARTS AS SOLIDS -- faces (convex, ordered), edges, vertices in
       world metres, at their built place (never exploded)
       ===================================================================== */
    function hull2(pts) {
      var P = pts.slice().sort(function (a, b) { return a.x === b.x ? a.y - b.y : a.x - b.x; });
      if (P.length < 3) return P;
      function cr(o, a, b) { return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x); }
      var lo = [], up = [], i;
      for (i = 0; i < P.length; i++) { while (lo.length >= 2 && cr(lo[lo.length - 2], lo[lo.length - 1], P[i]) <= 1e-12) lo.pop(); lo.push(P[i]); }
      for (i = P.length - 1; i >= 0; i--) { while (up.length >= 2 && cr(up[up.length - 2], up[up.length - 1], P[i]) <= 1e-12) up.pop(); up.push(P[i]); }
      up.pop(); lo.pop();
      return lo.concat(up);
    }
    function facePoly(pts, n) {
      var u = norm(cross(n, Math.abs(n[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0])), w = cross(n, u), seen = {}, q = [];
      pts.forEach(function (p) {
        var k = Math.round(p[0] * 2e4) + ',' + Math.round(p[1] * 2e4) + ',' + Math.round(p[2] * 2e4);
        if (seen[k]) return; seen[k] = 1;
        q.push({ x: dot(p, u), y: dot(p, w), p: p });
      });
      return hull2(q).map(function (o) { return o.p; });
    }
    function fromTris(tris) {
      var faces = {}, list = [], verts = [], vs = {}, edges = [], bb = { x0: 1e9, x1: -1e9, y0: 1e9, y1: -1e9, z0: 1e9, z1: -1e9 };
      function addV(p) {
        var k = Math.round(p[0] * 2e4) + ',' + Math.round(p[1] * 2e4) + ',' + Math.round(p[2] * 2e4);
        if (vs[k]) return; vs[k] = 1; verts.push(p);
        if (p[0] < bb.x0) bb.x0 = p[0]; if (p[0] > bb.x1) bb.x1 = p[0];
        if (p[1] < bb.y0) bb.y0 = p[1]; if (p[1] > bb.y1) bb.y1 = p[1];
        if (p[2] < bb.z0) bb.z0 = p[2]; if (p[2] > bb.z1) bb.z1 = p[2];
      }
      tris.forEach(function (t) {
        var n = norm(cross(sub(t[1], t[0]), sub(t[2], t[0])));
        if (!n) return;
        var d = dot(n, t[0]);
        var key = Math.round(n[0] * 400) + ',' + Math.round(n[1] * 400) + ',' + Math.round(n[2] * 400) + ',' + Math.round(d * 2000);
        var F = faces[key];
        if (!F) { F = faces[key] = { n: n, pts: [] }; list.push(F); }
        F.pts.push(t[0], t[1], t[2]);
        addV(t[0]); addV(t[1]); addV(t[2]);
        edges.push([t[0], t[1]], [t[1], t[2]], [t[2], t[0]]);
      });
      list.forEach(function (F) { F.poly = facePoly(F.pts, F.n); delete F.pts; });
      return { faces: list.filter(function (F) { return F.poly.length >= 3; }), verts: verts, edges: edges, bb: bb };
    }
    function meshSolid(m, M) {
      var T = global.THREE, g = m.geometry, pos = g.attributes.position, idx = g.index;
      var n = idx ? idx.count : pos.count, v = new T.Vector3(), tris = [], i;
      function P(k) { v.fromBufferAttribute(pos, idx ? idx.getX(k) : k).applyMatrix4(M); return [v.x, v.y, v.z]; }
      for (i = 0; i + 2 < n; i += 3) tris.push([P(i), P(i + 1), P(i + 2)]);
      return fromTris(tris);
    }
    /* the roof sheet from its numbers: a thin slab, crest lines on top and
       the wave along the two ends that face the slope */
    function roofSolid(m, M) {
      var T = global.THREE, R = m.userData.roofSpec, v = new T.Vector3();
      var hw = R.w / 2, hl = R.len / 2, a = R.amp;
      function W(x, y, z) { v.set(x, y, z).applyMatrix4(M); return [v.x, v.y, v.z]; }
      var c = [W(-hw, -a, -hl), W(hw, -a, -hl), W(hw, a, -hl), W(-hw, a, -hl), W(-hw, -a, hl), W(hw, -a, hl), W(hw, a, hl), W(-hw, a, hl)];
      var Q = [[0, 3, 2, 1], [4, 5, 6, 7], [0, 1, 5, 4], [3, 7, 6, 2], [0, 4, 7, 3], [1, 2, 6, 5]], tris = [];
      Q.forEach(function (q) { tris.push([c[q[0]], c[q[1]], c[q[2]]], [c[q[0]], c[q[2]], c[q[3]]]); });
      var S = fromTris(tris);
      var ribs = Math.max(8, Math.round(R.len / R.period)), k, crest = [], waveP = [], waveN = [];
      for (k = 0; k < ribs; k++) { var zc = ((k + 0.25) / ribs - 0.5) * R.len; crest.push([W(-hw, a, zc), W(hw, a, zc)]); }
      for (k = 0; k <= ribs * 8; k++) {
        var z = -hl + R.len * k / (ribs * 8), y = Math.sin((z / R.len + 0.5) * Math.PI * 2 * ribs) * a;
        waveP.push(W(hw, y, z)); waveN.push(W(-hw, y, z));
      }
      var nTop = norm(sub(W(0, 1, 0), W(0, 0, 0))), nX = norm(sub(W(1, 0, 0), W(0, 0, 0)));
      S.faces.forEach(function (F) {
        if (dot(F.n, nTop) > 0.99) F.lines = crest;
        else if (dot(F.n, nX) > 0.99) F.wave = waveP;
        else if (dot(F.n, nX) < -0.99) F.wave = waveN;
      });
      return S;
    }
    function solids() {
      var T = global.THREE, eng = global.RackEngine, out = [];
      if (!T || !eng || !eng.rackGroup) return out;
      eng.rackGroup.updateMatrixWorld(true);
      var gm = eng.rackGroup.matrixWorld;
      eng.rackGroup.children.forEach(function (m) {
        var pt = m.userData && m.userData.partType;
        if (!DRAW[pt] || !m.isMesh || !m.geometry) return;
        var bp = m.userData.basePos || m.position;
        var M = new T.Matrix4().compose(bp, m.quaternion, m.scale).premultiply(gm);
        var S = null;
        try { S = (pt === 'roof' && m.userData.roofSpec) ? roofSolid(m, M) : meshSolid(m, M); } catch (e) { S = null; }
        if (!S || !S.faces.length) return;
        S.pt = pt; S.code = PART_MATERIAL[pt]; S.mesh = m;
        S.c = [(S.bb.x0 + S.bb.x1) / 2, (S.bb.y0 + S.bb.y1) / 2, (S.bb.z0 + S.bb.z1) / 2];
        out.push(S);
      });
      return out;
    }
    function of(pt) { return _sol.filter(function (S) { return S.pt === pt; }); }
    function maxY(list) { var y = -1e9; list.forEach(function (S) { if (S.bb.y1 > y) y = S.bb.y1; }); return y; }

    /* =====================================================================
       VIEWS -- f into the paper, r to the right, u up
       ===================================================================== */
    function view(f, up) { f = norm(f); var r = norm(cross(f, up)); return { f: f, r: r, u: cross(r, f) }; }
    var V = {
      plan:  { f: [0, -1, 0], r: [1, 0, 0], u: [0, 0, -1] },
      front: view([-1, 0, 0], [0, 1, 0]),
      back:  view([1, 0, 0], [0, 1, 0]),
      left:  view([0, 0, 1], [0, 1, 0]),
      right: view([0, 0, -1], [0, 1, 0])
    };
    function clipPoly(poly, p0, f) {
      var out = [], n = poly.length, i;
      for (i = 0; i < n; i++) {
        var a = poly[i], b = poly[(i + 1) % n], da = dot(sub(a, p0), f), db = dot(sub(b, p0), f);
        if (da >= -1e-9) out.push(a);
        if ((da >= -1e-9) !== (db >= -1e-9)) out.push(lerp(a, b, da / (da - db)));
      }
      return out;
    }
    function clipSeg(a, b, p0, f) {
      var da = dot(sub(a, p0), f), db = dot(sub(b, p0), f);
      if (da < 0 && db < 0) return null;
      if (da >= 0 && db >= 0) return [a, b];
      var p = lerp(a, b, da / (da - db));
      return da >= 0 ? [a, p] : [p, b];
    }
    /* what the view sees: faces (cut away in front of a section), the cut
       faces of the parts the plane goes through, and the extent */
    function scene(Vw, cut, list) {
      var items = [], cuts = [], ext = { x0: 1e9, x1: -1e9, y0: 1e9, y1: -1e9 };
      function grow(p) { var x = dot(p, Vw.r), y = dot(p, Vw.u); if (x < ext.x0) ext.x0 = x; if (x > ext.x1) ext.x1 = x; if (y < ext.y0) ext.y0 = y; if (y > ext.y1) ext.y1 = y; }
      (list || _sol).forEach(function (S) {
        if (cut && cut.skip && cut.skip(S)) return;
        S.faces.forEach(function (F) {
          if (dot(F.n, Vw.f) > -1e-3) return;
          var poly = cut ? clipPoly(F.poly, cut.p0, Vw.f) : F.poly;
          if (poly.length < 3) return;
          var d = 0; poly.forEach(function (p) { d += dot(p, Vw.f); grow(p); });
          items.push({ d: d / poly.length, poly: poly, S: S, F: F });
        });
        if (!cut) return;
        var pts = [];
        S.edges.forEach(function (e) {
          var da = dot(sub(e[0], cut.p0), Vw.f), db = dot(sub(e[1], cut.p0), Vw.f);
          if (Math.abs(da) < 1e-7) pts.push(e[0]);
          if ((da > 1e-7 && db < -1e-7) || (da < -1e-7 && db > 1e-7)) pts.push(lerp(e[0], e[1], da / (da - db)));
        });
        if (pts.length < 3) return;
        var h = hull2(pts.map(function (p) { return { x: dot(p, Vw.r), y: dot(p, Vw.u), p: p }; }));
        if (h.length < 3) return;
        var ar = 0, i;
        for (i = 0; i < h.length; i++) { var a2 = h[i], b2 = h[(i + 1) % h.length]; ar += a2.x * b2.y - b2.x * a2.y; }
        if (Math.abs(ar) < 1e-7) return;
        cuts.push({ poly: h.map(function (o) { return o.p; }), S: S });
      });
      items.sort(function (a, b) { return b.d - a.d; });
      return { items: items, cuts: cuts, ext: ext };
    }
    function extOf(Vw, list) {
      var e = { x0: 1e9, x1: -1e9, y0: 1e9, y1: -1e9 };
      (list || _sol).forEach(function (S) { S.verts.forEach(function (p) { var x = dot(p, Vw.r), y = dot(p, Vw.u); if (x < e.x0) e.x0 = x; if (x > e.x1) e.x1 = x; if (y < e.y0) e.y0 = y; if (y > e.y1) e.y1 = y; }); });
      return e;
    }
    /* world -> paper: the extent's centre on (cx, cy) at s mm per metre */
    function mapper(Vw, s, cx, cy, ext) {
      var ox = cx - (ext.x0 + ext.x1) / 2 * s, oy = cy + (ext.y0 + ext.y1) / 2 * s;
      return {
        s: s, V: Vw,
        P: function (p) { return [ox + dot(p, Vw.r) * s, oy - dot(p, Vw.u) * s]; },
        X: function (x) { return ox + x * s; }, Y: function (y) { return oy - y * s; },
        box: { x0: ox + ext.x0 * s, x1: ox + ext.x1 * s, y0: oy - ext.y1 * s, y1: oy - ext.y0 * s }
      };
    }

    /* ---------- paper drawing ---------- */
    function poly(doc, pts, style) {
      if (pts.length < 2) return;
      var segs = [], i;
      for (i = 1; i < pts.length; i++) segs.push([pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]]);
      doc.lines(segs, pts[0][0], pts[0][1], [1, 1], style, true);
    }
    function ink(doc, w, c) { c = c || INK; doc.setDrawColor(c[0], c[1], c[2]); doc.setLineWidth(w); doc.setLineDashPattern([], 0); }
    function hatch(doc, pts, step) {
      var x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9;
      pts.forEach(function (p) { if (p[0] < x0) x0 = p[0]; if (p[0] > x1) x1 = p[0]; if (p[1] < y0) y0 = p[1]; if (p[1] > y1) y1 = p[1]; });
      var k;
      for (k = x0 - (y1 - y0); k < x1; k += step) {
        /* the line x - y = k - y0 ... clipped to the convex polygon */
        var hits = [], i, n = pts.length;
        for (i = 0; i < n; i++) {
          var a = pts[i], b = pts[(i + 1) % n];
          var fa = (a[0] - a[1]) - (k - y0), fb = (b[0] - b[1]) - (k - y0);
          if ((fa >= 0) !== (fb >= 0)) { var t = fa / (fa - fb); hits.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]); }
        }
        if (hits.length >= 2) doc.line(hits[0][0], hits[0][1], hits[1][0], hits[1][1]);
      }
    }
    function drawScene(doc, sc, Mp, opt) {
      opt = opt || {};
      sc.items.forEach(function (it) {
        var c = FILL[it.S.pt] || [200, 170, 130], P = it.poly.map(Mp.P);
        doc.setFillColor(c[0], c[1], c[2]); ink(doc, 0.16);
        poly(doc, P, 'FD');
        if (it.F.lines && !opt.cut) { ink(doc, 0.08, [70, 72, 78]); it.F.lines.forEach(function (l) { var a = Mp.P(l[0]), b = Mp.P(l[1]); doc.line(a[0], a[1], b[0], b[1]); }); }
        if (it.F.wave) {
          ink(doc, 0.12, [60, 62, 68]);
          var w = it.F.wave, i;
          for (i = 1; i < w.length; i++) {
            var sg = opt.cut ? clipSeg(w[i - 1], w[i], opt.cut.p0, Mp.V.f) : [w[i - 1], w[i]];
            if (!sg) continue;
            var a2 = Mp.P(sg[0]), b2 = Mp.P(sg[1]); doc.line(a2[0], a2[1], b2[0], b2[1]);
          }
        }
      });
      sc.cuts.forEach(function (ct) {
        var c = FILL[ct.S.pt] || [200, 170, 130], P = ct.poly.map(Mp.P);
        doc.setFillColor(Math.round(c[0] * 0.9 + 25), Math.round(c[1] * 0.9 + 25), Math.round(c[2] * 0.9 + 25));
        ink(doc, 0.1); poly(doc, P, 'F');
        ink(doc, 0.1); hatch(doc, P, 0.9);
        ink(doc, 0.45); poly(doc, P, 'S');
      });
    }

    /* ---------- text ---------- */
    var FNT = 'helvetica';
    function txt(doc, s, x, y, size, o) {
      o = o || {};
      doc.setFont(FNT, o.bold ? 'bold' : 'normal'); doc.setFontSize(size);
      var c = o.c || [30, 26, 22]; doc.setTextColor(c[0], c[1], c[2]);
      s = String(s);
      var w = doc.getTextWidth(s), ang = o.angle || 0;
      if (!ang) { doc.text(s, x, y, { align: o.align || 'left' }); return w; }
      var r = ang * Math.PI / 180, dx = Math.cos(r), dy = -Math.sin(r), k = (o.align === 'center') ? w / 2 : (o.align === 'right' ? w : 0);
      doc.text(s, x - dx * k, y - dy * k, { angle: ang });
      return w;
    }
    function tw(doc, s, size, bold) { doc.setFont(FNT, bold ? 'bold' : 'normal'); doc.setFontSize(size); return doc.getTextWidth(String(s)); }
    var DS = 6.4, CAP = 1.6;   /* dimension text size (pt), cap height (mm) */

    /* =====================================================================
       DIMENSIONS -- chains along x or y on paper, ticks, extension lines
       from the feature (foot) to the line, text on the drawing's side of
       the line, too-short ones moved out past the line
       c = { axis: 'h'|'v', at: [paper coord along], foot: [paper coord
       across, per point], base: paper coord across of the line, out: +1|-1 }
       ===================================================================== */
    function chain(doc, c) {
      var at = c.at.slice(), i, n = at.length;
      if (n < 2) return;
      var ord = at.map(function (v, i2) { return { v: v, f: c.foot[i2] }; }).sort(function (a, b) { return a.v - b.v; });
      var uniq = [];
      ord.forEach(function (o) { if (!uniq.length || Math.abs(o.v - uniq[uniq.length - 1].v) > 0.05) uniq.push(o); });
      if (uniq.length < 2) return;
      var H = c.axis === 'h', base = c.base, out = c.out;
      function XY(along, across) { return H ? [along, across] : [across, along]; }
      ink(doc, 0.13, [35, 35, 35]);
      var a0 = XY(uniq[0].v, base), a1 = XY(uniq[uniq.length - 1].v, base);
      doc.line(a0[0], a0[1], a1[0], a1[1]);
      uniq.forEach(function (o) {
        var g = (o.f == null) ? base - out * 2 : o.f;
        var s0 = XY(o.v, g + out * 0.8), s1 = XY(o.v, base + out * 1.2);
        if ((base - g) * out > 1.0) { ink(doc, 0.09, [80, 80, 80]); doc.line(s0[0], s0[1], s1[0], s1[1]); }
        ink(doc, 0.22, [25, 25, 25]);
        var t0 = XY(o.v - 0.8, base + 0.8), t1 = XY(o.v + 0.8, base - 0.8);
        if (!H) { t0 = [base - 0.8, o.v + 0.8]; t1 = [base + 0.8, o.v - 0.8]; }
        doc.line(t0[0], t0[1], t1[0], t1[1]);
      });
      var lastEnd = [-1e9, -1e9];
      for (i = 0; i + 1 < uniq.length; i++) {
        var len = uniq[i + 1].v - uniq[i].v, mid = (uniq[i].v + uniq[i + 1].v) / 2;
        var s = (c.texts && c.texts[i] != null) ? c.texts[i] : fD(len / c.s);
        var w = tw(doc, s, DS), side = -out, along = mid, row = 0;
        if (w + 1.2 > len) {
          /* too short: out past the line, on the first of two rows that is
             free there, else after the last one on the emptier row */
          side = out;
          if (along - w / 2 >= lastEnd[0] + 0.8) row = 0;
          else if (along - w / 2 >= lastEnd[1] + 0.8) row = 1;
          else { row = (lastEnd[0] <= lastEnd[1]) ? 0 : 1; along = lastEnd[row] + 0.8 + w / 2; }
          lastEnd[row] = along + w / 2;
        }
        var acr = base + (side < 0 ? -(0.8 + row * (CAP + 1.0)) : (0.8 + CAP + row * (CAP + 1.0)));
        if (H) txt(doc, s, along, acr, DS, { align: 'center' });
        else txt(doc, s, acr, along, DS, { align: 'center', angle: 90 });
      }
    }
    /* level marks beside an elevation: a short line from the drawing, a
       triangle, the value; marks too close are pushed apart */
    function levels(doc, Mp, list, xEdge, side) {
      var L2 = list.map(function (o) { return { y: Mp.Y(o.y), v: o.y, t: o.t }; }).sort(function (a, b) { return b.y - a.y; });
      var i, ty = [];
      for (i = 0; i < L2.length; i++) { var y = L2[i].y; if (i && ty[i - 1] - y < 3.4) y = ty[i - 1] - 3.4; ty.push(y); }
      L2.forEach(function (o, k) {
        var x1 = xEdge + side * 4, x2 = xEdge + side * 7;
        doc.setLineDashPattern([1.2, 0.6, 0.2, 0.6], 0); ink(doc, 0.1, [70, 70, 70]); doc.setLineDashPattern([1.2, 0.6, 0.2, 0.6], 0);
        doc.line(xEdge + side * 0.8, o.y, x1, o.y);
        doc.setLineDashPattern([], 0);
        if (Math.abs(ty[k] - o.y) > 0.05) { ink(doc, 0.1, [70, 70, 70]); doc.line(x1, o.y, x2, ty[k]); }
        else { ink(doc, 0.1, [70, 70, 70]); doc.line(x1, o.y, x2, o.y); }
        doc.setFillColor(25, 25, 25);
        doc.triangle(x2 - 0.9, ty[k] - 1.3, x2 + 0.9, ty[k] - 1.3, x2, ty[k], 'F');
        var s = fL(o.v) + (o.t ? '  ' + o.t : '');
        txt(doc, s, x2 + side * 1.6, ty[k] + 0.9, DS, { align: side > 0 ? 'left' : 'right', bold: true });
      });
    }
    /* a numbered material tag on a part, and the legend under the views */
    var LEG = [['decking', 'TST00001'], ['post', 'KVH10060'], ['batten', 'KVL20040'], ['roof', 'ROR12004']];
    function tag(doc, x, y, n) {
      doc.setFillColor(255, 255, 255); ink(doc, 0.2, [25, 25, 25]);
      doc.circle(x, y, 2.0, 'FD');
      txt(doc, String(n), x, y + 0.9, 6.5, { align: 'center', bold: true });
    }
    function tagsOn(doc, sc, Mp) {
      var done = {}, placed = [];
      LEG.forEach(function (L1, k) {
        var cand = [];
        sc.items.forEach(function (it) {
          var match = (L1[0] === 'post') ? (PART_MATERIAL[it.S.pt] === 'KVH10060') : (it.S.pt === L1[0]);
          if (!match) return;
          var P = it.poly.map(Mp.P), x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9;
          P.forEach(function (p) { if (p[0] < x0) x0 = p[0]; if (p[0] > x1) x1 = p[0]; if (p[1] < y0) y0 = p[1]; if (p[1] > y1) y1 = p[1]; });
          var a = (x1 - x0) * (y1 - y0);
          if (x1 - x0 < 3.6 || y1 - y0 < 3.6) a = a * 0.01;
          cand.push({ a: a, x: (x0 + x1) / 2, y: (y0 + y1) / 2 });
        });
        cand.sort(function (p, q) { return q.a - p.a; });
        for (var ci = 0; ci < cand.length; ci++) {
          var c2 = cand[ci];
          if (!(c2.a > 1)) break;
          if (placed.some(function (q) { return Math.abs(q[0] - c2.x) < 6 && Math.abs(q[1] - c2.y) < 6; })) continue;
          tag(doc, c2.x, c2.y, k + 1); done[k] = 1; placed.push([c2.x, c2.y]);
          break;
        }
      });
      return done;
    }
    function legend(doc, x, y, used) {
      var w0 = txt(doc, T_('bpLegend', 'Legend').toUpperCase(), x, y, 6.5, { bold: true, c: [120, 110, 95] });
      var xx = x + w0 + 5;
      LEG.forEach(function (L1, k) {
        if (used && !used[k]) return;
        tag(doc, xx + 2, y - 0.9, k + 1);
        var w = txt(doc, catName(L1[1]), xx + 5.5, y, 6.6);
        xx += w + 12;
      });
    }

    /* ---------- the sheet: number top right, scale + units at the foot ---------- */
    var TOP = 30, BOT = 185, LM = 12, RM = 285;
    function sheetHead(doc, no, scaleLabel) {
      txt(doc, String(no), RM, 22, 15, { align: 'right', bold: true });
      txt(doc, T_('sheet', 'Sheet').toUpperCase(), RM - tw(doc, String(no), 15, true) - 2, 22, 6.5, { align: 'right', c: [150, 140, 120] });
      var s = unitsLine() + (scaleLabel ? ('   ' + T_('pdfScale', 'Scale') + ' ' + scaleLabel + '  (A4)') : '');
      txt(doc, s, LM, BOT + 2.5, 6.5, { c: [120, 110, 95] });
    }
    function viewTitle(doc, x, y, t, sub) {
      txt(doc, String(t).toUpperCase(), x, y, 8.5, { bold: true });
      if (sub) txt(doc, sub, x, y + 3.6, 6.5, { c: [120, 110, 95] });
    }
    function picture(doc, pic, box) {
      if (!pic || !pic.url) return null;
      var k = Math.min(box.w / pic.w, box.h / pic.h), w = pic.w * k, h = pic.h * k;
      var x = box.x + (box.w - w) / 2, y = box.y + (box.h - h) / 2;
      try { doc.addImage(pic.url, 'JPEG', x, y, w, h); } catch (e) { return null; }
      var s = w / (pic.ext.x1 - pic.ext.x0);
      var Vw = pic.V;
      return {
        s: s, V: Vw,
        P: function (p) { return [x + (dot(p, Vw.r) - pic.ext.x0) * s, y + (pic.ext.y1 - dot(p, Vw.u)) * s]; },
        box: { x0: x, y0: y, x1: x + w, y1: y + h }
      };
    }

    /* =====================================================================
       THE MODEL'S OWN NUMBERS, read off the solids
       ===================================================================== */
    function geo() {
      var tw2 = DIMS.twoByFour.w, th = DIMS.twoByFour.h, L = STATE.L, W = STATE.W, H = STATE.H;
      var deck = of('decking'), roof = of('roof'), raf = of('rafter'), posts = of('post');
      var hiPosts = posts.filter(function (S) { return S.c[0] > 0; }), loPosts = posts.filter(function (S) { return S.c[0] < 0; });
      var g = {
        L: L, W: W, H: H, tw: tw2, th: th,
        deckTop: maxY(deck), frameBot: DIMS.deckHeight - th, frameTop: DIMS.deckHeight,
        hBack: loPosts.length ? maxY(loPosts) : H, hFront: hiPosts.length ? maxY(hiPosts) : H + th,
        rafTop: maxY(raf), roofTop: maxY(roof),
        rafZ: raf.map(function (S) { return S.c[2]; }).sort(function (a, b) { return a - b; }),
        nDeck: deck.length,
        roofBB: roof.length ? roof[0].bb : null,
        rafX0: 1e9, rafX1: -1e9,
        pitch: Math.atan((H + th - H) / (W + 2 * tw2)) * 180 / Math.PI
      };
      raf.forEach(function (S) { if (S.bb.x0 < g.rafX0) g.rafX0 = S.bb.x0; if (S.bb.x1 > g.rafX1) g.rafX1 = S.bb.x1; });
      /* the posts along the rack, both faces of each (the middle pair of a
         long rack included) */
      var pz = [];
      (loPosts.length ? loPosts : posts).forEach(function (S) { pz.push(S.bb.z0, S.bb.z1); });
      pz.sort(function (a, b) { return a - b; });
      g.postZ = pz.filter(function (z, i) { return !i || Math.abs(z - pz[i - 1]) > 1e-4; });
      if (g.postZ.length < 2) g.postZ = [-L / 2, -L / 2 + th, L / 2 - th, L / 2];
      if (deck.length > 1) {
        var zs = deck.map(function (S) { return S.bb.z0; }).sort(function (a, b) { return a - b; });
        g.deckGap = (zs[1] - zs[0]) - DIMS.decking.h;
      } else g.deckGap = 0;
      return g;
    }

    /* =====================================================================
       THE PICTURES (async, before the PDF): an orthographic camera at an
       azimuth / elevation, its axes read back for the overlays
       ===================================================================== */
    function camDir(az, el) { var a = az * Math.PI / 180, e = el * Math.PI / 180; return [Math.cos(e) * Math.cos(a), Math.sin(e), Math.cos(e) * Math.sin(a)]; }
    function render(R, opt) {
      var T = global.THREE, eng = global.RackEngine, kids = eng.rackGroup.children;
      var d = camDir(opt.az, opt.el), list = opt.frame || _sol;
      var cen = [0, 0, 0], e0 = { x0: 1e9, x1: -1e9, y0: 1e9, y1: -1e9, z0: 1e9, z1: -1e9 };
      list.forEach(function (S) { var b = S.bb; e0.x0 = Math.min(e0.x0, b.x0); e0.x1 = Math.max(e0.x1, b.x1); e0.y0 = Math.min(e0.y0, b.y0); e0.y1 = Math.max(e0.y1, b.y1); e0.z0 = Math.min(e0.z0, b.z0); e0.z1 = Math.max(e0.z1, b.z1); });
      cen = [(e0.x0 + e0.x1) / 2, (e0.y0 + e0.y1) / 2, (e0.z0 + e0.z1) / 2];
      var cam = new T.OrthographicCamera(-1, 1, 1, -1, 0.05, 60);
      cam.position.set(cen[0] + d[0] * 20, cen[1] + d[1] * 20, cen[2] + d[2] * 20);
      cam.up.set(0, 1, 0); cam.lookAt(cen[0], cen[1], cen[2]); cam.updateMatrixWorld(true);
      var me = cam.matrixWorld.elements, Vw = { r: [me[0], me[1], me[2]], u: [me[4], me[5], me[6]], f: [-me[8], -me[9], -me[10]] };
      var ext = extOf(Vw, list);
      var padX = (ext.x1 - ext.x0) * 0.04 + 0.03, padY = (ext.y1 - ext.y0) * 0.04 + 0.03;
      ext.x0 -= padX; ext.x1 += padX; ext.y0 -= padY; ext.y1 += padY;
      var pw = opt.w || 1500, ph = opt.h || 1100, ar = pw / ph, ew = ext.x1 - ext.x0, eh = ext.y1 - ext.y0;
      if (ew / eh > ar) { var nh = ew / ar, dy = (nh - eh) / 2; ext.y0 -= dy; ext.y1 += dy; }
      else { var nw = eh * ar, dx = (nw - ew) / 2; ext.x0 -= dx; ext.x1 += dx; }
      var cr = dot([cam.position.x, cam.position.y, cam.position.z], Vw.r), cu = dot([cam.position.x, cam.position.y, cam.position.z], Vw.u);
      cam.left = ext.x0 - cr; cam.right = ext.x1 - cr; cam.top = ext.y1 - cu; cam.bottom = ext.y0 - cu;
      cam.updateProjectionMatrix();

      var saved = [], mats = [], ghost = opt.ghostMat;
      kids.forEach(function (c) {
        saved.push([c, c.visible, c.position.clone(), c.material]);
        var pt = c.userData && c.userData.partType, mode = opt.mode ? opt.mode(pt) : (DRAW[pt] ? 'n' : 'h');
        if (c.userData && c.userData.basePos) c.position.copy(c.userData.basePos);
        if (mode === 'h') { c.visible = false; return; }
        c.visible = true;
        var es = c.children.filter(function (k) { return k.userData && k.userData.isEdgeLine; });
        es.forEach(function (k) { saved.push([k, k.visible, null, null]); k.visible = (mode !== 'g'); });
        if (mode === 'g' && c.isMesh && ghost) c.material = ghost;
        else if (c.material && mats.indexOf(c.material) < 0) { mats.push(c.material); }
      });
      var mstate = mats.map(function (m) { var s = [m, m.opacity, m.transparent, m.depthWrite]; m.opacity = 1; m.transparent = false; m.depthWrite = true; return s; });
      var url = null;
      try {
        R.setSize(pw, ph, false);
        R.render(eng.scene, cam);
        url = R.domElement.toDataURL('image/jpeg', 0.9);
      } catch (eR) { url = null; _note += ' render failed (' + ((eR && eR.message) || eR) + ')'; }
      mstate.forEach(function (s) { s[0].opacity = s[1]; s[0].transparent = s[2]; s[0].depthWrite = s[3]; });
      for (var i = saved.length - 1; i >= 0; i--) {
        var sv = saved[i]; sv[0].visible = sv[1];
        if (sv[2]) sv[0].position.copy(sv[2]);
        if (sv[3]) sv[0].material = sv[3];
      }
      return { url: url, w: pw, h: ph, ext: ext, V: Vw };
    }

    var STEPS = [
      { types: ['frame'], screws: ['screw-frame'], k: ['assembly_2'], fb: ['Frame'] },
      { types: ['decking'], screws: ['screw-decking'], k: ['assembly_4'], fb: ['Decking'] },
      { types: ['post'], screws: ['screw-post-bottom'], k: ['assembly_6'], fb: ['Posts'] },
      { types: ['rail-side', 'rail-long'], screws: ['screw-rail-side', 'screw-rail-long'], k: ['assembly_8', 'assembly_10'], fb: ['Side rails', 'Long rails'] },
      { types: ['rafter'], screws: ['screw-rafter'], k: ['assembly_12'], fb: ['Rafters'] },
      { types: ['batten'], screws: ['screw-batten'], k: ['assembly_14'], fb: ['Battens'] },
      { types: ['roof'], screws: ['screw-roof'], k: ['assembly_16'], fb: ['Roofing'] },
      { types: ['brace'], screws: ['screw-brace'], k: ['assembly_18'], fb: ['Brace'] }
    ];
    function stepTitle(st) { return st.k.map(function (k, i) { return T_(k, st.fb[i]); }).join(' + '); }

    /* the marks: every distinct piece, numbered in the order it is built
       (Matt's 1.01, 1.02 ...) */
    function marks() {
      var list = [], by = {};
      var inst = (global.RackInstances && global.RackInstances.all) ? global.RackInstances.all() : [];
      STEPS.forEach(function (st, si) {
        inst.filter(function (i) { return st.types.indexOf(i.partType) >= 0; })
          .sort(function (a, b) { return st.types.indexOf(a.partType) - st.types.indexOf(b.partType) || (b.length || 0) - (a.length || 0); })
          .forEach(function (i) {
            var isRoof = (i.partType === 'roof'), len = isRoof ? 0 : (i.length || 0);
            var key = (i.boqCode || i.partType) + '|' + (isRoof ? 'roof' : fD(len)) + '|' + (i.angle || 0);
            var m = by[key];
            if (!m) {
              var n = list.length + 1;
              m = by[key] = { no: '1.' + (n < 10 ? '0' : '') + n, pt: i.partType, pts: [], code: i.boqCode, len: len, angle: i.angle || 0, qty: 0, step: si, dims: i.dimensions || null, inst: [] };
              list.push(m);
            }
            if (m.pts.indexOf(i.partType) < 0) m.pts.push(i.partType);
            m.qty++; m.inst.push(i);
          });
      });
      return list;
    }
    function markOfMesh(mesh) {
      var r = null;
      (_marks || []).forEach(function (m) { m.inst.forEach(function (i) { if (i.mesh === mesh) r = m; }); });
      return r;
    }
    function screwCount(tag) {
      var n = 0, eng = global.RackEngine;
      eng.rackGroup.children.forEach(function (c) { if (c.userData && c.userData.partType === tag) n++; });
      return n;
    }

    async function prepare() {
      _note = ''; _pics = {}; _sol = solids(); _marks = marks();
      var T = global.THREE;
      if (!T || !_sol.length) { _note = 'no model'; return false; }
      var cv = document.createElement('canvas'), R = null;
      try {
        R = new T.WebGLRenderer({ canvas: cv, antialias: true, alpha: false, preserveDrawingBuffer: true });
        R.setPixelRatio(1);
        R.setClearColor(0xffffff, 1);
        var live = global.RackEngine.renderer;
        if (live) { R.toneMapping = live.toneMapping; if ('outputColorSpace' in live) R.outputColorSpace = live.outputColorSpace; }
      } catch (eW) { _note = 'no WebGL for the pictures'; R = null; }
      var g0 = geo();
      var ghost = new T.MeshLambertMaterial({ color: 0xd6d6d6 });
      var full = _sol;
      var noRoof = _sol.filter(function (S) { return S.pt !== 'roof' && S.pt !== 'batten'; });
      function normal(pt) { return DRAW[pt] ? 'n' : 'h'; }
      function framing(pt) { return (DRAW[pt] && pt !== 'roof' && pt !== 'batten') ? 'n' : 'h'; }
      if (R) {
        _pics.scheme = render(R, { az: -38, el: 24, w: 1300, h: 1000, mode: normal });
        _pics.ovA = render(R, { az: -38, el: 22, w: 1300, h: 1200, mode: normal });
        _pics.ovB = render(R, { az: 142, el: 22, w: 1300, h: 1200, mode: normal });
        _pics.archA = render(R, { az: -38, el: 22, w: 1400, h: 1000, mode: normal });
        _pics.archB = render(R, { az: 142, el: 22, w: 1400, h: 1000, mode: normal });
        _pics.frA = render(R, { az: -38, el: 26, w: 1400, h: 1000, mode: framing, frame: noRoof });
        _pics.frB = render(R, { az: 142, el: 26, w: 1400, h: 1000, mode: framing, frame: noRoof });
        _pics.steps = STEPS.map(function (st, si) {
          var done = {}, now = {};
          STEPS.forEach(function (s2, sj) { if (sj < si) s2.types.forEach(function (t) { done[t] = 1; }); });
          st.types.concat(st.screws).forEach(function (t) { now[t] = 1; });
          return render(R, { az: -38, el: 28, w: 1100, h: 900, frame: full, ghostMat: ghost,
            mode: function (pt) { return now[pt] ? 'n' : (done[pt] ? 'g' : 'h'); } });
        });
      }
      try { if (R) { R.dispose(); if (R.forceContextLoss) R.forceContextLoss(); } } catch (eD) {}
      try { ghost.dispose(); } catch (eG) {}
      _note = (_note ? _note + '; ' : '') + _sol.length + ' parts, ' + _marks.length + ' marks, ' + Object.keys(_pics).length + ' picture sets, deck ' + g0.nDeck + ' boards, gap ' + fD(g0.deckGap) +
        ', pitch ' + g0.pitch.toFixed(1) + ' deg';
      return true;
    }

    /* =====================================================================
       THE SHEETS
       ===================================================================== */
    var FONT = function (ctx) { FNT = (ctx && ctx.fontFamily) || 'helvetica'; };

    /* 101 -- FLOOR PLAN, 3D SCHEME, NOTES */
    function sheetPlan(doc, X, ctx) {
      FONT(ctx);
      var g = geo(), Vw = V.plan, yc = (g.deckTop + g.hBack - g.th) / 2;
      var cut = { p0: [0, yc, 0] };
      var sc = scene(Vw, cut);
      var roof = of('roof'), ext = extOf(Vw, roof.length ? roof : _sol);
      var box = { x: LM, y: TOP + 6, w: 150, h: BOT - TOP - 10 };
      var mL = 24, mR = 10, mT = 8, mB = 22;
      var sFit = Math.min((box.w - mL - mR) / (ext.x1 - ext.x0), (box.h - mT - mB) / (ext.y1 - ext.y0));
      var SC = pickScale(sFit);
      var Mp = mapper(Vw, SC.s, box.x + mL + (box.w - mL - mR) / 2, box.y + mT + (box.h - mT - mB) / 2, ext);
      viewTitle(doc, LM, TOP, T_('viewPlan', 'Floor plan'), SC.label);
      drawScene(doc, sc, Mp, { cut: cut });
      /* the roof above, dashed */
      if (roof.length) {
        var rp = hull2(roof[0].verts.map(function (p) { var q = Mp.P(p); return { x: q[0], y: q[1] }; })).map(function (o) { return [o.x, o.y]; });
        ink(doc, 0.18, [60, 60, 60]); doc.setLineDashPattern([1.6, 1.0], 0); poly(doc, rp, 'S'); doc.setLineDashPattern([], 0);
      }
      /* section marks 1-1 (x = 0, looking at the back) and 2-2 */
      var zc = secZ(g);
      secMark(doc, '1', Mp, 'x');
      secMark(doc, '2', Mp, 'z', zc);
      /* along the rack (paper y): posts and the clear between, the rack, the roof */
      var bx = Mp.box, zL = -g.L / 2, zR = g.L / 2;
      var ys = g.postZ.map(function (z) { return Mp.P([0, 0, z])[1]; });
      var footL = Mp.P([-g.W / 2, 0, 0])[0];
      chain(doc, { axis: 'v', at: ys, foot: ys.map(function () { return footL; }), base: bx.x0 - 7, out: -1, s: SC.s });
      ys = [Mp.P([0, 0, zR])[1], Mp.P([0, 0, zL])[1]];
      chain(doc, { axis: 'v', at: ys, foot: [footL, footL], base: bx.x0 - 15, out: -1, s: SC.s });
      ys = [ys[0], 0, 0, ys[1]];
      if (g.roofBB) {
        var yr = [Mp.P([0, 0, g.roofBB.z1])[1], Mp.P([0, 0, g.roofBB.z0])[1]];
        chain(doc, { axis: 'v', at: [yr[0], ys[0], ys[3], yr[1]], foot: [null, footL, footL, null], base: bx.x0 - 22, out: -1, s: SC.s });
      }
      /* across (paper x): posts, the frame between them, the roof */
      var xs = [Mp.P([-g.W / 2, 0, 0])[0], Mp.P([-g.W / 2 + g.tw, 0, 0])[0], Mp.P([g.W / 2 - g.tw, 0, 0])[0], Mp.P([g.W / 2, 0, 0])[0]];
      var footB = Mp.P([0, 0, zR])[1];
      chain(doc, { axis: 'h', at: xs, foot: [footB, footB, footB, footB], base: bx.y1 + 7, out: 1, s: SC.s });
      chain(doc, { axis: 'h', at: [xs[0], xs[3]], foot: [footB, footB], base: bx.y1 + 14, out: 1, s: SC.s });
      if (g.roofBB) {
        var xr = [Mp.P([g.roofBB.x0, 0, 0])[0], xs[0], xs[3], Mp.P([g.roofBB.x1, 0, 0])[0]];
        chain(doc, { axis: 'h', at: xr, foot: [null, footB, footB, null], base: bx.y1 + 21, out: 1, s: SC.s });
      }
      /* the decking, in words */
      var boards = String(T_('rackDocBoards', '[n] boards, gap [g]')).replace('[n]', String(g.nDeck)).replace('[g]', fD(Math.max(0, g.deckGap)));
      txt(doc, catName('TST00001') + ': ' + boards, LM, BOT - 2.5, 6.5, { c: [80, 70, 60] });

      /* 3D scheme top right */
      var px = 172, pw = RM - px;
      viewTitle(doc, px, TOP, T_('viewIso', 'Isometric'), '');
      picture(doc, _pics && _pics.scheme, { x: px, y: TOP + 3, w: pw, h: 70 });
      /* the notes */
      var y = TOP + 82;
      txt(doc, T_('notes', 'Notes').toUpperCase(), px, y, 7.5, { bold: true });
      y += 5;
      doc.setFont(FNT, 'normal'); doc.setFontSize(6.6); doc.setTextColor(50, 44, 38);
      var k;
      for (k = 1; k <= 8; k++) {
        var n = T_('rackDocNote' + k, NOTES[k - 1]);
        if (!n) continue;
        var lines = doc.splitTextToSize('- ' + n, pw - 2);
        lines.forEach(function (ln) { if (y < BOT - 1) { doc.text(ln, px, y); y += 2.9; } });
        y += 0.9;
      }
      sheetHead(doc, 101, SC.label);
    }
    var NOTES = [
      'Check every dimension on site before cutting and assembly.',
      'Read this sheet together with all the other sheets of the set.',
      'Stand the rack on a level, firm base or on concrete footings; correct any unevenness before you start.',
      'Use lumber treated against moisture, decay and insects, and seal fresh cuts.',
      'Use exterior-grade screws made for lumber and fix them as their maker recommends.',
      'Fix the corrugated roofing with sealing-washer screws as the panel maker specifies.',
      'Keep air moving around the stacked firewood and let water drain away from it.',
      'Follow standard safety practice when cutting and assembling.'
    ];
    function secZ(g) {
      var z = g.rafZ, best = 0, i;
      if (z.length >= 2) { var mid = Math.floor((z.length - 1) / 2); best = (z[mid] + z[mid + 1]) / 2; }
      for (i = 0; i < z.length; i++) if (Math.abs(z[i] - best) < g.tw) best += g.tw * 1.5;
      return best;
    }
    function secMark(doc, label, Mp, axis, zc) {
      var a, b, dir;
      if (axis === 'x') {
        a = Mp.P([0, 0, -STATE.L / 2 - DIMS.roofOverZ - 0.06]); b = Mp.P([0, 0, STATE.L / 2 + DIMS.roofOverZ + 0.06]); dir = [-1, 0];
      } else {
        a = Mp.P([-STATE.W / 2 - DIMS.twoByFour.w - DIMS.roofOverX - 0.06, 0, zc]); b = Mp.P([STATE.W / 2 + DIMS.twoByFour.w + DIMS.rafterExt + DIMS.roofOverX + 0.06, 0, zc]);
        dir = (Mp.V.u[2] < 0) ? [0, -1] : [0, 1];
      }
      ink(doc, 0.3, [20, 20, 20]); doc.setLineDashPattern([4, 1, 0.6, 1], 0);
      doc.line(a[0], a[1], b[0], b[1]); doc.setLineDashPattern([], 0);
      [a, b].forEach(function (p) {
        var q = [p[0] + dir[0] * 3.2, p[1] + dir[1] * 3.2];
        ink(doc, 0.3, [20, 20, 20]); doc.line(p[0], p[1], q[0], q[1]);
        doc.setFillColor(20, 20, 20);
        if (dir[0]) doc.triangle(q[0], q[1], q[0] - dir[0] * 1.4, q[1] - 1.0, q[0] - dir[0] * 1.4, q[1] + 1.0, 'F');
        else doc.triangle(q[0], q[1], q[0] - 1.0, q[1] - dir[1] * 1.4, q[0] + 1.0, q[1] - dir[1] * 1.4, 'F');
        txt(doc, label, q[0] + dir[0] * 2.4 + (dir[0] ? 0 : 2.6), q[1] + dir[1] * 2.4 + (dir[0] ? 1.0 : 1.0), 8, { bold: true, align: 'center' });
      });
    }

    /* 102 -- OVERALL DIMENSIONS (two 3D views with 3D dimensions) */
    function dim3(doc, Mp, a, b, off, txtS) {
      var A = Mp.P(a), B = Mp.P(b), A2 = Mp.P(add(a, off)), B2 = Mp.P(add(b, off));
      ink(doc, 0.09, [70, 70, 70]);
      var ex = [A2[0] - A[0], A2[1] - A[1]], el = Math.sqrt(ex[0] * ex[0] + ex[1] * ex[1]) || 1, eu = [ex[0] / el, ex[1] / el];
      doc.line(A[0] + eu[0] * 0.8, A[1] + eu[1] * 0.8, A2[0] + eu[0] * 1.2, A2[1] + eu[1] * 1.2);
      doc.line(B[0] + eu[0] * 0.8, B[1] + eu[1] * 0.8, B2[0] + eu[0] * 1.2, B2[1] + eu[1] * 1.2);
      ink(doc, 0.14, [25, 25, 25]); doc.line(A2[0], A2[1], B2[0], B2[1]);
      var dx = B2[0] - A2[0], dy = B2[1] - A2[1], L = Math.sqrt(dx * dx + dy * dy) || 1, ux = dx / L, uy = dy / L;
      ink(doc, 0.24, [20, 20, 20]);
      [A2, B2].forEach(function (p) { doc.line(p[0] - (ux + uy) * 0.8, p[1] - (uy - ux) * 0.8, p[0] + (ux + uy) * 0.8, p[1] + (uy - ux) * 0.8); });
      var ang = -Math.atan2(dy, dx) * 180 / Math.PI;
      if (ang > 90) ang -= 180; if (ang < -90) ang += 180;
      var r = ang * Math.PI / 180, nx = -Math.sin(r), ny = -Math.cos(r);
      var mx = (A2[0] + B2[0]) / 2 + nx * 0.9, my = (A2[1] + B2[1]) / 2 + ny * 0.9;
      txt(doc, txtS, mx, my, 7, { align: 'center', angle: ang, bold: true });
    }
    function sheetOverall(doc, X, ctx) {
      FONT(ctx);
      var g = geo(), h2 = (BOT - TOP - 8);
      var bw = (RM - LM - 8) / 2;
      viewTitle(doc, LM, TOP, T_('rackDocOverall', 'Overall dimensions'), '');
      var MA = picture(doc, _pics && _pics.ovA, { x: LM, y: TOP + 4, w: bw, h: h2 });
      var MB = picture(doc, _pics && _pics.ovB, { x: LM + bw + 8, y: TOP + 4, w: bw, h: h2 });
      var hw = g.W / 2, hl = g.L / 2, k = 0.22;
      if (MA) {
        dim3(doc, MA, [hw, 0, -hl], [hw, 0, hl], [k, 0, 0], fD(g.L));
        dim3(doc, MA, [-hw, 0, -hl], [hw, 0, -hl], [0, 0, -k], fD(g.W));
        dim3(doc, MA, [hw + g.tw, 0, -hl], [hw + g.tw, g.roofTop, -hl], [0, 0, -k * 1.6], fD(g.roofTop));
        dim3(doc, MA, [hw, 0, -hl], [hw, g.hFront, -hl], [0, 0, -k * 0.8], fD(g.hFront));
      }
      if (MB) {
        dim3(doc, MB, [-hw, 0, hl], [-hw, g.deckTop, hl], [0, 0, k * 0.8], fD(g.deckTop));
        dim3(doc, MB, [-hw, 0, hl], [-hw, g.hBack, hl], [0, 0, k * 1.6], fD(g.hBack));
        if (g.roofBB) {
          var rb = g.roofBB;
          dim3(doc, MB, [rb.x0, g.roofTop, rb.z0], [rb.x0, g.roofTop, rb.z1], [-k * 0.7, 0, 0], fD(rb.z1 - rb.z0));
        }
        dim3(doc, MB, [-hw, g.deckTop, -hl], [-hw, g.hBack - g.th, -hl], [0, 0, -k], fD(g.hBack - g.th - g.deckTop));
      }
      sheetHead(doc, 102, '');
    }

    /* 103 / 104 -- ELEVATIONS, two to a sheet, one scale for all four */
    function elevScale() {
      var cw = (RM - LM - 10) / 2, ch = BOT - TOP - 26, s = 1e9;
      ['front', 'back', 'left', 'right'].forEach(function (k) {
        var e = extOf(V[k]);
        s = Math.min(s, (cw - 44) / (e.x1 - e.x0), (ch - 22) / (e.y1 - e.y0));
      });
      return pickScale(s);
    }
    function elevation(doc, key, title, cell, SC, used) {
      var g = geo(), Vw = V[key], sc = scene(Vw, null), ext = sc.ext;
      var Mp = mapper(Vw, SC.s, cell.x + 18 + (cell.w - 44) / 2, cell.y + 10 + (cell.h - 22) / 2, ext);
      viewTitle(doc, cell.x, cell.y, title, SC.label);
      drawScene(doc, sc, Mp);
      var bx = Mp.box;
      /* the ground */
      ink(doc, 0.5, [20, 20, 20]); doc.line(bx.x0 - 6, Mp.Y(0), bx.x1 + 6, Mp.Y(0));
      var t = tagsOn(doc, sc, Mp); for (var k in t) used[k] = 1;
      /* the levels on the right */
      var lv = [{ y: 0 }, { y: g.deckTop }];
      if (key === 'front' || key === 'left' || key === 'right') lv.push({ y: g.hFront });
      if (key === 'back' || key === 'left' || key === 'right') lv.push({ y: g.hBack });
      lv.push({ y: g.roofTop });
      levels(doc, Mp, lv, bx.x1, 1);
      /* along the bottom */
      var foot = Mp.Y(0), at;
      if (key === 'front' || key === 'back') {
        at = g.postZ.map(function (z) { return Mp.P([0, 0, z])[0]; });
        chain(doc, { axis: 'h', at: at, foot: at.map(function () { return foot; }), base: foot + 6, out: 1, s: SC.s });
        at = [Mp.P([0, 0, -g.L / 2])[0], Mp.P([0, 0, g.L / 2])[0]];
        chain(doc, { axis: 'h', at: at, foot: [foot, foot], base: foot + 12.5, out: 1, s: SC.s });
      } else {
        at = [-g.W / 2 - g.tw, -g.W / 2, g.W / 2, g.W / 2 + g.tw, g.rafX1].map(function (x) { return Mp.P([x, 0, 0])[0]; });
        var fy = [Mp.Y(g.hBack), foot, foot, Mp.Y(g.hFront), Mp.Y(g.hFront)];
        chain(doc, { axis: 'h', at: at, foot: fy, base: foot + 6, out: 1, s: SC.s });
        chain(doc, { axis: 'h', at: [at[0], at[4]], foot: [fy[0], fy[4]], base: foot + 12.5, out: 1, s: SC.s });
        /* the roof pitch, in words by the roof */
        txt(doc, T_('roofPitch', 'Roof Pitch') + ' ' + g.pitch.toFixed(1) + '\u00b0', (bx.x0 + bx.x1) / 2, bx.y0 - 2.5, 6.5, { align: 'center', c: [80, 70, 60] });
      }
      /* the overall height on the left */
      var xl = bx.x0 - 6;
      chain(doc, { axis: 'v', at: [Mp.Y(0), Mp.Y(g.roofTop)], foot: [null, bx.x0], base: xl, out: -1, s: SC.s });
    }
    function sheetElev(doc, X, ctx, pair, no) {
      FONT(ctx);
      var SC = elevScale(), cw = (RM - LM - 10) / 2, used = {};
      var cells = [{ x: LM, y: TOP, w: cw, h: BOT - TOP - 14 }, { x: LM + cw + 10, y: TOP, w: cw, h: BOT - TOP - 14 }];
      pair.forEach(function (p, i) { elevation(doc, p[0], p[1], cells[i], SC, used); });
      legend(doc, LM, BOT - 3, used);
      sheetHead(doc, no, SC.label);
    }

    /* 105 -- SECTIONS 1-1 (along the rack, looking at the back) and 2-2 */
    function sheetSections(doc, X, ctx) {
      FONT(ctx);
      var g = geo(), zc = secZ(g);
      var S1 = { V: V.front, cut: { p0: [0, 0, 0] } }, S2 = { V: V.right, cut: { p0: [0, 0, zc] } };
      var A = scene(S1.V, S1.cut), B = scene(S2.V, S2.cut);
      var w1 = 158, w2 = RM - LM - w1 - 8, ch = BOT - TOP - 14;
      var s = Math.min((w1 - 50) / (A.ext.x1 - A.ext.x0), (ch - 36) / (A.ext.y1 - A.ext.y0), (w2 - 46) / (B.ext.x1 - B.ext.x0), (ch - 36) / (B.ext.y1 - B.ext.y0));
      var SC = pickScale(s);
      /* 1-1 */
      var M1 = mapper(S1.V, SC.s, LM + 22 + (w1 - 50) / 2, TOP + 18 + (ch - 36) / 2, A.ext);
      viewTitle(doc, LM, TOP, T_('section', 'Section') + ' 1-1', SC.label);
      drawScene(doc, A, M1, { cut: S1.cut });
      ink(doc, 0.5, [20, 20, 20]); doc.line(M1.box.x0 - 6, M1.Y(0), M1.box.x1 + 6, M1.Y(0));
      var foot = M1.Y(0);
      var at = g.postZ.map(function (z) { return M1.P([0, 0, z])[0]; });
      chain(doc, { axis: 'h', at: at, foot: at.map(function () { return foot; }), base: foot + 6, out: 1, s: SC.s });
      at = [M1.P([0, 0, -g.L / 2])[0], M1.P([0, 0, g.L / 2])[0]];
      chain(doc, { axis: 'h', at: at, foot: [foot, foot], base: foot + 12.5, out: 1, s: SC.s });
      /* the rafters, centre to centre, over the top */
      if (g.rafZ.length > 1) {
        var rz = g.rafZ.map(function (z) { return M1.P([0, 0, z])[0]; }), ft = M1.Y(g.rafTop);
        chain(doc, { axis: 'h', at: rz, foot: rz.map(function () { return ft; }), base: M1.box.y0 - 6, out: -1, s: SC.s });
      }
      /* heights on the left: frame, deck, clear, rail */
      var hs = [0, g.frameBot, g.frameTop, g.deckTop, g.hBack - g.th, g.hBack].map(M1.Y);
      var fx = M1.box.x0;
      chain(doc, { axis: 'v', at: hs, foot: hs.map(function () { return fx; }), base: fx - 6, out: -1, s: SC.s });
      chain(doc, { axis: 'v', at: [hs[0], hs[5]], foot: [fx, fx], base: fx - 14, out: -1, s: SC.s });
      /* 2-2 */
      var x2 = LM + w1 + 8;
      var M2 = mapper(S2.V, SC.s, x2 + 24 + (w2 - 46) / 2, TOP + 18 + (ch - 36) / 2, B.ext);
      viewTitle(doc, x2, TOP, T_('section', 'Section') + ' 2-2', SC.label);
      drawScene(doc, B, M2, { cut: S2.cut });
      ink(doc, 0.5, [20, 20, 20]); doc.line(M2.box.x0 - 6, M2.Y(0), M2.box.x1 + 6, M2.Y(0));
      var f2 = M2.Y(0);
      var ax = [-g.W / 2, -g.W / 2 + g.tw, g.W / 2 - g.tw, g.W / 2].map(function (x) { return M2.P([x, 0, 0])[0]; });
      chain(doc, { axis: 'h', at: ax, foot: ax.map(function () { return f2; }), base: f2 + 6, out: 1, s: SC.s });
      chain(doc, { axis: 'h', at: [ax[0], ax[3]], foot: [f2, f2], base: f2 + 12.5, out: 1, s: SC.s });
      /* the rafter, over the top: back rail, the posts, the front rail, the overhang */
      var rx = [g.rafX0, -g.W / 2, g.W / 2, g.W / 2 + g.tw, g.rafX1].map(function (x) { return M2.P([x, 0, 0])[0]; });
      var rt = M2.Y(g.rafTop);
      chain(doc, { axis: 'h', at: rx, foot: rx.map(function () { return rt; }), base: M2.box.y0 - 6, out: -1, s: SC.s });
      var h2l = [0, g.deckTop, g.hBack].map(M2.Y), h2r = [0, g.deckTop, g.hFront].map(M2.Y);
      chain(doc, { axis: 'v', at: h2l, foot: h2l.map(function () { return M2.box.x0; }), base: M2.box.x0 - 6, out: -1, s: SC.s });
      chain(doc, { axis: 'v', at: h2r, foot: h2r.map(function () { return M2.box.x1; }), base: M2.box.x1 + 7, out: 1, s: SC.s });
      txt(doc, T_('roofPitch', 'Roof Pitch') + ' ' + g.pitch.toFixed(1) + '\u00b0', (M2.box.x0 + M2.box.x1) / 2, M2.box.y0 - 13, 6.5, { align: 'center', c: [80, 70, 60] });
      sheetHead(doc, 105, SC.label);
    }

    /* 106 -- 3D VIEWS: architectural and the structural framing */
    function sheetViews(doc, X, ctx) {
      FONT(ctx);
      var cw = (RM - LM - 8) / 2, ch = (BOT - TOP - 14) / 2;
      viewTitle(doc, LM, TOP, T_('rackDocArch', 'Architectural views'), '');
      picture(doc, _pics && _pics.archA, { x: LM, y: TOP + 3, w: cw, h: ch });
      picture(doc, _pics && _pics.archB, { x: LM + cw + 8, y: TOP + 3, w: cw, h: ch });
      var y2 = TOP + ch + 9;
      viewTitle(doc, LM, y2, T_('rackDocFraming', 'Structural framing'), '');
      picture(doc, _pics && _pics.frA, { x: LM, y: y2 + 3, w: cw, h: ch });
      picture(doc, _pics && _pics.frB, { x: LM + cw + 8, y: y2 + 3, w: cw, h: ch });
      sheetHead(doc, 106, '');
    }

    /* 107 / 108 -- ASSEMBLY: four steps a sheet, the new pieces in colour
       with their marks, what is already built grey */
    function sheetSteps(doc, X, ctx, from, no) {
      FONT(ctx);
      var cw = (RM - LM - 8) / 2, ch = (BOT - TOP - 6) / 2 - 2, i;
      for (i = 0; i < 4; i++) {
        var si = from + i, st = STEPS[si];
        if (!st) break;
        var cx = LM + (i % 2) * (cw + 8), cy = TOP - 3 + Math.floor(i / 2) * (ch + 6);
        /* the number and the name */
        doc.setFillColor(26, 28, 29); doc.circle(cx + 3, cy + 1.2, 3, 'F');
        txt(doc, String(si + 1), cx + 3, cy + 2.4, 9, { align: 'center', bold: true, c: [255, 255, 255] });
        txt(doc, stepTitle(st).toUpperCase(), cx + 8, cy + 2.6, 8.5, { bold: true });
        var pic = _pics && _pics.steps && _pics.steps[si];
        var Mp = picture(doc, pic, { x: cx, y: cy + 5, w: 82, h: ch - 5 });
        /* the marks on the new pieces: two of each, apart */
        var mk = [];
        (_marks || []).forEach(function (m) {
          var own = m.inst.filter(function (i2) { return st.types.indexOf(i2.partType) >= 0; });
          if (own.length) mk.push({ no: m.no, pt: m.pt, len: m.len, dims: m.dims, qty: own.length, inst: own });
        });
        if (Mp) {
          var placed = [];
          mk.forEach(function (m) {
            var pts = m.inst.map(function (inst) {
              var S = null; _sol.forEach(function (s2) { if (s2.mesh === inst.mesh) S = s2; });
              return S ? Mp.P(S.c) : null;
            }).filter(Boolean);
            if (!pts.length) return;
            var pick = [pts[0]];
            if (pts.length > 1) pick.push(pts[pts.length - 1]);
            pick.forEach(function (p) {
              var w = tw(doc, m.no, 6, true) + 2.4;
              var clash = placed.some(function (q) { return Math.abs(q[0] - p[0]) < (w + q[2]) / 2 && Math.abs(q[1] - p[1]) < 3.6; });
              if (clash) return;
              placed.push([p[0], p[1], w]);
              doc.setFillColor(255, 255, 255); ink(doc, 0.2, [25, 25, 25]);
              doc.roundedRect(p[0] - w / 2, p[1] - 1.7, w, 3.4, 0.8, 0.8, 'FD');
              txt(doc, m.no, p[0], p[1] + 0.9, 6, { align: 'center', bold: true });
            });
          });
        }
        /* beside the picture: what goes on, and with what */
        var tx = cx + 86, tW = cw - 86, ly = cy + 12;
        mk.forEach(function (m) {
          if (ly > cy + ch - 4) return;
          txt(doc, m.no, tx, ly, 7.5, { bold: true });
          txt(doc, m.qty + ' x  ' + (m.pt === 'roof' && m.dims ? (fD(m.dims.width) + ' x ' + fD(m.dims.length)) : fD(m.len)), tx + 10, ly, 7.5, { bold: true });
          var nm = doc.splitTextToSize(partName(m.pt) + '  ' + profName(m.pt), tW - 10);
          txt(doc, nm[0] || '', tx + 10, ly + 3.4, 6.2, { c: [110, 100, 85] });
          ly += 8.4;
        });
        var fs = [];
        st.screws.forEach(function (tg) { var n = screwCount(tg); if (n) fs.push(n + ' x ' + catName(SCREW_MATERIAL[tg])); });
        if (fs.length && ly < cy + ch - 4) {
          txt(doc, T_('screws', 'Screws').toUpperCase(), tx, ly + 1, 6.2, { bold: true, c: [120, 110, 95] });
          doc.setFont(FNT, 'normal'); doc.setFontSize(6.4); doc.setTextColor(60, 52, 44);
          var yy2 = ly + 4.6;
          fs.forEach(function (f) { doc.splitTextToSize(f, tW).forEach(function (ln) { if (yy2 < cy + ch) { doc.text(ln, tx, yy2); yy2 += 2.9; } }); });
        }
      }
      sheetHead(doc, no, '');
    }

    /* 109 -- PARTS SCHEDULE: the marks with their pieces, and each piece
       drawn with its length and its end cut */
    function sheetParts(doc, X, ctx) {
      FONT(ctx);
      var M = _marks || [];
      viewTitle(doc, LM, TOP, T_('rackDocParts', 'Parts schedule'), '');
      var cols = [[T_('rackDocMark', 'Mark'), 13], [T_('pdfCutHeaderPart', 'Part'), 26], [T_('type', 'Type'), 52], [T_('length', 'Length'), 22], [T_('qty', 'Qty'), 10], [T_('total', 'Total'), 22]];
      var x = LM, y = TOP + 7, i;
      doc.setFillColor(26, 28, 29); doc.rect(LM, y - 3.6, 145, 5.2, 'F');
      cols.forEach(function (c) { txt(doc, String(c[0]).toUpperCase(), x + 1, y, 6, { bold: true, c: [255, 255, 255] }); x += c[1]; });
      y += 5.4;
      M.forEach(function (m, k) {
        if (k % 2) { doc.setFillColor(246, 243, 238); doc.rect(LM, y - 3.6, 145, 5, 'F'); }
        var isRoof = (m.pt === 'roof'), len = isRoof && m.dims ? (fD(m.dims.width) + ' x ' + fD(m.dims.length)) : fD(m.len);
        var cells = [m.no, m.pts.map(partName).join(', '), catName(m.code), len, String(m.qty), isRoof ? '' : fD(m.len * m.qty)];
        x = LM;
        cells.forEach(function (s, ci) {
          var w = cols[ci][1] - 1.5, t = String(s);
          doc.setFont(FNT, ci === 0 ? 'bold' : 'normal'); doc.setFontSize(6.6);
          while (t.length > 3 && doc.getTextWidth(t) > w) t = t.slice(0, -2) + '.';
          txt(doc, t, x + 1, y, 6.6, { bold: ci === 0 });
          x += cols[ci][1];
        });
        y += 5;
      });
      /* the pieces, drawn: one length scale for all, the section to scale */
      var dx = LM + 152, dw = RM - dx, maxL = 0;
      M.forEach(function (m) { if (m.pt !== 'roof' && m.len > maxL) maxL = m.len; });
      txt(doc, T_('rackDocDetail', 'Manufacturing detail').toUpperCase(), dx, TOP + 3.4, 7, { bold: true });
      var yy = TOP + 11, k2 = maxL > 0 ? (dw - 22) / maxL : 1;
      M.forEach(function (m) {
        if (m.pt === 'roof' || yy > BOT - 6) return;
        var th = (m.pt === 'batten') ? DIMS.oneByTwo.h : (m.pt === 'decking' ? DIMS.decking.h : DIMS.twoByFour.h);
        var bh = Math.max(1.6, Math.min(4.5, th * k2)), bl = m.len * k2, bx = dx + 12;
        var c = FILL[m.pt] || [205, 160, 105];
        txt(doc, m.no, dx, yy + bh / 2 + 0.9, 6.5, { bold: true });
        doc.setFillColor(c[0], c[1], c[2]); ink(doc, 0.16);
        var cutA = m.angle || 0, ch2 = Math.min(bl * 0.3, Math.tan(cutA * Math.PI / 180) * bh);
        if (cutA > 0 && m.pt === 'rafter') poly(doc, [[bx, yy], [bx + bl, yy], [bx + bl - ch2, yy + bh], [bx, yy + bh]], 'FD');
        else poly(doc, [[bx, yy], [bx + bl, yy], [bx + bl, yy + bh], [bx, yy + bh]], 'FD');
        var lab = fD(m.len) + '   ' + profName(m.pt) + (cutA ? ('   ' + cutA + '\u00b0 ' + T_('cut', 'cut').toLowerCase()) : '');
        ink(doc, 0.1, [60, 60, 60]); doc.line(bx, yy + bh + 1.6, bx + bl, yy + bh + 1.6);
        doc.line(bx, yy + bh + 0.8, bx, yy + bh + 2.4); doc.line(bx + bl, yy + bh + 0.8, bx + bl, yy + bh + 2.4);
        txt(doc, lab, bx, yy + bh + 4.6, 6.2, { c: [60, 50, 40] });
        yy += bh + 9.5;
      });
      sheetHead(doc, 109, '');
    }

    function pages() {
      if (!_sol || !_sol.length) return [];
      var P = [
        { title: T_('viewPlan', 'Floor plan'), draw: sheetPlan },
        { title: T_('rackDocOverall', 'Overall dimensions'), draw: sheetOverall },
        { title: T_('rackDocElevations', 'Elevations'), draw: function (d, X, c) { sheetElev(d, X, c, [['front', T_('viewFront', 'Front')], ['back', T_('viewBack', 'Back')]], 103); } },
        { title: T_('rackDocElevations', 'Elevations'), draw: function (d, X, c) { sheetElev(d, X, c, [['left', T_('viewLeft', 'Left')], ['right', T_('viewRight', 'Right')]], 104); } },
        { title: T_('pdfCrossSections', 'Cross sections'), draw: sheetSections },
        { title: T_('pdf3d', '3D visualisation'), draw: sheetViews },
        { title: T_('assemblyStepsHint', 'Assembly steps'), draw: function (d, X, c) { sheetSteps(d, X, c, 0, 107); } },
        { title: T_('assemblyStepsHint', 'Assembly steps'), draw: function (d, X, c) { sheetSteps(d, X, c, 4, 108); } },
        { title: T_('rackDocParts', 'Parts schedule'), draw: sheetParts }
      ];
      /* every sheet in its own try: one failing sheet says so, the rest stand */
      return P.map(function (p) {
        return { title: p.title, draw: function (doc, X, ctx) {
          try { p.draw(doc, X, ctx); }
          catch (e) {
            _note += '; sheet "' + p.title + '" FAILED ' + ((e && e.message) || e);
            try { doc.setTextColor(180, 80, 60); doc.setFontSize(9); doc.text('Sheet failed: ' + ((e && e.message) || e), LM, 40); } catch (e2) {}
          }
        } };
      });
    }
    return { prepare: prepare, pages: pages, note: function () { return _note; }, marks: function () { return _marks; }, solids: function () { return _sol; } };
  })();

  /* THE SHOPPING LIST (rack-v10-docs) -- the cut pieces packed into the
     lengths the yard sells (copied from the house line's _shopPack), and the
     roof in corrugated panels: strips of the slope's length cut from whole
     panels, as many strips as the panel's cover width needs */
  var SALE_LEN_M  = [3.0, 4.0, 5.0, 6.0];
  var SALE_LEN_FT = [8, 10, 12, 16];
  function _shopPack(pieces, stock) {
    var bars = [], i, j2, p, placed, k;
    var sorted = pieces.slice().sort(function (a, b) { return b - a; });
    for (i = 0; i < sorted.length; i++) {
      p = sorted[i];
      placed = false;
      for (j2 = 0; j2 < bars.length; j2++) {
        if (bars[j2].left >= p - 1e-6) { bars[j2].left -= p; placed = true; break; }
      }
      if (placed) continue;
      var fit = null;
      for (k = 0; k < stock.length; k++) if (stock[k] >= p - 1e-6) { fit = stock[k]; break; }
      if (fit == null) fit = stock[stock.length - 1];
      bars.push({ len: fit, left: fit - p });
    }
    var by = {}, out = [];
    for (i = 0; i < bars.length; i++) {
      var key = String(bars[i].len);
      if (!by[key]) { by[key] = { size: bars[i].len, count: 0, waste: 0 }; out.push(by[key]); }
      by[key].count++;
      by[key].waste += bars[i].left;
    }
    out.sort(function (a, b) { return a.size - b.size; });
    return out;
  }
  function buildShopping(bridge, boqRows) {
    var met = !!STATE.isMetric, rows = [], cuts = {};
    var inst = (global.RackInstances && global.RackInstances.all) ? global.RackInstances.all() : [];
    inst.forEach(function (i) {
      if (!i || !i.boqCode || i.boqCode === 'ROR12004' || !(i.length > 0)) return;
      (cuts[i.boqCode] = cuts[i.boqCode] || []).push(i.length);
    });
    var boqBy = {};
    (boqRows || []).forEach(function (r) { boqBy[r.code] = r; });
    var stock = met ? SALE_LEN_M : SALE_LEN_FT.map(function (f) { return f * 0.3048; });
    Object.keys(cuts).forEach(function (code) {
      var packed = _shopPack(cuts[code], stock), b = boqBy[code] || {};
      rows.push({
        code: code, name: (b.name || code), qty: b.qty != null ? Math.round(b.qty * 100) / 100 : null, unit: b.unit || '',
        kind: 'length',
        buy: packed.map(function (q) {
          return { size: met ? (Math.round(q.size * 100) / 100 + ' m') : (Math.round(q.size / 0.3048) + ' ft'),
                   count: q.count,
                   waste: met ? (Math.round(q.waste * 100) / 100 + ' m') : (Math.round(q.waste / 0.3048 * 10) / 10 + ' ft') };
        }),
        note: cuts[code].length + ' cut(s)'
      });
    });
    /* the roof: US 26 in x 8 ft panels covering 24 in, metric 1100 x 2000
       covering 1000 */
    var tw = DIMS.twoByFour.w;
    var slope = (STATE.W + 2 * tw + DIMS.rafterExt + 2 * DIMS.roofOverX), along = STATE.L + 2 * DIMS.roofOverZ;
    var cover = met ? 1.0 : 24 * IN_M, panelL = met ? 2.0 : 96 * IN_M;
    var strips = Math.ceil(along / cover - 1e-6), per = Math.max(1, Math.floor(panelL / slope + 1e-6));
    var panels = Math.ceil(strips / per), b2 = boqBy['ROR12004'] || {};
    rows.push({
      code: 'ROR12004', name: b2.name || 'ROR12004', qty: b2.qty != null ? Math.round(b2.qty * 100) / 100 : null, unit: b2.unit || '',
      kind: 'sheet',
      buy: [{ size: met ? '1100 x 2000 mm' : '26 in x 8 ft', count: panels, waste: '' }],
      note: strips + ' x ' + (met ? (Math.round(slope * 1000) + ' mm') : fmtFtIn(slope)) + ' strips'
    });
    rows.sort(function (a, c) { if (a.kind !== c.kind) return a.kind === 'length' ? -1 : 1; return a.name < c.name ? -1 : 1; });
    return rows;
  }

  /* ----------------------------------------------------------
     EXPORT
     ---------------------------------------------------------- */
  global.RackLib = {
    __version: VERSION,
    STATE:     STATE,
    DEFAULTS:  DEFAULTS,
    RANGES:    RANGES,
    DIMS:      DIMS,
    MATERIALS:       MATERIALS,
    SCREW_MATERIAL:  SCREW_MATERIAL,
    PART_MATERIAL:   PART_MATERIAL,
    boot:      boot,
    reset:     reset,
    toggleUnits: toggleUnits,
    share:     share,
    exportPdf: exportPdf,
    saveConfig: saveConfig,
    savePref:   savePref,
    refreshUI: refreshUI,
    fmtLen:    fmtLen,
    fmtDim:    fmtDim,
    fmtFtIn:   fmtFtIn,
    fmtArea:   fmtArea,
    doc:       RackDoc,
    docNote:   function () { return _docNote; }
  };

})(window);